/**
 * @fileoverview Shared types between backend and frontend.
 *
 * This is the CANONICAL SOURCE for these types.
 * The frontend copies this file via `pnpm sync:types`.
 *
 * To add a new shared type:
 * 1. Add it here
 * 2. Run `pnpm sync:types` in frontend (or it runs automatically on dev/build)
 * 3. Import from '@/types/shared' in frontend
 */

/** Trusted public media origins used by browser imports and backend storage. */
export const KNOWN_CDN_ORIGINS = [
  'https://shots.martini.video',
  'https://staging.martini.video',
  'https://cdn.martini.film',
  'https://staging-cdn.martini.film',
]

/**
 * Shot rating - mutually exclusive approval/rejection status.
 * A shot cannot be both favorite and reject at the same time.
 */
export type ShotRating = 'favorite' | 'reject'

/** Independent 1-5 review score. Undefined means no star rating. */
export type StarRating = 1 | 2 | 3 | 4 | 5

/** Max option rows a serialized model input keeps (spec.json, MCP get_models). */
export const MAX_SERIALIZED_INPUT_OPTIONS = 40

/**
 * Returns a copy of `inputs` where every `{ options: [...] }` input longer than
 * `max` keeps its default option first plus the first `max - 1` others, and a
 * map of input name → original length for the ones that were cut. Validation
 * always runs against the handler's full list, never against this copy.
 */
export function compactModelInputs<T extends Record<string, unknown>>(
  inputs: T,
  max = MAX_SERIALIZED_INPUT_OPTIONS,
): { inputs: T; truncated: Record<string, number> } {
  const compacted = { ...inputs }
  const truncated: Record<string, number> = {}

  for (const [key, value] of Object.entries(inputs)) {
    if (!value || typeof value !== 'object') continue

    const input = value as Record<string, unknown>
    if (!Array.isArray(input.options) || input.options.length <= max) continue

    const defaultOption = input.options.find(option => {
      if (!option || typeof option !== 'object') return false
      const candidate = option as Record<string, unknown>
      return candidate.default === true || candidate.value === input.default
    })
    const remaining = defaultOption ? input.options.filter(option => option !== defaultOption) : input.options
    const kept = (defaultOption ? [defaultOption, ...remaining] : remaining).slice(0, max)

    compacted[key as keyof T] = { ...input, options: kept } as T[keyof T]
    truncated[key] = input.options.length
  }

  return { inputs: compacted, truncated }
}

/**
 * Shot color label - for custom organizational workflows.
 * Only one color label can be applied at a time.
 */
export type ShotColorLabel = 'green' | 'yellow' | 'red' | 'blue' | 'purple'

export type ColorLabelNames = Partial<Record<ShotColorLabel, string>>

export const DEFAULT_COLOR_LABEL_NAMES: Record<ShotColorLabel, string> = {
  green: 'Green',
  yellow: 'Yellow',
  red: 'Red',
  blue: 'Blue',
  purple: 'Purple',
}

export function resolveColorLabelName(names: ColorLabelNames | undefined, color: ShotColorLabel): string {
  return names?.[color]?.trim() || DEFAULT_COLOR_LABEL_NAMES[color]
}

/**
 * Asset operation type - determines display noun and behavior.
 * Add new operations here as video models evolve.
 *
 * Root-level takes (t2v, i2v, upload, generate-image) share sibling numbering.
 * Child-level (extend, variation, offshoot) use per-operation counters.
 */
export type AssetOperation =
  | 't2v' // text-to-video (root take)
  | 'i2v' // image-to-video (root take)
  | 'extend' // video extension
  | 'variation' // same prompt, different seed (sibling in same row)
  | 'offshoot' // derived from existing shot via "Reuse Prompt" (child in new row)
  | 'v2v' // video-to-video (edit)
  | 'upload' // user-uploaded video (root take)
  | 'generate-image' // image generation (root take)
  | 'upscale' // upscaled version of an existing asset
  | 'i2s' // image-to-set (creates 3D set from image)
  | 'set-screenshot' // screenshot captured from 3D set
  | 'generate-audio' // audio generation (root take)
  | 'inpaint-audio' // audio inpaint (regenerates a region of an existing audio asset)
  | 'video-frame' // still frame extracted from a video asset
  | 'camera-motion' // frontend-authored camera motion draft / rendered motion
  | 'crop' // deterministic derivation: rect crop of a parent image (child op, never root)
  | 'crop-adjust' // crop child promoted by its first angle or colour adjustment; carries edit/editRevisions instead of cropRect/cropRevisions

/**
 * Inset (in natural sheet pixels) applied to each cell of a panel-grid crop so
 * the extracted panels exclude the grid's divider lines. Canonical home
 * (contract C18): the onboarding 2×2 storyboard crops and `sheetQuadrantCss`
 * both derive from this value so quadrant CSS previews and the real crop
 * children render near-identical pixels.
 *
 * NOTE: the v9/v10 character-sheet extractors keep a private copy of this
 * constant until T-WATCH's separator-band hoist consolidates them here.
 */
export const PANEL_GRID_INSET_PX = 4

/**
 * Export job status - used for Premiere Pro XML export progress tracking.
 */
export type ExportStatus =
  'pending' | 'validating' | 'downloading' | 'generating' | 'bundling' | 'uploading' | 'completed' | 'failed'

/**
 * Render job status - used for video rendering progress tracking.
 */
export type RenderStatus =
  | 'pending'
  | 'queued' // Waiting for render slot (concurrency limit)
  | 'validating'
  | 'downloading'
  | 'generating' // Preparing render project
  | 'rendering' // Rendering video
  | 'uploading'
  | 'completed'
  | 'failed'

/** Named export destinations exposed by the web and MCP render surfaces. */
export type RenderPreset = 'youtube' | 'short-form' | 'square' | 'feed'
export type RenderResolution = '1080p' | '4k'
export type RenderFit = 'contain' | 'cover'

/**
 * Numeric rank for a resolution option value so dropdowns list low → high
 * (720p then 1080p then 4K; 1K then 2K then 4K). Handlers disagree on notation
 * (`4k` vs `4K` vs `2160p`), so this normalizes before comparing.
 */
export function resolutionSortRank(value: string): number {
  const v = value.trim().toLowerCase()
  if (v === 'sd') return 480
  if (v === 'hd') return 1080
  if (v === 'uhd' || v === '4k') return 2160
  if (v === '8k') return 4320
  const pMatch = /^(\d+)p$/.exec(v)
  if (pMatch) return Number(pMatch[1])
  const kMatch = /^(\d+)k$/.exec(v)
  if (kMatch) return Number(kMatch[1]) * 1024
  return Number.POSITIVE_INFINITY
}

/**
 * Maps a resolution label (e.g. "1080p", "4K") to its pixel height so per-second
 * models with `resolutionTiers` land on the right rung.
 *
 * Canonical for BOTH the quote and the charge — the frontend estimate and
 * `calculateGenerationCost` import this one function, because two copies that
 * drift by a single case arm silently bill a different rung than the button
 * showed. An unmatched label returns `undefined`, which skips `resolutionTiers`
 * and falls back to the model's BASE rate; a handler that rewrites unsupported
 * labels must clamp to a rung this function agrees with (ENG-761).
 *
 * Distinct from `resolutionSortRank` — that ranks options for a dropdown and
 * reads "2K" as 2048; this is real pixel height, so "2K" is 1440.
 */
export function resolutionStringToHeight(resolution?: string | null): number | undefined {
  if (!resolution) return undefined
  switch (resolution.trim().toLowerCase()) {
    case '360p':
      return 360
    case '480p':
      return 480
    case '540p':
      return 540
    case '720p':
      return 720
    case '768p':
      return 768
    case '1080p':
      return 1080
    case '1440p':
    case '2k':
      return 1440
    case '2160p':
    case '4k':
      return 2160
    default:
      return undefined
  }
}

/**
 * Resolves an orientation-neutral video resolution tier. A portrait
 * 1080×1920 output is still 1080p, so explicit geometry uses the short edge.
 */
export function resolveVideoResolutionTierHeight(input: {
  outputWidth?: number
  outputHeight?: number
  sourceWidth?: number
  sourceHeight?: number
  scaleFactor?: number
  resolution?: string | null
}): number | undefined {
  const isPositiveFinite = (value: number | undefined): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0

  if (isPositiveFinite(input.outputWidth) && isPositiveFinite(input.outputHeight)) {
    return Math.min(input.outputWidth, input.outputHeight)
  }
  if (
    isPositiveFinite(input.sourceWidth) &&
    isPositiveFinite(input.sourceHeight) &&
    isPositiveFinite(input.scaleFactor)
  ) {
    return Math.min(input.sourceWidth, input.sourceHeight) * input.scaleFactor
  }
  return resolutionStringToHeight(input.resolution)
}

export function compareResolutionValues(a: string, b: string): number {
  const diff = resolutionSortRank(a) - resolutionSortRank(b)
  return diff !== 0 ? diff : a.localeCompare(b)
}

export function sortResolutionOptions<T extends { value: string | number }>(options: readonly T[]): T[] {
  return [...options].sort((a, b) => compareResolutionValues(String(a.value), String(b.value)))
}

/** Explicit output geometry for an MP4 render. */
export interface RenderTargetFrame {
  width: number
  height: number
  fit: RenderFit
}

export const DEFAULT_RENDER_TARGET_FRAME: Readonly<RenderTargetFrame> = {
  width: 1920,
  height: 1080,
  fit: 'contain',
}

const MAX_RENDER_TARGET_DIMENSION = 3840
const MAX_RENDER_TARGET_PIXELS = 3840 * 2160

export function isValidRenderTargetFrame(frame: RenderTargetFrame | undefined): frame is RenderTargetFrame {
  if (!frame) return false
  const { width, height, fit } = frame
  return (
    Number.isInteger(width) &&
    Number.isInteger(height) &&
    width >= 2 &&
    height >= 2 &&
    width <= MAX_RENDER_TARGET_DIMENSION &&
    height <= MAX_RENDER_TARGET_DIMENSION &&
    width % 2 === 0 &&
    height % 2 === 0 &&
    width * height <= MAX_RENDER_TARGET_PIXELS &&
    (fit === 'contain' || fit === 'cover')
  )
}

/** Canonical dimensions for each named export destination. */
export const RENDER_PRESET_FRAMES = {
  youtube: {
    aspectRatio: '16:9',
    resolutions: {
      '1080p': { width: 1920, height: 1080 },
      '4k': { width: 3840, height: 2160 },
    },
  },
  'short-form': {
    aspectRatio: '9:16',
    resolutions: {
      '1080p': { width: 1080, height: 1920 },
      '4k': { width: 2160, height: 3840 },
    },
  },
  square: {
    aspectRatio: '1:1',
    resolutions: {
      '1080p': { width: 1080, height: 1080 },
      '4k': { width: 2160, height: 2160 },
    },
  },
  feed: {
    aspectRatio: '4:5',
    resolutions: {
      '1080p': { width: 1080, height: 1350 },
      '4k': { width: 2160, height: 2700 },
    },
  },
} as const satisfies Record<
  RenderPreset,
  {
    aspectRatio: string
    resolutions: Record<RenderResolution, { width: number; height: number }>
  }
>

export function resolveRenderPresetFrame(
  preset: RenderPreset,
  resolution: RenderResolution = '1080p',
  fit: RenderFit = 'contain',
): RenderTargetFrame {
  return { ...RENDER_PRESET_FRAMES[preset].resolutions[resolution], fit }
}

/** Scale a sequence's working frame for export while staying inside the render service limits. */
export function resolveSequenceRenderFrame(
  storedFrame: RenderTargetFrame | undefined,
  resolution: RenderResolution = '1080p',
  requestedFit?: RenderFit,
): RenderTargetFrame {
  const frame = isValidRenderTargetFrame(storedFrame) ? storedFrame : DEFAULT_RENDER_TARGET_FRAME
  const fit = requestedFit ?? frame.fit
  if (resolution === '1080p') return { ...frame, fit }

  const dimensionScale = MAX_RENDER_TARGET_DIMENSION / Math.max(frame.width, frame.height)
  const pixelScale = Math.sqrt(MAX_RENDER_TARGET_PIXELS / (frame.width * frame.height))
  const scale = Math.min(2, dimensionScale, pixelScale)
  // Round inward: nearest-even rounding can push an otherwise valid frame
  // back over the pixel cap after scaling (for example 1082×1918 at 4K).
  const roundEvenWithinLimit = (value: number) => Math.max(2, Math.floor(value / 2) * 2)

  return {
    width: roundEvenWithinLimit(frame.width * scale),
    height: roundEvenWithinLimit(frame.height * scale),
    fit,
  }
}

/**
 * Fit a clip's fade-in and fade-out inside its duration. When the two fades
 * together exceed the clip, both are scaled by the same factor so they meet at
 * the point they would have crossed, preserving the ratio the editor chose.
 * Units are the caller's (ticks, seconds, frames) — the rule is unit-agnostic,
 * and MCP, the browser preview, MLT, and XMEML all resolve fades through it.
 */
export function clampAudioFades(
  fadeIn: number = 0,
  fadeOut: number = 0,
  duration: number,
): { fadeIn: number; fadeOut: number } {
  const positiveFadeIn = Math.max(0, fadeIn)
  const positiveFadeOut = Math.max(0, fadeOut)
  const total = positiveFadeIn + positiveFadeOut
  if (total <= duration || total === 0) return { fadeIn: positiveFadeIn, fadeOut: positiveFadeOut }
  const scale = Math.max(0, duration) / total
  return { fadeIn: positiveFadeIn * scale, fadeOut: positiveFadeOut * scale }
}

/**
 * URLs returned from backend after synchronous frame upload.
 * These are R2 URLs safe to store in Yjs (small strings, not base64).
 *
 * Used by:
 * - /generate endpoint response (backend returns uploaded frame URLs)
 * - /upload/frame endpoint response
 * - Frontend to store in generationParams for regeneration
 */
export interface FrameUrls {
  imageUrl?: string
  endFrameUrl?: string
  middleFrameUrl?: string
  /** Inpainting mask (model: flux-pro-fill), uploaded to R2 like the other frames. */
  maskUrl?: string
  imageReferences?: string[]
  elements?: {
    frontalImageUrl: string
    referenceImageUrls?: string[]
  }[]
}

export interface TtsTimestampSpan {
  text: string
  /** Seconds. */
  start: number
  /** Seconds. */
  end: number
}

/**
 * The TTS vendor's own word timing for a generated voice line, stored
 * unchanged (vendors can misplace a word; consumers sanity-check it). Times are
 * seconds on the delivered file's clock: vendor time + `offsetSeconds` = time
 * from sample 0 of the asset's original file.
 */
export interface TtsTimestamps {
  source: 'typecast/with-timestamps'
  clock: 'delivered-file'
  offsetSeconds: number
  audioDuration?: number
  /** Words only: the vendor's per-character spans would bloat the shared doc and every agent read. */
  words: TtsTimestampSpan[]
}

/**
 * Source video color metadata captured at transcode time via ffprobe.
 *
 * Powers honest HDR/SDR preview labeling across the canvas, the expanded
 * modal, and spec badges. Absent on legacy assets transcoded before this was
 * tracked (treat missing as "unknown / assume SDR").
 */
export interface SourceColorMetadata {
  /** Bits per color sample (8, 10, 12). */
  bitDepth?: number
  /** Normalized color transfer characteristic — e.g. 'smpte2084' (PQ), 'arib-std-b67' (HLG), 'bt709'. */
  transfer?: string
  /** Normalized color primaries — e.g. 'bt2020', 'bt709'. */
  primaries?: string
  /** True when the source is HDR (PQ/HLG transfer). */
  isHdr: boolean
}

/**
 * Supported video container formats.
 * This is the SINGLE SOURCE OF TRUTH for what we accept.
 *
 * Note: FFmpeg handles decoding all common video/audio codecs.
 * This list is about CONTAINERS, not codecs.
 */
export const SUPPORTED_VIDEO_FORMATS = {
  // Standard formats
  'video/mp4': { extension: '.mp4', label: 'MP4' },
  'video/quicktime': { extension: '.mov', label: 'MOV' },
  'video/webm': { extension: '.webm', label: 'WebM' },
  'video/x-matroska': { extension: '.mkv', label: 'MKV' },
  // Broadcast/legacy formats
  'video/mp2t': { extension: '.ts', label: 'TS' },
  'video/x-msvideo': { extension: '.avi', label: 'AVI' },
} as const

export type SupportedVideoMimeType = keyof typeof SUPPORTED_VIDEO_FORMATS

/** All supported extensions (for validation). */
export const SUPPORTED_VIDEO_EXTENSIONS = Object.values(SUPPORTED_VIDEO_FORMATS).map(f => f.extension)

/** Human-readable format list (for error messages). */
export const SUPPORTED_VIDEO_LABELS = Object.values(SUPPORTED_VIDEO_FORMATS).map(f => f.label)

// ============================================
// Model Pricing (Single Source of Truth)
// ============================================

/** Value of one olive in USD. */
/**
 * A media reference the pipeline can persist and submit: an uploaded http(s)
 * URL — including local-dev filesystem storage — as opposed to a
 * lifecycle-temporary reference (`pending:<uuid>` upload markers, `blob:`
 * object URLs, `data:` URIs). This is the ONE definition of "durable"; three
 * independent copies of it (frontend readiness, frontend submission, backend
 * validation) each independently required `https:` and each independently
 * broke local dev. Provider fetchability of loopback URLs is a separate
 * concern handled at submission time (providerFetchableInput).
 */
export function isDurableMediaUrl(value: string): boolean {
  return /^https?:\/\//i.test(value)
}

export const OLIVE_VALUE_USD = 0.1

// ============================================
// Top-up bonus (Pricing v2) — subscribers only
// ============================================
// Two stacked incentives, surfaced as one "+N bonus" per pack:
//  1. Loyalty %: flat per paid tier (Pro/Studio). FREE users get NO bonus.
//  2. Volume %: +1% per step UP the plan's own pack ladder, so bigger presets
//     visibly reward more — the brackets ARE the tiles users see, so a buyer
//     who does the math sees "bigger is better".
// The credited bonus is rounded to the NEAREST 5 olives so totals stay clean.
// Founder call 2026-07-06.

/**
 * Loyalty bonus % by paid plan ('_comp' stripped; free/unknown = 0 = none).
 * Same rate for both tiers — it's never printed as a %, so there's no need to
 * differentiate, and it keeps the grant simple.
 */
export const TOPUP_LOYALTY_BONUS_PCT: Record<string, number> = { pro: 3, studio: 3 }

/**
 * Plan pack ladders — the top-up presets shown to each tier. Canonical here so
 * the volume-bonus brackets stay aligned with the tiles users actually see
 * (TopUpCard imports these).
 */
export const TOPUP_PACK_LADDER: Record<string, readonly number[]> = {
  pro: [300, 750, 2000, 5000],
  studio: [750, 2000, 5000, 10000],
}

/** Extra volume bonus % by ladder position (1st preset +0 … 4th preset +3). */
const TOPUP_VOLUME_BONUS_PCT_BY_TIER = [0, 1, 2, 3]

/**
 * Bonus olives on a top-up: loyalty% (paid tier) + volume% (position on that
 * plan's pack ladder), rounded to the nearest 5 olives. Free/unknown plans get
 * 0. Used by the backend grant AND the frontend display so they match by
 * construction.
 */
export function computeTopupBonus(
  plan: string | null | undefined,
  faceOlives: number,
): { bonusOlives: number; pct: number } {
  const basePlan = (plan ?? '').replace(/_comp$/, '')
  const loyaltyPct = TOPUP_LOYALTY_BONUS_PCT[basePlan] ?? 0
  if (loyaltyPct === 0 || faceOlives <= 0) return { bonusOlives: 0, pct: 0 }
  const ladder = TOPUP_PACK_LADDER[basePlan] ?? []
  let volumePct = 0
  for (let i = 0; i < ladder.length; i++) {
    if (faceOlives >= ladder[i]) volumePct = TOPUP_VOLUME_BONUS_PCT_BY_TIER[i]
  }
  const pct = loyaltyPct + volumePct
  const bonusOlives = Math.round((faceOlives * pct) / 100 / 5) * 5
  return { bonusOlives, pct }
}

// ============================================
// Auto top-up (Autumn-native billing control)
// ============================================
// Autumn watches the olive balance server-side and charges the org's saved
// card when it drops below the threshold — we only store the config on the
// Autumn customer (billing_controls.auto_topups) and mirror it in the UI.
// Tier bonuses do NOT apply to auto top-ups (v1 decision, see
// .context/plan-auto-topup.md).

/** Auto top-up settings for one org, as shown/edited on the billing page. */
export interface AutoTopupConfig {
  enabled: boolean
  /** Balance at/below which Autumn buys the refill. */
  thresholdOlives: number
  /** Olives purchased per auto top-up (face value, $0.10/olive). */
  refillOlives: number
}

/** "Refill when below" choices offered in the UI. */
export const AUTO_TOPUP_THRESHOLD_OPTIONS = [25, 50, 100, 250, 500, 1000] as const

/** Upper bound for a custom "Refill when below" value (lower bound is 1). */
export const AUTO_TOPUP_THRESHOLD_MAX_OLIVES = 100000

/**
 * Plan-based defaults so enabling is one click. Personal defaults key off the
 * subscription plan ('_comp' stripped); teams get team-sized numbers
 * regardless of plan.
 */
export function autoTopupDefaults(plan: string | null | undefined, orgType: 'personal' | 'team'): AutoTopupConfig {
  if (orgType === 'team') {
    return { enabled: true, thresholdOlives: 500, refillOlives: 5000 }
  }
  const basePlan = (plan ?? '').replace(/_comp$/, '')
  if (basePlan === 'studio') return { enabled: true, thresholdOlives: 100, refillOlives: 2000 }
  if (basePlan === 'pro') return { enabled: true, thresholdOlives: 50, refillOlives: 750 }
  return { enabled: true, thresholdOlives: 50, refillOlives: 300 }
}

/** Olive cost configuration for a single model. */
export interface ModelOliveCost {
  /** Cost in olives per second (video models). */
  olivesPerSecond?: number
  /**
   * Cost per combined input/output second when one or more video references are
   * present. This is an exact rate and takes precedence over videoInputDiscount.
   */
  videoInputOlivesPerSecond?: number
  /** Additional olives per second when audio is enabled. */
  audioSurchargeOlives?: number
  /** Additional olives per second when voice control increases upstream audio pricing. */
  voiceSurchargeOlives?: number
  /** Cost in olives per asset (image/fixed-price models). */
  olivesPerAsset?: number
  /**
   * Cost in olives per output minute, rounded UP to the next whole minute.
   * Used by providers (e.g. ElevenLabs Music) that bill in 1-minute increments.
   * 10 s → 1 minute → `olivesPerMinuteCeil`. 61 s → 2 minutes → 2 × `olivesPerMinuteCeil`.
   */
  olivesPerMinuteCeil?: number
  /**
   * Cost in olives per 1,000 characters of input text (TTS / voiceover models).
   * The character count is the prompt length, known before submit, so TTS bills
   * upfront with no hold/refund. Pair with `minimumOlives` (defaults to 0.1) for
   * the per-generation floor so a very short line never bills to ~0.
   */
  olivesPerThousandChars?: number
  /** Floor for ceil-rounded billing — guarantees at least N olives per generation. */
  minimumOlives?: number
  /** Resolution-based pricing tiers (checked in order; first matching tier wins). */
  resolutionTiers?: Array<{
    maxHeight: number
    olivesPerSecond: number
    audioSurchargeOlives?: number
    videoInputOlivesPerSecond?: number
  }>
  /** Explicit per-image resolution prices. */
  imageResolutionTiers?: Array<{
    resolution: string
    olivesPerAsset: number
    qualityOlivesPerAsset?: Record<string, number>
  }>
  /** Duration-based pricing tiers for fixed-price models (checked in order; first matching tier wins). */
  durationTiers?: Array<{ maxDuration: number; olivesPerAsset: number }>
  /** Output-megapixel-based pricing tiers for per-image models (checked in order; first matching tier wins). */
  megapixelTiers?: Array<{ maxMP: number; olivesPerAsset: number }>
  /**
   * Per-quality multipliers for per-image models that expose a quality tier
   * (e.g., GPT Image 2 low/medium/high). When absent, no multiplier is applied
   * — this is the gate that prevents quality discounts from leaking onto
   * models that don't expose the parameter.
   */
  qualityMultipliers?: Record<string, number>
  /**
   * Exact per-second rate cards keyed by a video render-quality selector.
   * Unlike image `qualityMultipliers`, these tiers may carry their own
   * resolution matrix (for example FLUX.3 Draft is fixed 720p while Full has
   * distinct 720p/1080p prices).
   */
  perSecondQualityTiers?: Record<
    string,
    {
      olivesPerSecond: number
      resolutionTiers?: Array<{ maxHeight: number; olivesPerSecond: number }>
    }
  >
  /**
   * Multiplier applied to the per-second cost when the request includes one or more
   * video references (e.g., FAL Seedance 2.0 uses a 0.9× conservative reference rate).
   * Billing applies this to generated output seconds plus known trimmed video-reference seconds;
   * absent means no discount/input-duration pricing.
   */
  videoInputDiscount?: number
  /**
   * Flat per-image charge for reference images beyond an included allowance
   * (founder pricing decision 2026-08-31, H3 first). Billed on the DISTINCT
   * images the provider actually receives — handlers dedupe repeated URLs and
   * flatten element frontals into the same list, and a start-frame request
   * strips references entirely, so count with
   * `countBillableReferenceImages()`, never a raw array length. The charge is
   * per generation (not per second) and lands on the canonical cost, so every
   * surface — UI estimate, UI charge, MCP, public API, negotiated multipliers
   * — inherits it from this one declaration.
   */
  referenceImageSurcharge?: {
    /** Reference images included per request at no extra charge. */
    includedImages: number
    /** Olives per distinct billable image beyond `includedImages`. */
    olivesPerImage: number
    /** Ceiling on billable images — mirrors the handler's cap on what it sends. */
    maxImages: number
  }
  /** Absolute list price per 1,000 reference-image tokens, pro-rata. */
  referenceTokenSurcharge?: {
    includedTokens: number
    olivesPer1kTokens: number
    processedShortEdge: number
    dimensionStep: number
    pixelsPerToken: number
    /** Estimate-only assumption for assets awaiting dimensions (1824 = 16:9); never used for billing. */
    estimateTokensPerUnmeasuredImage: number
    maxImages: number
    /**
     * Reference audio is tokenized against the same allowance, per second of the clip the
     * provider receives (H3 Max: 80/s, measured on fal's billing events 2026-09-22). Absent
     * when the card does not bill audio.
     */
    tokensPerAudioSecond?: number
    /** Estimate-only assumption for a clip whose length is unknown; the model's combined cap. */
    estimateSecondsPerUnmeasuredAudio?: number
    /**
     * Reference video is sampled at this fraction of its source frames (H3 Max: 0.3, i.e.
     * 7.2 samples per second of a 24 fps clip) and each sample costs the tokens of a frame
     * at the OUTPUT resolution, not the clip's (fal, 2026-09-22). Absent when the card does
     * not bill video.
     */
    videoSamplesPerFrame?: number
    /** Tokens per sampled frame by output height; the last tier covers anything taller. */
    videoTokensPerSampleTiers?: Array<{ maxHeight: number; tokensPerSample: number }>
    /** Estimate-only assumptions for a clip that could not be probed: the model's cap, at 24 fps. */
    estimateSecondsPerUnmeasuredVideo?: number
    estimateFramesPerSecondForUnmeasuredVideo?: number
  }
}

/**
 * Fal-route Seedance 2.5 rate card, ~5% under Fal list price — launch-pricing
 * exception: Martini's 5% FAL discount makes these rates approximately
 * break-even (native BytePlus uses the MODEL_OLIVE_COSTS base card). Two
 * consumers: the fal-provider override in `getProviderAwareModelOliveCost`,
 * and `seedance-2.5-custom`, which routes natively but is deliberately priced
 * identically to the fal-routed `seedance-2.5`.
 */
const SEEDANCE_25_FAL_CARD: ModelOliveCost = {
  olivesPerSecond: 4.5,
  videoInputOlivesPerSecond: 2.7,
  resolutionTiers: [
    { maxHeight: 480, olivesPerSecond: 2.1, videoInputOlivesPerSecond: 1.3 },
    { maxHeight: 720, olivesPerSecond: 4.5, videoInputOlivesPerSecond: 2.7 },
    { maxHeight: 1080, olivesPerSecond: 11.1, videoInputOlivesPerSecond: 6.7 },
  ],
}

/**
 * Fal-routed Seedance 2.5 edit card: an edit processes ~one source second plus one
 * output second, both at the video-input rate, so each tier is 2× the fal card's
 * `videoInputOlivesPerSecond`. Shared by `seedance-2.5-edit` on the fal route and
 * by `seedance-2.5-custom-edit`, which pins the native route but bills these rates
 * so the choice is never price-steered (same policy as `seedance-2.5-custom`).
 */
const SEEDANCE_25_EDIT_FAL_CARD: ModelOliveCost = {
  olivesPerSecond: 5.4,
  resolutionTiers: [
    { maxHeight: 480, olivesPerSecond: 2.6 },
    { maxHeight: 720, olivesPerSecond: 5.4 },
    { maxHeight: 1080, olivesPerSecond: 13.4 },
  ],
}

/**
 * H3 Max reference-token terms, shared by every H3 Max route that takes references.
 */
const H3_MAX_REFERENCE_TOKEN_SURCHARGE = {
  includedTokens: 4096,
  olivesPer1kTokens: 0.2,
  processedShortEdge: 1024,
  dimensionStep: 32,
  pixelsPerToken: 1024,
  estimateTokensPerUnmeasuredImage: 1824,
  maxImages: 9,
  tokensPerAudioSecond: 80,
  estimateSecondsPerUnmeasuredAudio: 15,
  // Reference video: 0.3 samples per source frame, 1,008 tokens a sample at 768P/1080P
  // and 390 at 480P — a 124-frame "5 s" clip is the 37,296 tokens fal's page quotes.
  // Measured on eleven invoiced requests (2026-09-04 → 09-22).
  videoSamplesPerFrame: 0.3,
  videoTokensPerSampleTiers: [
    { maxHeight: 480, tokensPerSample: 390 },
    { maxHeight: 1080, tokensPerSample: 1008 },
  ],
  estimateSecondsPerUnmeasuredVideo: 15,
  estimateFramesPerSecondForUnmeasuredVideo: 24,
} satisfies NonNullable<ModelOliveCost['referenceTokenSurcharge']>

/**
 * Centralized olive costs for all generation models.
 *
 * This is the SINGLE SOURCE OF TRUTH for user-facing pricing.
 * Backend handlers and the frontend pricing page both read from here.
 */
export const MODEL_OLIVE_COSTS = {
  // Video generation
  'veo3.1': {
    olivesPerSecond: 1.8,
    audioSurchargeOlives: 1.8,
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 1.8, audioSurchargeOlives: 1.8 },
      { maxHeight: 2160, olivesPerSecond: 3.6, audioSurchargeOlives: 1.8 },
    ],
  },
  'veo3.1-fast': {
    olivesPerSecond: 0.9,
    audioSurchargeOlives: 0.5,
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 0.9, audioSurchargeOlives: 0.5 },
      { maxHeight: 2160, olivesPerSecond: 2.7, audioSurchargeOlives: 0.5 },
    ],
  },
  'kling-o3': { olivesPerSecond: 1.0, audioSurchargeOlives: 0.3 },
  'kling-v3': { olivesPerSecond: 1.0, audioSurchargeOlives: 0.5, voiceSurchargeOlives: 0.3 },
  'kling-2.6': { olivesPerSecond: 0.6, audioSurchargeOlives: 0.7 },
  'kling-o1': { olivesPerSecond: 1.0 },
  'hailuo-02': { olivesPerSecond: 0.7 },
  // H3 is a DELIBERATE EXCEPTION to Pricing v2's +15-25% markup: Koh's call
  // (2026-08-21) is to price it ~5% UNDER fal's published list, H3 only, so
  // onboarding can hand out cheap/free H3 generations. Fal lists $0.05/$0.06/
  // $0.13/$0.16 per second at 480P/768P/2K/4K; -5% rounded to the nearest 0.1
  // olive lands at parity on the two cheap rungs (0.475 and 0.57 both round up)
  // and 6-8% under on 2K/4K. That used to be roughly break-even; the fal order
  // form of 2026-09-16 discounts the H3 family 15%, so the same card now earns
  // +15/+15/+7.9/+9.3% across the rungs — see docs/pricing-margins.md.
  // Base is the 2K default, for requests with no resolvable resolution.
  // Per fal, 2K and 4K upscale a 768P render rather than generating natively.
  'hailuo-03': {
    olivesPerSecond: 1.2,
    // fal bills a reference clip's seconds at the output resolution's rate, on top of the
    // output seconds (115 invoiced reference-to-video requests joined to fal's billing
    // events, 2026-09-02..23: quantity = take seconds + clip seconds, no separate input
    // rate). Reference audio is not billed. The card charges the clip at the same rate,
    // so the input term is the tier's own rate (`videoInputOlivesPerSecond`).
    videoInputOlivesPerSecond: 1.2,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.5, videoInputOlivesPerSecond: 0.5 },
      { maxHeight: 768, olivesPerSecond: 0.6, videoInputOlivesPerSecond: 0.6 },
      { maxHeight: 1440, olivesPerSecond: 1.2, videoInputOlivesPerSecond: 1.2 },
      { maxHeight: 2160, olivesPerSecond: 1.5, videoInputOlivesPerSecond: 1.5 },
    ],
    // First 5 reference images ride free; each further distinct image is $0.08.
    // maxImages mirrors HAILUO_03_MAX_IMAGE_REFERENCES — the handler never
    // sends more than 9, so billing must not count more than 9 either.
    referenceImageSurcharge: { includedImages: 5, olivesPerImage: 0.8, maxImages: 9 },
  },
  // H3 Max: fal's regular card is $0.05/$0.08/$0.16 per second at 480P/768P/1080P (the launch
  // promo through 2026-09-14 discounts it 75%; priced on the regular card so nothing
  // needs repricing when it ends). Pricing v2 +20-25%; 1080P doubles the 768P rate. The
  // H3 under-list onboarding exception above is H3-only. Base is the 768P default.
  'hailuo-03-max': {
    olivesPerSecond: 1.0,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.6 },
      { maxHeight: 768, olivesPerSecond: 1.0 },
      { maxHeight: 1080, olivesPerSecond: 2.0 },
    ],
    // Every reference token passes through at fal cost — images, audio (80 per second of the
    // clip) and video (0.3 samples per source frame at the output grid) share one allowance,
    // and the surcharge below is charged on the pooled total (Long, 2026-09-23: the card no
    // longer absorbs the clips). Measured on fal's billing events, 2026-09-04 → 09-22.
    referenceTokenSurcharge: H3_MAX_REFERENCE_TOKEN_SURCHARGE,
  },
  // H3 Max Extend: fal's card is $0.05/$0.08/$0.16/$0.32 per generated second at
  // 480P/768P/1080P/2K — H3 Max's ladder plus a 2K rung at twice 1080P — so it takes
  // H3 Max's olive card (Pricing v2 +20-25%) and doubles 1080P for 2K. The source clip
  // is not billed: fal's billable units for a 5 s and an 11 s source were the requested
  // seconds alone (2026-09-25), where the same clip as a reference-to-video input billed
  // its tokens. fal bills the requested seconds, not the slightly longer clip it returns.
  'hailuo-03-max-extend': {
    olivesPerSecond: 1.0,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.6 },
      { maxHeight: 768, olivesPerSecond: 1.0 },
      { maxHeight: 1080, olivesPerSecond: 2.0 },
      { maxHeight: 1440, olivesPerSecond: 4.0 },
    ],
  },
  // H3 Max Insert: fal's card is $0.05/$0.06 per second of the generated scene at 480P/768P,
  // billed on the new scene alone, not the full spliced output (fal.ai, 2026-10-02). Pricing v2
  // +20% gives 0.6/0.7. Reference images and videos tokenize against the same 4,096-token
  // allowance and $0.02/1k rate as H3 Max, so it shares that surcharge. Base is the 768P default.
  'hailuo-03-max-insert': {
    olivesPerSecond: 0.7,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.6 },
      { maxHeight: 768, olivesPerSecond: 0.7 },
    ],
    referenceTokenSurcharge: H3_MAX_REFERENCE_TOKEN_SURCHARGE,
  },
  // H3 Max Recast: fal's card is $0.30 / $0.45 per output second at 768P / 1080P,
  // reference photos free. Launched 2026-10-01, after the fal order form, so it is
  // priced off list at Pricing v2 +20%. The output is as long as the source clip, so
  // /generate bills the trusted source duration (resolveH3MaxRecastBillingDuration).
  'hailuo-03-max-recast': {
    olivesPerSecond: 5.4,
    resolutionTiers: [
      { maxHeight: 768, olivesPerSecond: 3.6 },
      { maxHeight: 1080, olivesPerSecond: 5.4 },
    ],
  },
  // H3 Max Turbo (preview) is charged AT fal's list price (decided by Long,
  // 2026-09-21). Fal bills a $0.0125 unit at 1 / 1.6 / 3.2 units per second for
  // 480P / 768P / 1080P (billing events, 2026-09-21) — a quarter of H3 Max, not
  // the half its launch card assumed. The 15% contract discount is the margin.
  // No reference route, so no surcharge. Base is the 768P default.
  'hailuo-03-max-turbo': {
    olivesPerSecond: 0.2,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.125 },
      { maxHeight: 768, olivesPerSecond: 0.2 },
      { maxHeight: 1080, olivesPerSecond: 0.4 },
    ],
  },
  'sora-2': {
    olivesPerSecond: 4.5,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 2.7 }],
  },
  'sora-2-standard': { olivesPerSecond: 0.9 },
  'seedance-1.5': {
    olivesPerSecond: 0.5,
    audioSurchargeOlives: 0.5,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.2, audioSurchargeOlives: 0.1 },
      { maxHeight: 720, olivesPerSecond: 0.2, audioSurchargeOlives: 0.3 },
    ],
  },
  'seedance-2.0': {
    // Native BytePlus ModelArk rates use official token pricing +20%, rounded to 0.1 olive.
    // Base stays the 1080p default; 4K remains an explicit opt-in.
    olivesPerSecond: 4.5,
    videoInputOlivesPerSecond: 2.7,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.8, videoInputOlivesPerSecond: 0.5 },
      { maxHeight: 720, olivesPerSecond: 1.8, videoInputOlivesPerSecond: 1.1 },
      { maxHeight: 1080, olivesPerSecond: 4.5, videoInputOlivesPerSecond: 2.7 },
      { maxHeight: 2160, olivesPerSecond: 9.3, videoInputOlivesPerSecond: 5.6 },
    ],
  },
  'seedance-2.0-enhancor': {
    olivesPerSecond: 6.1,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 1.2 },
      { maxHeight: 720, olivesPerSecond: 2.7 },
    ],
    videoInputDiscount: 0.9,
  },
  'seedance-2.0-fast': {
    olivesPerSecond: 1.4,
    videoInputOlivesPerSecond: 0.8,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 0.7, videoInputOlivesPerSecond: 0.4 }],
  },
  'seedance-2.0-mini': {
    olivesPerSecond: 0.9,
    videoInputOlivesPerSecond: 0.5,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 0.4, videoInputOlivesPerSecond: 0.3 }],
  },
  // Seedance 2.5 native BytePlus rates use official token pricing +20%, rounded
  // to 0.1 olive. The Fal rollback card below preserves its higher launch rates.
  'seedance-2.5': {
    // Base stays the 720p default; 1080p is an explicit opt-in.
    olivesPerSecond: 2.8,
    videoInputOlivesPerSecond: 1.7,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 1.2, videoInputOlivesPerSecond: 0.7 },
      { maxHeight: 720, olivesPerSecond: 2.8, videoInputOlivesPerSecond: 1.7 },
      { maxHeight: 1080, olivesPerSecond: 6.8, videoInputOlivesPerSecond: 4.1 },
    ],
  },
  'seedance-2.5-edit': {
    olivesPerSecond: 3.4,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 1.4 },
      { maxHeight: 720, olivesPerSecond: 3.4 },
      { maxHeight: 1080, olivesPerSecond: 8.2 },
    ],
  },
  // Custom pins the native route but bills the same rates as the fal-routed
  // seedance-2.5 (deliberate — see SEEDANCE_25_FAL_CARD).
  'seedance-2.5-custom': SEEDANCE_25_FAL_CARD,
  'seedance-2.5-custom-edit': SEEDANCE_25_EDIT_FAL_CARD,
  // fal's US-hosted endpoints: Pricing v2 (cost +20%, 0.1-olive steps) on fal's US list,
  // which is 1.2× the regular endpoints and outside the order form's Seedance discount.
  // Always fal-routed, so this is the only card (no SEEDANCE_FAL_OLIVE_COSTS entry).
  'seedance-2.5-us': {
    olivesPerSecond: 6.7,
    videoInputOlivesPerSecond: 4.0,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 3.0, videoInputOlivesPerSecond: 1.8 },
      { maxHeight: 720, olivesPerSecond: 6.7, videoInputOlivesPerSecond: 4.0 },
      { maxHeight: 1080, olivesPerSecond: 16.4, videoInputOlivesPerSecond: 9.8 },
    ],
  },
  // An edit bills ~one source second plus one output second at the video-input rate
  // (see SEEDANCE_25_EDIT_FAL_CARD), priced here per Pricing v2 on that US-list cost
  // (2× the US video-input rate, +20%) rather than doubling the rounded olives.
  'seedance-2.5-us-edit': {
    olivesPerSecond: 8.0,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 3.6 },
      { maxHeight: 720, olivesPerSecond: 8.0 },
      { maxHeight: 1080, olivesPerSecond: 19.7 },
    ],
  },
  'grok-imagine': {
    olivesPerSecond: 0.6,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 0.5 }],
  },
  'grok-imagine-1.5': {
    // FAL account pricing: $0.01/output-sec on i2v; t2v/r2v use $0.00017/compute-sec.
    // At 20 compute-sec/output-sec, both price paths round to the 0.1-olive billable floor.
    olivesPerSecond: 0.1,
  },
  // Grok Imagine 1.5 Lite: fal and xAI list $0.02/$0.03/$0.14 per second (480p/720p/1080p);
  // Pricing v2 +20%, rounded to 0.1 olive. The +$0.01 input-image fee on i2v is absorbed.
  'grok-imagine-1.5-lite': {
    olivesPerSecond: 1.7,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.2 },
      { maxHeight: 720, olivesPerSecond: 0.4 },
    ],
  },
  'pixverse-v6': {
    olivesPerSecond: 0.8,
    audioSurchargeOlives: 0.2,
    resolutionTiers: [
      { maxHeight: 360, olivesPerSecond: 0.2, audioSurchargeOlives: 0.1 },
      { maxHeight: 540, olivesPerSecond: 0.3, audioSurchargeOlives: 0.1 },
      { maxHeight: 720, olivesPerSecond: 0.4, audioSurchargeOlives: 0.1 },
      { maxHeight: 1080, olivesPerSecond: 0.8, audioSurchargeOlives: 0.2 },
    ],
  },
  // Boreal: fal $0.01/$0.03/$0.12 per second; Pricing v2 +20%, rounded to 0.1 olive.
  boreal: {
    olivesPerSecond: 1.4,
    resolutionTiers: [
      { maxHeight: 720, olivesPerSecond: 0.1 },
      { maxHeight: 1080, olivesPerSecond: 0.4 },
      { maxHeight: 1440, olivesPerSecond: 1.4 },
    ],
  },
  'happy-horse': {
    // Default falls through to 1080p pricing when resolution isn't passed.
    olivesPerSecond: 2.5,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 1.3 }],
  },
  // Wan 3.0: fal $0.05/$0.10/$0.20 per second at 480p/720p/1080p, audio included.
  // Pricing v2 +20% midpoint, rounded to 0.1 olive. Base is the 1080p default.
  'wan-3.0': {
    olivesPerSecond: 2.4,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.6 },
      { maxHeight: 720, olivesPerSecond: 1.2 },
      { maxHeight: 1080, olivesPerSecond: 2.4 },
    ],
  },
  // Wan 3.0 Prime: fal $0.068/$0.14/$0.28 per second at 480p/720p/1080p.
  // Explicit FAL-parity exception: keep the exact 480p rate instead of rounding to 0.1 olive.
  'wan-3.0-prime': {
    olivesPerSecond: 2.8,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 0.68 },
      { maxHeight: 720, olivesPerSecond: 1.4 },
      { maxHeight: 1080, olivesPerSecond: 2.8 },
    ],
  },
  'ltx-2.3': {
    // Default falls through to 1080p pricing when resolution isn't resolvable
    // (e.g., caller omits resolution from the request body). Safer than
    // defaulting to the 4K rate.
    olivesPerSecond: 0.7,
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 0.7 },
      { maxHeight: 1440, olivesPerSecond: 1.4 },
      { maxHeight: 2160, olivesPerSecond: 2.9 },
    ],
  },
  'luma-ray-3.2-v2v': {
    // Video-to-video that surfaces in the Create Shot tab (capability
    // generate-video). FAL prices per resolution: 540p $0.144/s, 720p
    // $0.216/s, 1080p $0.432/s → ~10% below, rounded to 0.1 olive.
    // Fallback matches the handler's 540p default (LumaRay32V2VHandler
    // prepareInput) so API/MCP callers that omit `resolution` are billed
    // for the resolution they actually receive instead of the 1080p rate.
    olivesPerSecond: 1.3,
    resolutionTiers: [
      { maxHeight: 540, olivesPerSecond: 1.3 },
      { maxHeight: 720, olivesPerSecond: 1.9 },
      { maxHeight: 1080, olivesPerSecond: 3.9 },
    ],
  },
  // FLUX.3 full: fal $0.17/s at 720p and $0.29/s at 1080p. Draft: $0.06/s
  // at fixed 720p. Customer rates target ~10% below fal and round to 0.1 olive.
  'flux-3': {
    olivesPerSecond: 2.6,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 1.5 }],
    perSecondQualityTiers: {
      draft: { olivesPerSecond: 0.5 },
      full: {
        olivesPerSecond: 2.6,
        resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 1.5 }],
      },
    },
  },
  // Gemini Omni Flash (Google) — text/image/reference-to-video with native audio.
  // fal token-bills it (~$0.125/sec t2v, ~$0.13/sec i2v/reference) → ~10% below, flat
  // 1.2 olives/sec across all three modes. Duration 3–10s, no resolution tiers.
  'gemini-omni-flash': {
    // 720p keeps its original 1.2 rate — Pricing v2 never reprices an existing model upward,
    // and at v1.1's $0.10/sec fal cost that lands at +20% anyway. The rungs v1.1 added are new
    // tiers, so they take the Pricing v2 +15-25% band: cost x1.2 rounded to 0.1 olive. 360p
    // rounds up to 0.4 because 0.3 would price the draft rung exactly at cost.
    olivesPerSecond: 1.2,
    resolutionTiers: [
      { maxHeight: 360, olivesPerSecond: 0.4 },
      { maxHeight: 720, olivesPerSecond: 1.2 },
      { maxHeight: 1080, olivesPerSecond: 1.8 },
      { maxHeight: 2160, olivesPerSecond: 3.6 },
    ],
  },

  // Video extend
  'veo3.1-extend': {
    olivesPerSecond: 1.8,
    audioSurchargeOlives: 1.8,
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 1.8, audioSurchargeOlives: 1.8 },
      { maxHeight: 2160, olivesPerSecond: 3.6, audioSurchargeOlives: 1.8 },
    ],
  },
  'kling-o1-extend': { olivesPerSecond: 1.0 },
  // FLUX.3 Extend full: fal $0.41/s at 720p and $0.53/s at 1080p. Draft:
  // $0.12/s at fixed 720p. Rates follow the same ~10%-below target.
  'flux-3-extend': {
    olivesPerSecond: 4.8,
    resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 3.7 }],
    perSecondQualityTiers: {
      draft: { olivesPerSecond: 1.1 },
      full: {
        olivesPerSecond: 4.8,
        resolutionTiers: [{ maxHeight: 720, olivesPerSecond: 3.7 }],
      },
    },
  },

  // Draft Enhance always produces a full-quality 1080p render. fal charges
  // $0.29/s; the request has no prompt, resolution, or audio knobs. Unlike
  // create/extend (~10% below fal), enhance targets ~5% below: it is a
  // deliberate second render of a take the user already chose, so it carries
  // less discount. $0.29 × 0.95 = $0.2755 → 2.8 olives at $0.10/olive.
  'flux-3-draft-enhance': { olivesPerSecond: 2.8 },
  'seedance-2.5-draft-enhance': { olivesPerSecond: 6.8, videoInputOlivesPerSecond: 4.1 },
  'seedance-2.5-edit-draft-enhance': { olivesPerSecond: 8.2 },
  'seedance-2.5-custom-draft-enhance': { olivesPerSecond: 11.1, videoInputOlivesPerSecond: 6.7 },
  'seedance-2.5-custom-edit-draft-enhance': { olivesPerSecond: 13.4 },

  // Video edit
  'kling-o3-edit': { olivesPerSecond: 1.5 },
  // FLUX.3 Edit: fal bills $0.205 per source second (no draft/resolution knobs).
  // Pricing v2 +20% midpoint: $0.205 × 1.2 = $0.246 → 2.5 olives, billed on the
  // trusted source-clip duration (max 15s) like the other edit models.
  'flux-3-edit': { olivesPerSecond: 2.5 },
  'kling-o1-edit': { olivesPerSecond: 1.5 },
  'kling-o1-restyle': { olivesPerSecond: 1.5 },
  'luma-ray-2-modify': { olivesPerSecond: 3.2 },
  // Gemini Omni Flash video edit — instruction-only v2v (fal token-bills ~$0.13/sec →
  // ~10% below → 1.2 olives/sec, billed on the source clip's duration like the Kling edits).
  // Same v1.1 rate card as generate: the edit endpoint bills per output second by resolution
  // too, on the processed source clip's duration.
  'gemini-omni-flash-edit': {
    // 720p keeps its original 1.2 rate — Pricing v2 never reprices an existing model upward,
    // and at v1.1's $0.10/sec fal cost that lands at +20% anyway. The rungs v1.1 added are new
    // tiers, so they take the Pricing v2 +15-25% band: cost x1.2 rounded to 0.1 olive. 360p
    // rounds up to 0.4 because 0.3 would price the draft rung exactly at cost.
    olivesPerSecond: 1.2,
    resolutionTiers: [
      { maxHeight: 360, olivesPerSecond: 0.4 },
      { maxHeight: 720, olivesPerSecond: 1.2 },
      { maxHeight: 1080, olivesPerSecond: 1.8 },
      { maxHeight: 2160, olivesPerSecond: 3.6 },
    ],
  },
  // VEED Lip Sync 2.0 — FAL $0.07/output sec; +20% target rounds to $0.08.
  'veed-lipsync-v2': { olivesPerSecond: 0.8 },

  // Image generation
  'flux-2-max': {
    olivesPerAsset: 0.6,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 0.6 },
      { resolution: '2K', olivesPerAsset: 0.9 },
      { resolution: '4K', olivesPerAsset: 1.4 },
    ],
  },
  // FLUX.1 [pro] Fill (inpainting) — FAL bills $0.05/MP, rounded up to the nearest
  // MP. Inpaint output matches the source dims (no resolution selector), so a flat
  // per-asset olive sized for a typical ~1 MP edit. 0.5 olives ($0.05) is FAL parity
  // at the 0.1-olive floor for a 1 MP edit (the ~10% target rounds to the same tier
  // — see grok-imagine), and runs below FAL for the common >1 MP edit since FAL
  // rounds megapixels up while Martini stays flat. See docs/pricing-margins.md.
  'flux-pro-fill': {
    olivesPerAsset: 0.5,
  },
  'nano-banana-pro-2': {
    olivesPerAsset: 1.4,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.4 },
      { resolution: '2K', olivesPerAsset: 1.4 },
      { resolution: '4K', olivesPerAsset: 2.7 },
    ],
  },
  'nano-banana-2': {
    olivesPerAsset: 0.7,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 0.7 },
      { resolution: '2K', olivesPerAsset: 1.1 },
      { resolution: '4K', olivesPerAsset: 1.4 },
    ],
  },
  // Nano Banana 2.1 — fal list $0.08 per 1K image, with 2K and 4K at 1.5x and 2x
  // ($0.12 / $0.16), Nano Banana 2's multipliers; fal's 2.1 page shows only the base rate.
  // Launched after the fal order form, so it bills at list. Pricing v2 midpoint (+20%),
  // rounded to 0.1 olive: $0.096 → 1.0, $0.144 → 1.4, $0.192 → 1.9. Web search is absorbed.
  'nano-banana-2.1': {
    olivesPerAsset: 1.0,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.0 },
      { resolution: '2K', olivesPerAsset: 1.4 },
      { resolution: '4K', olivesPerAsset: 1.9 },
    ],
  },
  // Nano Banana 2 Lite — Google's fastest/cheapest image model. fal token-meters it
  // (~$0.048/image at a fixed 1K output) → ~10% below → 0.4 olives/image. No resolution
  // tiers (fixed 1K) and no web search, unlike nano-banana-2. VERIFY the effective per-image
  // charge in the fal dashboard, since token metering varies slightly with prompt/complexity.
  'nano-banana-2-lite': { olivesPerAsset: 0.4 },
  // Seedream 5 Lite — ByteDance's fast/cheap image model. FAL bills a flat $0.035/image
  // regardless of resolution → ~10% below → 0.3 olives/image. Natively 2K–4K (no 1K
  // output). Explicit flat tiers are REQUIRED: without imageResolutionTiers, the shared
  // resolveImageOlivesPerAsset applies the global 2K=1.5×/4K=2× multiplier, which would
  // overcharge a model whose FAL cost doesn't move with resolution. See docs/pricing-margins.md.
  'seedream-v5-lite': {
    olivesPerAsset: 0.3,
    imageResolutionTiers: [
      { resolution: '2K', olivesPerAsset: 0.3 },
      { resolution: '4K', olivesPerAsset: 0.3 },
    ],
  },
  // Seedream 5 Pro — ByteDance's flagship image model. Unlike Lite (flat $0.035), FAL prices
  // by output size in two tiers: ≤1536×1536 (≤2.36 MP) = $0.0675, larger up to 4.19 MP = $0.135.
  // Priced at FAL parity, rounded to the nearest 0.1 olive: $0.0675 → 0.7, $0.135 → 1.4.
  // The 1K/2K knob maps to those two tiers (the handler emits dims/presets that stay in-bracket),
  // so explicit tiers are REQUIRED — otherwise the global 2K=1.5×/4K=2× multiplier would misprice.
  // ORDER MATTERS: 2K is listed FIRST because resolveImageOlivesPerAsset falls back to
  // imageResolutionTiers[0] when a request omits `resolution` (API/MCP/stale clients), and the
  // handler defaults omitted-resolution output to 2K. First tier must be the 2K default so those
  // requests are charged 1.4 (not 0.7) — do NOT reorder to 1K-first. /edit's ~$0.0045/extra-ref
  // surcharge is absorbed. See docs/pricing-margins.md.
  'seedream-v5-pro': {
    olivesPerAsset: 1.4,
    imageResolutionTiers: [
      { resolution: '2K', olivesPerAsset: 1.4 },
      { resolution: '1K', olivesPerAsset: 0.7 },
    ],
  },
  'gpt-image-2': {
    olivesPerAsset: 1.4,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.4, qualityOlivesPerAsset: { low: 0.1, medium: 0.4, high: 1.4 } },
      { resolution: '2K', olivesPerAsset: 2.0, qualityOlivesPerAsset: { low: 0.1, medium: 0.5, high: 2.0 } },
      { resolution: '4K', olivesPerAsset: 3.6, qualityOlivesPerAsset: { low: 0.1, medium: 0.9, high: 3.6 } },
    ],
  },
  // GPT Image 2.5 Flare / Sunburst — OpenAI token-bills both variants at the SAME rates as
  // GPT Image 2 ($30/1M output tokens), so the per-image provider cost ladder is GPT Image 2's
  // (1K/2K/4K × low/medium/high ≈ $0.005–$0.401, see docs/pricing-margins.md) and identical for
  // the two variants. Unlike gpt-image-2 (legacy ~10% below FAL), these are NEW models and take
  // the Pricing v2 band: cost × 1.2, rounded to 0.1 olive, 0.1 floor (low tiers). VERIFY the
  // effective per-image charge in the fal dashboard after the first live runs — OpenAI states the
  // GPT Image 2 token calculator does not estimate 2.5 consumption.
  // ORDER MATTERS: 1K first — resolveImageOlivesPerAsset falls back to imageResolutionTiers[0]
  // when a request omits `resolution`, and the handler defaults omitted resolution to 1K.
  'gpt-image-2.5-flare': {
    olivesPerAsset: 1.9,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.9, qualityOlivesPerAsset: { low: 0.1, medium: 0.5, high: 1.9 } },
      { resolution: '2K', olivesPerAsset: 2.7, qualityOlivesPerAsset: { low: 0.1, medium: 0.7, high: 2.7 } },
      { resolution: '4K', olivesPerAsset: 4.8, qualityOlivesPerAsset: { low: 0.1, medium: 1.2, high: 4.8 } },
    ],
  },
  'gpt-image-2.5-sunburst': {
    olivesPerAsset: 1.9,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 1.9, qualityOlivesPerAsset: { low: 0.1, medium: 0.5, high: 1.9 } },
      { resolution: '2K', olivesPerAsset: 2.7, qualityOlivesPerAsset: { low: 0.1, medium: 0.7, high: 2.7 } },
      { resolution: '4K', olivesPerAsset: 4.8, qualityOlivesPerAsset: { low: 0.1, medium: 1.2, high: 4.8 } },
    ],
  },
  // Qwen Image 3.0 Pro — Fal bills $0.04 at 1K and $0.075 at 2K for text generation
  // or editing. Pricing v2 midpoint: $0.04 * 1.20 = $0.048 → 0.5 olives;
  // $0.075 * 1.20 = $0.09 → 0.9 olives. ORDER MATTERS: the handler defaults omitted
  // or stale resolution state to 2K, so the fallback first tier must also be 2K.
  'qwen-image-3': {
    olivesPerAsset: 0.9,
    imageResolutionTiers: [
      { resolution: '2K', olivesPerAsset: 0.9 },
      { resolution: '1K', olivesPerAsset: 0.5 },
    ],
  },
  // Grok Imagine Image 2.0 — xAI's image model on Fal, priced per output image by
  // resolution AND quality: 1K low $0.04 / 1K medium $0.06 / 2K low $0.06 / 2K medium $0.08.
  // Pricing v2 midpoint (+20%), rounded to 0.1 olive: $0.048 → 0.5, $0.072 → 0.7,
  // $0.072 → 0.7, $0.096 → 1.0. Explicit tiers are REQUIRED so the global 2K=1.5×
  // multiplier never applies to a model whose 2K step is smaller than that.
  // ORDER MATTERS: 1K is listed FIRST because resolveImageOlivesPerAsset falls back to
  // imageResolutionTiers[0] when a request omits `resolution` (API/MCP/stale clients), and
  // the handler defaults omitted resolution to 1K — the two must agree.
  // /edit's +$0.01-per-input-image surcharge (max 3 refs) is absorbed, as with
  // seedream-v5-pro. See docs/pricing-margins.md.
  'grok-imagine-image-2': {
    olivesPerAsset: 0.7,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 0.7, qualityOlivesPerAsset: { low: 0.5, medium: 0.7 } },
      { resolution: '2K', olivesPerAsset: 1.0, qualityOlivesPerAsset: { low: 0.7, medium: 1.0 } },
    ],
  },
  // FLUX.3 Image — BFL's image model on Fal, priced per output image by resolution:
  // 1K $0.048 / 2K $0.10 / 4K $0.607 (BFL list; fal's 1K matches). Priced on that list,
  // not fal's 50%-off launch promo that ends 2026-10-08. Pricing v2 midpoint (+20%),
  // rounded to 0.1 olive: $0.0576 → 0.6, $0.12 → 1.2, $0.7284 → 7.3.
  // ORDER MATTERS: 2K is listed FIRST because resolveImageOlivesPerAsset falls back to
  // imageResolutionTiers[0] when a request omits `resolution`, and the handler defaults
  // omitted resolution to 2K. Any per-reference input charge on /edit-image is absorbed;
  // VERIFY against the first fal invoice (BFL lists $0.015 per input megapixel).
  'flux-3-image': {
    olivesPerAsset: 1.2,
    imageResolutionTiers: [
      { resolution: '2K', olivesPerAsset: 1.2 },
      { resolution: '1K', olivesPerAsset: 0.6 },
      { resolution: '4K', olivesPerAsset: 7.3 },
    ],
  },
  // Reve 2.1 — every text, edit, and remix request renders native 4K (~16 MP) and
  // costs $0.25/image on Fal. Pricing v2 midpoint: $0.25 * 1.20 = $0.30 = 3.0 olives.
  // One explicit 4K tier prevents stale resolution state from invoking global multipliers.
  reve: {
    olivesPerAsset: 3.0,
    imageResolutionTiers: [{ resolution: '4K', olivesPerAsset: 3.0 }],
  },
  // Ideogram 4.0 — FAL bills per output megapixel by rendering speed
  // ($0.03/$0.06/$0.10 per MP for Turbo/Balanced/Quality) PLUS a flat +$0.03/image for
  // prompt expansion (always on; required to avoid garbage-text output). Single tier (no
  // user-facing resolution selector); rendering speed maps to `quality`.
  // Handler sends explicit dimensions capped at 1536 on the long/square edge (verified honored
  // by fal: 1:1 -> 1536x1536 = 2.36 MP, the largest exposed output). Priced against that max,
  // incl. the +$0.03 expansion surcharge, ~10% below FAL:
  // turbo 2.36*$0.03+$0.03≈$0.101, balanced ≈$0.172, quality ≈$0.266.
  'ideogram-v4': {
    olivesPerAsset: 1.5,
    imageResolutionTiers: [
      {
        resolution: '1K',
        olivesPerAsset: 1.5,
        qualityOlivesPerAsset: { turbo: 0.9, balanced: 1.5, quality: 2.4 },
      },
    ],
  },
  // Ideogram 4.5 — fal list per image by quality, size-independent: text-to-image and edit
  // both $0.03 low / $0.06 medium / $0.22 high; edit adds $0.008 very_low. Pricing v2
  // midpoint (+20%, nearest 0.1, 0.1 floor): 0.1 / 0.4 / 0.7 / 2.6. Medium is fal's default.
  // Resolution is not an input, so the single tier is the fallback for every request.
  'ideogram-v4.5': {
    olivesPerAsset: 0.7,
    imageResolutionTiers: [
      { resolution: '1K', olivesPerAsset: 0.7, qualityOlivesPerAsset: { low: 0.4, medium: 0.7, high: 2.6 } },
    ],
  },
  'ideogram-v4.5-fill': {
    olivesPerAsset: 0.7,
    imageResolutionTiers: [
      {
        resolution: '1K',
        olivesPerAsset: 0.7,
        qualityOlivesPerAsset: { very_low: 0.1, low: 0.4, medium: 0.7, high: 2.6 },
      },
    ],
  },

  // Upscale (Topaz) — image
  // GAN models (Standard V2, Low Res V2, High Fidelity V2, CGI): 24 MP/credit
  'topaz-image-enhance': {
    olivesPerAsset: 2.0, // default for >4K output
    megapixelTiers: [
      { maxMP: 9, olivesPerAsset: 1.0 }, // up to ~4K
    ],
  },
  // Generative models (Standard MAX, Recovery V2, Wonder, Redefine, Wonder 3): 4 MP/credit
  // (Wonder 3 is billed by Topaz at 8 MP/credit — cheaper than the rest of this tier)
  'topaz-image-enhance-gen': {
    olivesPerAsset: 10.0, // default for >4K output
    megapixelTiers: [
      { maxMP: 2, olivesPerAsset: 1.5 }, // up to ~HD
      { maxMP: 9, olivesPerAsset: 3.0 }, // up to ~4K
    ],
  },
  // Bloom models (Bloom Realism): 2 MP/credit — 2× the per-MP cost of the other
  // generative models, so they get their own tier (Topaz credit ≈ $0.08–0.10)
  'topaz-image-enhance-bloom': {
    olivesPerAsset: 20.0, // default for >4K output (8K ≈ 33 MP ≈ 17 credits)
    megapixelTiers: [
      { maxMP: 2, olivesPerAsset: 1.5 }, // up to ~HD (~1 credit)
      { maxMP: 9, olivesPerAsset: 6.0 }, // up to ~4K (~5 credits)
    ],
  },

  // Upscale (Topaz) — video
  'topaz-video-enhance': {
    olivesPerSecond: 0.5,
    resolutionTiers: [
      { maxHeight: 1440, olivesPerSecond: 0.2 }, // 2K and below
    ],
  },
  'topaz-video-enhance-starlight': {
    olivesPerSecond: 3.0,
    resolutionTiers: [{ maxHeight: 1440, olivesPerSecond: 1.5 }],
  },
  'topaz-video-enhance-starlight-precise1': {
    olivesPerSecond: 6.0,
    resolutionTiers: [{ maxHeight: 1440, olivesPerSecond: 4.0 }],
  },
  'topaz-video-enhance-starlight-fast': {
    olivesPerSecond: 1.5,
    resolutionTiers: [{ maxHeight: 1440, olivesPerSecond: 0.8 }],
  },
  // Hyperion 2 / 2.5 (SDR→HDR). Topaz bills these per frame — ~13 frames/credit at
  // 1080p and ~6 at 4K ($0.18 / $0.40 per second at 30fps) — so they cannot share
  // the standard tier above. Cost-plus +20%, rounded to 0.1 olive. Hyperion 1 is
  // duration-billed (2 credits / 10s) and stays on the standard tier.
  'topaz-video-enhance-hyperion': {
    olivesPerSecond: 4.8,
    resolutionTiers: [{ maxHeight: 1440, olivesPerSecond: 2.2 }],
  },

  // Upscale (FAL / Black Forest Labs) — video.
  // FAL bills per delivered output second and prices on BOTH output resolution and
  // the creativity switch, so the two modes are separate rate cards rather than a
  // flat surcharge (the creative premium widens from +0.7 to +2.9 olives/sec across
  // the ladder). Cost-plus +20%, rounded to 0.1 olive:
  //   precise  1080p $0.14 → 1.7 | 2K $0.25 → 3.0 | 4K $0.55 → 6.6
  //   creative 1080p $0.20 → 2.4 | 2K $0.35 → 4.2 | 4K $0.79 → 9.5
  // The top-level rate is the 4K card: a 1.5-3x factor can push output past 2160p,
  // and FAL has no published tier above 4K, so anything taller bills at the 4K rate.
  'flux-video-upscale': {
    olivesPerSecond: 6.6,
    // The base card mirrors `precise`, which is the default this handler sends when a
    // caller omits the switch (MCP, direct API) — NOT FAL's own default of creativity=1.
    // Keep these two in lockstep: if the handler's default flips, this card flips with
    // it, or an omitting caller is billed for a mode it never ran.
    resolutionTiers: [
      { maxHeight: 1080, olivesPerSecond: 1.7 },
      { maxHeight: 1440, olivesPerSecond: 3.0 },
    ],
    perSecondQualityTiers: {
      precise: {
        olivesPerSecond: 6.6,
        resolutionTiers: [
          { maxHeight: 1080, olivesPerSecond: 1.7 },
          { maxHeight: 1440, olivesPerSecond: 3.0 },
        ],
      },
      creative: {
        olivesPerSecond: 9.5,
        resolutionTiers: [
          { maxHeight: 1080, olivesPerSecond: 2.4 },
          { maxHeight: 1440, olivesPerSecond: 4.2 },
        ],
      },
    },
  },

  // 3D set generation
  'worldlabs-marble-v1': { olivesPerAsset: 20 },

  // V9 shot pipeline (internal, per-asset flat cost covering transition + Kling + Nano Banana)
  'v9-shot': { olivesPerAsset: 14 },

  // V10 shot pipeline (same cost as V9 initially)
  'v10-shot': { olivesPerAsset: 14 },

  // Audio generation
  'elevenlabs-sfx-v2': {
    olivesPerAsset: 0.4,
    durationTiers: [
      { maxDuration: 5, olivesPerAsset: 0.1 },
      { maxDuration: 10, olivesPerAsset: 0.2 },
      { maxDuration: 15, olivesPerAsset: 0.3 },
      { maxDuration: 20, olivesPerAsset: 0.4 },
    ],
  },
  'beatoven-sfx': {
    olivesPerAsset: 0.9,
  },
  'cassetteai-sfx': {
    olivesPerAsset: 0.1,
  },
  // Sonilo 1.1: fal $0.0018/sec text or $0.009/sec video. Pricing v2's
  // 0.1-olive floor applies to text; video rounds to the same customer rate.
  'sonilo-sfx': { olivesPerSecond: 0.1 },
  // Sonilo Music: text $0.0025/s ×1.2 = $0.003 → 0.1-olive floor;
  // video $0.009/s ×1.2 = $0.0108 → rounds to 0.1 (+11% over cost), same customer rate as SFX.
  'sonilo-music': { olivesPerSecond: 0.1 },
  'minimax-music': { olivesPerAsset: 1.4 },
  // MiniMax Music 3: unlike v2.6 (a partner API at a flat $0.15/track), fal
  // self-hosts v3 and bills GPU **compute seconds** at $0.00125. Measured over
  // four real generations (requested → delivered → compute s):
  //   120 →  19.73 →  71.58    60 →  56.67 → 191.81
  //   300 → 194.85 → 409.51   300 → 217.44 → 395.91
  // Compute tracks the DELIVERED track length, not the requested ceiling — the
  // 120 s request cost less than the 60 s one because its lyrics ran out after
  // 19.73 s. Least-squares fit: `USD ≈ 0.085 + 0.00204 × delivered_s`, i.e. a
  // ~$0.09 fixed overhead plus a shallow slope. Neither flat shape works:
  // `olivesPerSecond` underprices short tracks (it can't recover the overhead)
  // and `olivesPerMinuteCeil` overcharges long ones, so the tiers below track
  // the fitted curve at cost +20%, rounded to 0.1 olive. The fit is noisy at
  // the short end (±40% at 20 s), which the overhead-dominated 60 s tier
  // absorbs.
  // Because cost follows the delivered length while `duration` is only a
  // ceiling, the charge is held at the requested tier and refunded down to the
  // delivered tier on completion (see minimaxMusic3Billing.ts).
  'minimax-music-3': {
    olivesPerAsset: 8.4,
    durationTiers: [
      { maxDuration: 60, olivesPerAsset: 2.5 },
      { maxDuration: 120, olivesPerAsset: 4.0 },
      { maxDuration: 180, olivesPerAsset: 5.4 },
      { maxDuration: 240, olivesPerAsset: 6.9 },
      { maxDuration: 300, olivesPerAsset: 8.4 },
    ],
  },
  lyria2: { olivesPerAsset: 0.9 },
  // Lyria 3 (ENG-599) replaces the dropped Lyria 2, priced at fal cost +25%.
  // Base: fal $0.04/track (fixed 30s). Pro: fal $0.08/track (full songs up to ~3 min).
  lyria3: { olivesPerAsset: 0.5 },
  'lyria-3-pro': { olivesPerAsset: 1.0 },
  // Lyria 3.5 (ENG-906): fal list $0.10/track (full songs up to ~3 min), Pricing v2 +20%.
  // Google models carry no fal contract discount, so list is the cost basis.
  'lyria-3.5': { olivesPerAsset: 1.2 },
  // ElevenLabs Music (ENG-867): fal now lists $0.60/output min (was $0.80);
  // legacy 10%-below-FAL basis -> 5.4. The direct ElevenLabs route costs
  // $0.15/min but fal stays the fallback, so fal remains the pricing floor.
  'elevenlabs-music': { olivesPerMinuteCeil: 5.4 },
  'stable-audio-25': { olivesPerAsset: 1.8 },
  'stable-audio-25-inpaint': { olivesPerAsset: 1.8 },
  // DeepFilterNet 3 speech enhancement (ENG-841): fal $0.001/sec = $0.06/min,
  // x1.2 = $0.072/min -> 0.7 olives per started minute of source audio.
  deepfilternet3: { olivesPerMinuteCeil: 0.7 },
  // VEED Clean Audio speech denoising (ENG-981): fal list $0.0125 per started minute
  // of input audio, x1.2 = $0.015 -> 0.15, rounded half-up to 0.2 (0.1 is below cost).
  'veed-clean-audio': { olivesPerMinuteCeil: 0.2 },
  // ByteDance Seed Audio 1.0: FAL bills $0.1875 per minute of generated audio.
  // The API has no duration parameter, so we hold the 2-minute maximum upfront
  // and refund the difference after completion based on actual output duration.
  'seed-audio-1.0': { olivesPerMinuteCeil: 1.7, minimumOlives: 3.4 },

  // Audio generation: TTS / voiceover. Billed per 1,000 characters of input text
  // (FAL bills these models per character; text length is known before submit).
  // FAL $/1k chars → +~20% → rounded to 0.1 olive (Pricing v2). 0.1-olive floor applies.
  'elevenlabs-tts-v3': { olivesPerThousandChars: 1.2 }, // FAL $0.10/1k → 1.2 olives/1k
  'elevenlabs-tts-v4': { olivesPerThousandChars: 1 }, // FAL $0.08/1k ×1.2 = $0.096 → 1.0 olives/1k
  'elevenlabs-tts-v4-turbo': { olivesPerThousandChars: 0.5 }, // FAL $0.04/1k ×1.2 = $0.048 → 0.5 olives/1k
  // BYOK-only: bills the team's ElevenLabs account; olives are never charged.
  // minimumOlives 0 opts out of the 0.1-olive floor so every estimate reads 0.
  'elevenlabs-tts-byok': { olivesPerThousandChars: 0, minimumOlives: 0 },
  'minimax-speech-02-hd': { olivesPerThousandChars: 1.2 }, // FAL $0.10/1k → 1.2 olives/1k
  'typecast-tts': { olivesPerThousandChars: 1.2 }, // Typecast ≈ $0.08/1k (1 credit/char) → 1.2 olives/1k
  // Gemini 3.8 TTS: priced off fal list; the direct Google route costs less per line.
  'gemini-3.8-flash-tts': { olivesPerThousandChars: 0.5 }, // FAL $0.045/1k ×1.2 = $0.054 → 0.5 olives/1k
  'gemini-3.8-flash-lite-tts': { olivesPerThousandChars: 0.4 }, // FAL $0.03/1k ×1.2 = $0.036 → 0.4 olives/1k

  // Mock (testing only)
  'mock-test': { olivesPerSecond: 3 },
  'mock-test-edit': { olivesPerSecond: 3 },
  'mock-test-image': { olivesPerAsset: 1 },
  'mock-test-set': { olivesPerAsset: 1 },
  'mock-test-audio': { olivesPerAsset: 1 },
  'mock-upscale-image': { olivesPerAsset: 1 },
  'mock-upscale-video': { olivesPerSecond: 1 },
} satisfies Record<string, ModelOliveCost>

const SEEDANCE_FAL_OLIVE_COSTS = {
  'seedance-2.0': {
    olivesPerSecond: 6.1,
    resolutionTiers: [
      { maxHeight: 480, olivesPerSecond: 1.2 },
      { maxHeight: 720, olivesPerSecond: 2.7 },
      { maxHeight: 1080, olivesPerSecond: 6.1 },
      { maxHeight: 2160, olivesPerSecond: 14.0 },
    ],
    videoInputDiscount: 0.9,
  },
  'seedance-2.0-fast': {
    olivesPerSecond: 2.2,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 1.0 }],
    videoInputDiscount: 0.9,
  },
  'seedance-2.0-mini': {
    olivesPerSecond: 1.4,
    resolutionTiers: [{ maxHeight: 480, olivesPerSecond: 0.6 }],
    videoInputDiscount: 0.9,
  },
  'seedance-2.5': SEEDANCE_25_FAL_CARD,
  'seedance-2.5-edit': SEEDANCE_25_EDIT_FAL_CARD,
  // A draft made on fal completes on fal (`bytedance/seedance-2.5/draft/complete`)
  // and bills the fal card's 1080p tier; the custom IDs already publish these figures.
  'seedance-2.5-draft-enhance': { olivesPerSecond: 11.1, videoInputOlivesPerSecond: 6.7 },
  'seedance-2.5-edit-draft-enhance': { olivesPerSecond: 13.4 },
} satisfies Record<string, ModelOliveCost>

/**
 * Resolves the active provider's user rate card.
 *
 * Note the naming is historical and reads backwards against how production is
 * configured: `MODEL_OLIVE_COSTS` holds the native BytePlus rates, but with
 * `SEEDANCE_20_PROVIDER=fal` those reach only the orgs allowlisted in
 * seedance20ProviderConfig (`resolveSeedanceRoutePlan`). Everyone else — every
 * public customer — routes to Fal and is billed the Fal card below, which sits
 * ~5% under Fal's list price. Marketing surfaces must quote the Fal card for
 * that reason; see `publicOliveCost` in frontend/src/lib/marketing-config.ts.
 */
export function getProviderAwareModelOliveCost(id: keyof typeof MODEL_OLIVE_COSTS, provider: string): ModelOliveCost {
  if (provider === 'fal.ai' && id in SEEDANCE_FAL_OLIVE_COSTS) {
    return SEEDANCE_FAL_OLIVE_COSTS[id as keyof typeof SEEDANCE_FAL_OLIVE_COSTS]
  }
  return MODEL_OLIVE_COSTS[id]
}

/**
 * Replaces EVERY rate-card field of `pricing` with `card`'s, keeping the
 * non-card fields (type, display, provider costs). A plain spread is wrong
 * here: the fal Seedance cards express reference pricing as a relative
 * `videoInputDiscount` while the native cards use an absolute
 * `videoInputOlivesPerSecond`, so a field absent from the new card must be
 * cleared, not inherited.
 */
export function replaceModelOliveCost<T extends ModelOliveCost>(pricing: T, card: ModelOliveCost): T {
  return {
    ...pricing,
    olivesPerSecond: card.olivesPerSecond,
    videoInputOlivesPerSecond: card.videoInputOlivesPerSecond,
    audioSurchargeOlives: card.audioSurchargeOlives,
    voiceSurchargeOlives: card.voiceSurchargeOlives,
    olivesPerAsset: card.olivesPerAsset,
    olivesPerMinuteCeil: card.olivesPerMinuteCeil,
    olivesPerThousandChars: card.olivesPerThousandChars,
    minimumOlives: card.minimumOlives,
    resolutionTiers: card.resolutionTiers,
    imageResolutionTiers: card.imageResolutionTiers,
    durationTiers: card.durationTiers,
    megapixelTiers: card.megapixelTiers,
    qualityMultipliers: card.qualityMultipliers,
    perSecondQualityTiers: card.perSecondQualityTiers,
    videoInputDiscount: card.videoInputDiscount,
  }
}

/**
 * The visual inputs of a Seedance request that decide its provider under
 * route-split (ENG-755). Field names match `GenerationParams`, so a full
 * params object satisfies this shape structurally.
 */
export interface SeedanceMediaInputSignals {
  imageUrl?: string
  endFrameUrl?: string
  imageReferences?: readonly string[]
  videoReferences?: readonly unknown[]
  audioReferences?: readonly unknown[]
  elements?: readonly unknown[]
}

/**
 * Single source of truth for what counts as a "media input" on a route-split
 * Seedance request (ENG-755). BytePlus direct moderates every media input —
 * start/end frames and audio references as much as visual references — and rejects faces far more often
 * than fal does, so ALL of them route the request to fal. The backend routing
 * decision and the frontend price estimate MUST both use this predicate; if
 * they ever disagree, the quoted price and the charged price diverge.
 */
export function seedanceHasMediaInputs(signals: SeedanceMediaInputSignals | undefined): boolean {
  if (!signals) return false
  return Boolean(
    signals.imageUrl?.trim() ||
    signals.endFrameUrl?.trim() ||
    signals.imageReferences?.length ||
    signals.videoReferences?.length ||
    signals.audioReferences?.length ||
    signals.elements?.length,
  )
}

/**
 * Topaz image models routed through the generative endpoint (image/enhance-gen/async).
 * Membership here decides ROUTING only, not price: models bill on the
 * 'topaz-image-enhance-gen' tier unless they are also in TOPAZ_BLOOM_IMAGE_MODELS,
 * which overrides to 'topaz-image-enhance-bloom' (billing and the frontend estimate
 * check the Bloom list first).
 */
export const TOPAZ_GENERATIVE_IMAGE_MODELS: string[] = [
  'Standard MAX',
  'Recovery V2',
  'Wonder',
  'Wonder 3',
  'Redefine',
  'Bloom Realism',
]

/**
 * Subset of TOPAZ_GENERATIVE_IMAGE_MODELS billed on the pricier Bloom tier (2 MP/credit,
 * 2× the other generative models). A new Bloom model must be added to BOTH lists:
 * the one above for endpoint routing, this one for pricing.
 */
export const TOPAZ_BLOOM_IMAGE_MODELS: string[] = ['Bloom Realism']

/**
 * Maximum INPUT megapixels the Topaz Image API accepts per model. Submitting a
 * source above the cap is rejected with HTTP 413 before any processing starts,
 * so the provider downscales to fit rather than failing the job (ENG-710).
 *
 * These are *input* limits and are unrelated to the output `megapixelTiers`
 * used for pricing. Values come from Topaz's model docs
 * (developer.topazlabs.com/image-models/…); `Recovery V2` is the limit the API
 * itself reported in the 413 that prompted this. Models Topaz doesn't document
 * fall back to the family default below.
 */
export const TOPAZ_IMAGE_INPUT_MP_LIMITS: Record<string, number> = {
  'Standard V2': 512,
  'Low Resolution V2': 512,
  'Text Refine': 512,
  CGI: 512,
  'Standard MAX': 24,
  'Recovery V2': 24,
  Wonder: 128,
  Redefine: 256,
}

/** Fallback input cap for GAN (image/enhance) models Topaz doesn't document. */
export const TOPAZ_GAN_IMAGE_INPUT_MP_LIMIT = 512

/**
 * Fallback input cap for generative (image/enhance-gen) models Topaz doesn't
 * document. Deliberately the conservative 24 MP the documented generative
 * models enforce — downscaling a rare oversized source costs a little detail;
 * guessing high costs the whole job.
 */
export const TOPAZ_GENERATIVE_IMAGE_INPUT_MP_LIMIT = 24

/**
 * Resolves the input megapixel cap for a Topaz image model. Unknown models get
 * their endpoint family's default, so adding a Topaz model never requires a
 * provider change — only an entry here when its documented limit differs.
 */
export function getTopazImageInputMegapixelLimit(model: string): number {
  const declared = TOPAZ_IMAGE_INPUT_MP_LIMITS[model]
  if (declared != null) return declared
  return TOPAZ_GENERATIVE_IMAGE_MODELS.includes(model)
    ? TOPAZ_GENERATIVE_IMAGE_INPUT_MP_LIMIT
    : TOPAZ_GAN_IMAGE_INPUT_MP_LIMIT
}

/** Set creation costs by mode (olives). */
export const SET_CREATION_COST_FAST = 20
export const SET_CREATION_COST_QUALITY = 45
export const SET_SUPPLEMENTARY_IMAGE_SURCHARGE = 5

export function calculateSetCreationCost(mode: SetCreationMode, hasSupplementaryImages: boolean = false): number {
  const baseCost = mode === 'quality' ? SET_CREATION_COST_QUALITY : SET_CREATION_COST_FAST
  return hasSupplementaryImages ? baseCost + SET_SUPPLEMENTARY_IMAGE_SURCHARGE : baseCost
}

/** Union of all model IDs that have pricing entries. */
export type PricedModelId = keyof typeof MODEL_OLIVE_COSTS

/**
 * A provider shutdown with an announced date. The model is offered normally
 * until `sunsetAt` and unlisted from that instant on — editor picker, `/models`,
 * MCP `get_models`, model-name resolution, and the marketing catalog all read
 * this one schedule, so retirement day needs no deploy.
 */
export interface ModelSunset {
  /** ISO-8601 instant the model stops being offered. */
  sunsetAt: string
  /**
   * Why the model is gone, in copy a customer can read: it is the body of the
   * error an API or MCP caller gets for a retired model id.
   */
  reason: string
}

const SORA_SUNSET_AT = '2026-09-24T00:00:00Z'

/**
 * Scheduled provider shutdowns, keyed by Martini model id.
 *
 * A retired model keeps its handler and its `MODEL_OLIVE_COSTS` entry so
 * provenance on existing clips still resolves and historical billing still
 * prices; delete all three together once nothing references the id anymore.
 *
 * OpenAI's final notice: the Sora Videos API (`sora-2`, `sora-2-pro`) shuts
 * down on September 24, 2026. Unlisted at 00:00 UTC that day — OpenAI did not
 * name a timezone, and a generation that starts hours before the upstream
 * goes dark can only fail, so err early rather than late.
 */
export const MODEL_SUNSETS: Readonly<Record<string, ModelSunset>> = {
  'sora-2': {
    sunsetAt: SORA_SUNSET_AT,
    reason:
      'Sora 2 Pro was retired on September 24, 2026, when OpenAI shut down the Sora API. Choose another video model.',
  },
  'sora-2-standard': {
    sunsetAt: SORA_SUNSET_AT,
    reason: 'Sora 2 was retired on September 24, 2026, when OpenAI shut down the Sora API. Choose another video model.',
  },
}

/** The sunset scheduled for a model, or undefined when none is. */
export function getModelSunset(modelId: string): ModelSunset | undefined {
  return MODEL_SUNSETS[modelId]
}

/**
 * True once `now` (epoch ms, defaults to the wall clock) has reached the
 * model's scheduled sunset. Models with no schedule are never sunset.
 */
export function isModelSunset(modelId: string, now: number = Date.now()): boolean {
  const sunset = MODEL_SUNSETS[modelId]
  return sunset !== undefined && now >= Date.parse(sunset.sunsetAt)
}

/** Get the per-second olive cost for a model, or throw if missing. */
export function getOlivesPerSecond(id: PricedModelId): number {
  const cost: ModelOliveCost = MODEL_OLIVE_COSTS[id]
  if (cost.olivesPerSecond === undefined) throw new Error(`MODEL_OLIVE_COSTS['${id}'] is missing olivesPerSecond`)
  return cost.olivesPerSecond
}

/**
 * Resolve per-second olive cost accounting for resolution tiers.
 * Returns the tier rate if outputHeight matches a tier, otherwise the default rate.
 */
export function resolveOlivesPerSecond(cost: ModelOliveCost, outputHeight?: number): number {
  const base = cost.olivesPerSecond ?? 0
  if (!cost.resolutionTiers || !outputHeight) return base
  for (const tier of cost.resolutionTiers) {
    if (outputHeight <= tier.maxHeight) return tier.olivesPerSecond
  }
  return base
}

/** Resolve the combined video-input/output rate for a selected resolution tier. */
export function resolveVideoInputOlivesPerSecond(cost: ModelOliveCost, outputHeight?: number): number {
  const outputRate = resolveOlivesPerSecond(cost, outputHeight)
  const base = cost.videoInputOlivesPerSecond ?? outputRate * (cost.videoInputDiscount ?? 1)
  if (!cost.resolutionTiers || !outputHeight) return base
  for (const tier of cost.resolutionTiers) {
    if (outputHeight <= tier.maxHeight) {
      return tier.videoInputOlivesPerSecond ?? tier.olivesPerSecond * (cost.videoInputDiscount ?? 1)
    }
  }
  return base
}

/** Resolve the audio surcharge for the selected video resolution tier. */
export function resolveAudioSurchargeOlives(cost: ModelOliveCost, outputHeight?: number): number {
  const base = cost.audioSurchargeOlives ?? 0
  if (!cost.resolutionTiers || !outputHeight) return base
  for (const tier of cost.resolutionTiers) {
    if (outputHeight <= tier.maxHeight) return tier.audioSurchargeOlives ?? base
  }
  return base
}

/**
 * Distinct reference images a request will actually be billed for.
 *
 * Mirrors what handlers send to the provider (see buildHailuo03References):
 * element frontals and loose image references collapse into one deduped list,
 * and a start-frame request strips references entirely — so a repeated URL or
 * a stripped list must never be charged. Both the backend charge and the UI
 * estimate call this same function; if the counts can drift, the quote lies.
 */
export function countBillableReferenceImages(input: {
  /** True when the request carries a start frame — reference-stripping mode. */
  startFramePresent: boolean
  imageReferenceUrls?: readonly string[] | null
  elementFrontalUrls?: readonly string[] | null
  maxImages?: number
}): number {
  if (input.startFramePresent) return 0
  const urls = [...(input.elementFrontalUrls ?? []), ...(input.imageReferenceUrls ?? [])].filter(Boolean)
  const distinct = new Set(urls).size
  return input.maxImages != null ? Math.min(distinct, input.maxImages) : distinct
}

export type ReferenceTokenTerms = NonNullable<ModelOliveCost['referenceTokenSurcharge']>

export interface ReferenceImageMeasurement {
  url: string
  width?: number
  height?: number
  processedWidth?: number
  processedHeight?: number
  tokens: number
  source: 'probed' | 'inline'
}

/** One reference audio clip as the provider tokenizes it: the seconds it receives, at the card's rate. */
export interface ReferenceAudioMeasurement {
  url: string
  seconds: number
  tokens: number
  /** `trim`: the request's trim window; `probed`: the source's length; `estimated`: the card's fallback. */
  source: 'trim' | 'probed' | 'estimated'
}

/** One reference video clip as the provider tokenizes it: the frames it receives, sampled at the card's rate. */
export interface ReferenceVideoMeasurement {
  url: string
  /** The window the worker sends, in seconds and in source frames. */
  seconds: number
  frames: number
  tokens: number
  /** `probed`: the source's own frame rate and length; `window`: the stamped trim at 24 fps; `estimated`: the card's fallback. */
  source: 'probed' | 'window' | 'estimated'
}

export interface ReferenceTokenBreakdown {
  includedTokens: number
  /** Image, audio and video tokens together; the allowance is shared. */
  measuredTokens: number
  billedTokens: number
  images: ReferenceImageMeasurement[]
  /** Present only when the request carries billable audio references. */
  audio?: ReferenceAudioMeasurement[]
  audioTokens?: number
  /** Present only when the request carries billable video references. */
  video?: ReferenceVideoMeasurement[]
  videoTokens?: number
}

/** Tighter than fal's accepted range so downstream rounding remains safe. */
export const FAL_AR_MIN_SAFE = 0.5
export const FAL_AR_MAX_SAFE = 2.0

export function paddedReferenceDimensions(dims: { width: number; height: number }) {
  const { width, height } = dims
  if (width / height < FAL_AR_MIN_SAFE) return { width: Math.ceil(height * FAL_AR_MIN_SAFE), height }
  if (width / height > FAL_AR_MAX_SAFE) return { width, height: Math.ceil(width / FAL_AR_MAX_SAFE) }
  return { width, height }
}

export function processedReferenceDimensions(dims: { width: number; height: number }, terms: ReferenceTokenTerms) {
  dims = paddedReferenceDimensions(dims)
  const scale = terms.processedShortEdge / Math.min(dims.width, dims.height)
  return {
    width: Math.round((dims.width * scale) / terms.dimensionStep) * terms.dimensionStep,
    height: Math.round((dims.height * scale) / terms.dimensionStep) * terms.dimensionStep,
  }
}

export function referenceImageTokens(
  dims: { width?: number; height?: number } | undefined,
  terms: ReferenceTokenTerms,
): number {
  if (
    !dims?.width ||
    !dims.height ||
    !Number.isFinite(dims.width) ||
    !Number.isFinite(dims.height) ||
    dims.width <= 0 ||
    dims.height <= 0
  )
    return terms.estimateTokensPerUnmeasuredImage
  const processed = processedReferenceDimensions({ width: dims.width, height: dims.height }, terms)
  return Math.ceil((processed.width * processed.height) / terms.pixelsPerToken)
}

/**
 * Tokens the provider bills for one reference audio clip. An unknown length is estimated at
 * the card's fallback (the model's combined cap), never at zero, and a card without an audio
 * rate bills none.
 */
export function referenceAudioTokens(seconds: number | undefined, terms: ReferenceTokenTerms): number {
  const rate = terms.tokensPerAudioSecond
  if (rate == null) return 0
  const clip =
    typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0
      ? seconds
      : (terms.estimateSecondsPerUnmeasuredAudio ?? 0)
  return Math.ceil(clip * rate)
}

/**
 * Tokens the provider bills for one reference video clip: the frames it receives (a window in
 * seconds at the source frame rate when the count is not known), sampled at the card's rate,
 * each sample priced at the OUTPUT resolution's frame size. An unknown clip is estimated at
 * the card's cap, never at zero, and a card without a video rate bills none.
 */
export function referenceVideoTokens(
  clip: { frames?: number; seconds?: number; frameRate?: number },
  outputHeight: number | undefined,
  terms: ReferenceTokenTerms,
): number {
  const samplesPerFrame = terms.videoSamplesPerFrame
  const tiers = terms.videoTokensPerSampleTiers
  if (samplesPerFrame == null || !tiers?.length) return 0
  const positive = (value: number | undefined) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
  const fps = positive(clip.frameRate) ?? terms.estimateFramesPerSecondForUnmeasuredVideo ?? 24
  const frames = positive(clip.frames) ?? (positive(clip.seconds) ?? terms.estimateSecondsPerUnmeasuredVideo ?? 0) * fps
  const tier = (outputHeight != null && tiers.find(t => outputHeight <= t.maxHeight)) || tiers[tiers.length - 1]
  return Math.floor(frames * samplesPerFrame) * tier.tokensPerSample
}

/**
 * The reference images a token card bills: provider order, deduplicated, capped. A start frame
 * strips nothing here — the token-card models (the H3 Max family) send every reference
 * alongside a first frame, unlike the per-image card's {@link countBillableReferenceImages}.
 */
export function billableReferenceImageUrls(input: {
  imageReferenceUrls?: readonly string[] | null
  elementFrontalUrls?: readonly string[] | null
  maxImages?: number
}): string[] {
  return [...new Set([...(input.elementFrontalUrls ?? []), ...(input.imageReferenceUrls ?? [])].filter(Boolean))].slice(
    0,
    input.maxImages,
  )
}

/**
 * The list-card surcharge for a request's reference tokens: everything beyond the included
 * allowance at the card's per-1k rate, rounded to the 0.0001-olive charge step. Takes the
 * pooled measured total (images, audio and video, as `generationReferenceTokens` sums them)
 * or, for an image-only estimate, the image measurements capped at the card's slot limit.
 */
export function resolveReferenceTokenSurchargeOlives(
  cost: ModelOliveCost,
  measured: number | readonly Pick<ReferenceImageMeasurement, 'tokens'>[],
): number {
  const terms = cost.referenceTokenSurcharge
  if (!terms) return 0
  const tokens =
    typeof measured === 'number'
      ? measured
      : measured.slice(0, terms.maxImages).reduce((sum, image) => sum + image.tokens, 0)
  return Math.round(((Math.max(0, tokens - terms.includedTokens) * terms.olivesPer1kTokens) / 1000) * 10000) / 10000
}

/** Flat olives added for reference images beyond the model's included allowance. */
export function resolveReferenceImageSurchargeOlives(cost: ModelOliveCost, billableReferenceImages: number): number {
  const terms = cost.referenceImageSurcharge
  if (!terms || billableReferenceImages <= 0) return 0
  const capped = Math.min(billableReferenceImages, terms.maxImages)
  return Math.max(0, capped - terms.includedImages) * terms.olivesPerImage
}

/** Get the per-asset olive cost for a model, or throw if missing. */
export function getOlivesPerAsset(id: PricedModelId): number {
  const cost: ModelOliveCost = MODEL_OLIVE_COSTS[id]
  if (cost.olivesPerAsset === undefined) throw new Error(`MODEL_OLIVE_COSTS['${id}'] is missing olivesPerAsset`)
  return cost.olivesPerAsset
}

/** Get the per-1,000-characters olive cost for a TTS model, or throw if missing. */
export function getOlivesPerThousandChars(id: PricedModelId): number {
  const cost: ModelOliveCost = MODEL_OLIVE_COSTS[id]
  if (cost.olivesPerThousandChars === undefined)
    throw new Error(`MODEL_OLIVE_COSTS['${id}'] is missing olivesPerThousandChars`)
  return cost.olivesPerThousandChars
}

/**
 * Resolve per-asset olive cost accounting for duration tiers.
 * Returns the tier rate if duration matches a tier, otherwise the default olivesPerAsset.
 */
export function resolveOlivesPerAsset(cost: ModelOliveCost, duration?: number): number {
  const base = cost.olivesPerAsset ?? 0
  if (!cost.durationTiers || duration == null) return base
  for (const tier of cost.durationTiers) {
    if (duration <= tier.maxDuration) return tier.olivesPerAsset
  }
  return base
}

/**
 * Resolve per-asset olive cost accounting for output megapixel tiers.
 * Returns the tier rate if outputMP matches a tier, otherwise the default olivesPerAsset.
 */
export function resolveOlivesPerAssetMP(cost: ModelOliveCost, outputMP?: number): number {
  const base = cost.olivesPerAsset ?? 0
  if (!cost.megapixelTiers || outputMP == null) return base
  for (const tier of cost.megapixelTiers) {
    if (outputMP <= tier.maxMP) return tier.olivesPerAsset
  }
  return base
}

/**
 * Resolve olives for a duration-priced model.
 *
 * - `olivesPerMinuteCeil`: bills per output minute, rounded UP. 10 s → 1 min, 61 s → 2 min.
 * - `olivesPerAsset` + `durationTiers`: tier-based fixed cost (existing SFX shape).
 * - `olivesPerAsset` only: flat per-asset.
 *
 * When `durationSeconds` is omitted/null and the cost shape uses `olivesPerMinuteCeil`,
 * returns `minimumOlives ?? olivesPerMinuteCeil` as a lower-bound estimate. This is
 * not equivalent to passing `0`, which rounds up to 1 minute and applies
 * `Math.max(minimumOlives, rate)`.
 *
 * Returns 0 if the cost shape doesn't match any duration-aware billing rule.
 */
export function resolveOlivesForDuration(cost: ModelOliveCost, durationSeconds?: number): number {
  if (cost.olivesPerMinuteCeil !== undefined) {
    if (durationSeconds == null) {
      return cost.minimumOlives ?? cost.olivesPerMinuteCeil
    }
    const minutes = Math.max(1, Math.ceil(durationSeconds / 60))
    // Olives are priced to 0.1; snap the multiple so 5.4 × 3 bills 16.2, not 16.200000000000003.
    const total = Math.round(minutes * cost.olivesPerMinuteCeil * 10) / 10
    return cost.minimumOlives !== undefined ? Math.max(cost.minimumOlives, total) : total
  }
  if (cost.olivesPerAsset !== undefined) {
    return resolveOlivesPerAsset(cost, durationSeconds)
  }
  return 0
}

/**
 * Resolve olives for a per-character (TTS / voiceover) model from the input text
 * length. Cost = (charCount / 1000) × `olivesPerThousandChars`, floored at
 * `minimumOlives` (defaults to the standard 0.1-olive minimum) so a short line
 * never bills to ~0. Returns 0 when the model isn't per-character priced.
 */
export function resolveOlivesForChars(cost: ModelOliveCost, charCount: number): number {
  if (cost.olivesPerThousandChars === undefined) return 0
  const raw = (Math.max(0, charCount) / 1000) * cost.olivesPerThousandChars
  const floor = cost.minimumOlives ?? 0.1
  return Math.max(floor, Math.round(raw * 10000) / 10000)
}

// ---------------------------------------------------------------------------
// TTS dialogue (Gemini 3.8 TTS: two speakers in one take)
// ---------------------------------------------------------------------------

/** One voice in a TTS dialogue. `id` is the alias the turns refer to (e.g. "Ava"). */
export interface TtsDialogueSpeaker {
  id: string
  voice: string
}

/** One spoken line, with optional delivery direction that overrides the take's style. */
export interface TtsDialogueTurn {
  speakerId: string
  text: string
  styleInstructions?: string
}

export interface TtsDialogue {
  speakers: TtsDialogueSpeaker[]
  turns: TtsDialogueTurn[]
}

/**
 * The readable `Alias: line` transcript of a dialogue. It stands in for the
 * prompt wherever one is displayed or stored; the provider receives the turns.
 */
export function ttsDialogueTranscript(dialogue: TtsDialogue): string {
  return dialogue.turns.map(turn => `${turn.speakerId}: ${turn.text}`).join('\n')
}

/**
 * Billable characters of a TTS request: the spoken text of every turn for a
 * dialogue (aliases and style direction are free on both routes), else the
 * prompt.
 */
export function ttsBillableChars(params: { prompt?: string | null; dialogue?: TtsDialogue | null }): number {
  if (params.dialogue) return params.dialogue.turns.reduce((sum, turn) => sum + turn.text.length, 0)
  return params.prompt?.length ?? 0
}

/** Resolve per-image olive cost accounting for resolution tiers, megapixels, and quality. */
export function resolveImageOlivesPerAsset(
  cost: ModelOliveCost,
  opts: { resolution?: string | null; quality?: string | null; outputMP?: number },
): number {
  // Match the requested resolution to a tier; fall back to the first (base) tier when the
  // resolution is omitted OR doesn't match any tier. This keeps single-tier models (Reve,
  // Ideogram 4.0) and their quality pricing correct even if a stray resolution is passed.
  const imageTier = cost.imageResolutionTiers
    ? ((opts.resolution ? cost.imageResolutionTiers.find(tier => tier.resolution === opts.resolution) : undefined) ??
      cost.imageResolutionTiers[0])
    : null
  if (imageTier) {
    if (opts.quality && imageTier.qualityOlivesPerAsset?.[opts.quality] !== undefined) {
      return imageTier.qualityOlivesPerAsset[opts.quality]
    }
    return imageTier.olivesPerAsset
  }

  let resolved =
    cost.megapixelTiers && opts.outputMP != null
      ? resolveOlivesPerAssetMP(cost, opts.outputMP)
      : (cost.olivesPerAsset ?? 0)

  // Backward-compatible multiplier path for models that have not opted into explicit tiers.
  if (!cost.megapixelTiers && !cost.imageResolutionTiers) {
    if (opts.resolution === '2K') resolved *= 1.5
    if (opts.resolution === '4K') resolved *= 2
  }

  if (opts.quality && cost.qualityMultipliers?.[opts.quality] !== undefined) {
    resolved *= cost.qualityMultipliers[opts.quality]
  }

  return Math.round(resolved * 10000) / 10000
}

/** Get the audio surcharge olive cost for a model, or throw if missing. */
export function getAudioSurchargeOlives(id: PricedModelId): number {
  const cost: ModelOliveCost = MODEL_OLIVE_COSTS[id]
  if (cost.audioSurchargeOlives === undefined)
    throw new Error(`MODEL_OLIVE_COSTS['${id}'] is missing audioSurchargeOlives`)
  return cost.audioSurchargeOlives
}

// ============================================
// Chat (AI Assistant) Models
// ============================================

export interface ChatSessionSummary {
  id: string
  title: string | null
  createdAt: string
  updatedAt: string
  /**
   * Every server response carries it. Absent only on a session the web client
   * builds locally from a bare id before the next list read (the desktop pane
   * never reads it); never fill it with a default.
   */
  allowance?: ChatAllowance
}

/** Per-thread olive budget for auto-approving generation tool calls (v1.3 mobile chat). */
export interface ChatAllowance {
  allowanceOlives: number
  allowanceSpent: number
  autoApprove: boolean
}

/** A chat thread as the phone's cross-project Chat tab lists it (GET /mobile/v1/chat/sessions). */
export interface MobileChatSessionSummary extends ChatSessionSummary {
  projectId: string
  projectName: string
}

/** Who runs the weights. Decides whether a first-party key can route around fal. */
export type ChatModelVendor = 'anthropic' | 'openai' | 'xai' | 'minimax' | 'mock'

/**
 * Per-token chat pricing for the inline AI chat pane. LLM inference runs on
 * fal's `openrouter/router` endpoint (per-token pass-through billing) unless
 * the vendor's first-party key is set (see chat/provider.ts); olive rates are
 * the vendor list price at $0.10/olive either way — LLM inference is billed at
 * cost, no margin and no discount (founder decision 2026-09-13), per
 * docs/pricing-margins.md.
 */
export interface ChatModelConfig {
  vendor: ChatModelVendor
  /** Model slug passed to fal's openrouter/router endpoint. */
  providerModelId: string
  /**
   * Model id on the vendor's own API, used when that vendor's key is present
   * (ANTHROPIC_API_KEY / OPENAI_API_KEY). Null when only the fal route exists.
   */
  directModelId: string | null
  label: string
  /** Olives per 1M input (prompt) tokens. */
  olivesPerMInputTokens: number
  /** Olives per 1M cached input tokens read from the provider cache. */
  olivesPerMCacheReadInputTokens: number
  /** Olives per 1M input tokens written to the provider cache. */
  olivesPerMCacheWriteInputTokens: number
  /** Olives per 1M output (completion) tokens. */
  olivesPerMOutputTokens: number
  /**
   * Olives deducted upfront per user message and refunded down to actual
   * usage after the turn settles. This is the user-charge ceiling. Provider
   * execution has separate request, output-token, step, and best-effort
   * metered-budget stops because a completed provider step can overshoot.
   */
  holdOlives: number
  /** Whether this model accepts image FileUIParts in chat turns. */
  supportsImageInput: boolean
}

export const CHAT_MODELS = {
  'opus-5.5': {
    vendor: 'anthropic',
    providerModelId: 'anthropic/claude-opus-5.5',
    directModelId: 'claude-opus-5-5',
    label: 'Claude Opus 5.5',
    // $4/$20 list, 1M context, 128K output. Thinking is always on (default
    // effort medium) and forced tool choice is rejected; the chat loop sets
    // neither. Cache reads are 0.05× input, not Anthropic's usual 0.1×.
    olivesPerMInputTokens: 40, // $4.00/M list
    olivesPerMCacheReadInputTokens: 2, // $0.20/M list
    olivesPerMCacheWriteInputTokens: 50, // $5.00/M list (1.25× input)
    olivesPerMOutputTokens: 200, // $20.00/M list
    // Twice the standard hold keeps the same ~250K uncached-input headroom as
    // the $2/M models (see gpt-6-astra).
    holdOlives: 10,
    supportsImageInput: true,
  },
  'chat-gpt': {
    vendor: 'openai',
    providerModelId: 'openai/gpt-6.1-sol',
    directModelId: 'gpt-6.1-sol',
    label: 'GPT-6.1 Sol',
    // Sol is GPT-6's balanced tier at 1M context; 6.1 keeps 6 Sol's $2/$10 and
    // halves cached input to $0.10/M. The >272K-token tier (2× input, 1.5×
    // output) is not modeled; the chat context caps keep turns under it.
    olivesPerMInputTokens: 20, // $2.00/M list
    olivesPerMCacheReadInputTokens: 1, // $0.10/M list
    olivesPerMCacheWriteInputTokens: 25, // $2.50/M list (1.25× input)
    olivesPerMOutputTokens: 100, // $10.00/M list
    holdOlives: 5,
    supportsImageInput: true,
  },
  'chat-sonnet': {
    vendor: 'anthropic',
    providerModelId: 'anthropic/claude-sonnet-5.5',
    directModelId: 'claude-sonnet-5-5',
    label: 'Claude Sonnet 5.5',
    // $2/$10 list, 1M context. Like Opus 5.5, cache reads are 0.05× input
    // ($0.10/M), not Anthropic's usual 0.1×.
    olivesPerMInputTokens: 20, // $2.00/M list
    olivesPerMCacheReadInputTokens: 1, // $0.10/M list
    olivesPerMCacheWriteInputTokens: 25, // $2.50/M list (5-minute writes, 1.25× input)
    olivesPerMOutputTokens: 100, // $10.00/M list
    holdOlives: 5,
    supportsImageInput: true,
  },
  'gpt-6-astra': {
    vendor: 'openai',
    providerModelId: 'openai/gpt-6-astra',
    directModelId: 'gpt-6-astra',
    label: 'GPT-6 Astra',
    // OpenAI's flagship (released 2026-09-04): $10/$50 list, 1M context, 128K
    // output. Same uncached price as Fable 5.1; OpenAI's cache reads are 4×
    // Anthropic's. The >272K-token tier (2× input, 1.5× output) is not modeled.
    olivesPerMInputTokens: 100, // $10.00/M list
    olivesPerMCacheReadInputTokens: 10, // $1.00/M list
    olivesPerMCacheWriteInputTokens: 125, // $12.50/M list (1.25× input)
    olivesPerMOutputTokens: 500, // $50.00/M list
    // 4× the standard hold: at 100 olives/M input, 5 olives would stop a
    // tool loop after ~50K uncached input tokens.
    holdOlives: 20,
    supportsImageInput: true,
  },
  'fable-5.1': {
    vendor: 'anthropic',
    providerModelId: 'anthropic/claude-fable-5.1',
    directModelId: 'claude-fable-5-1',
    label: 'Claude Fable 5.1',
    // Anthropic's most capable model (released 2026-09-01): $10/$50 list, 1M
    // context, 128K output, thinking always on. Cache reads are $0.25/M
    // (0.025× input), cache writes 1.25× input.
    olivesPerMInputTokens: 100, // $10.00/M list
    olivesPerMCacheReadInputTokens: 2.5, // $0.25/M list
    olivesPerMCacheWriteInputTokens: 125, // $12.50/M list (1.25× input)
    olivesPerMOutputTokens: 500, // $50.00/M list
    holdOlives: 20, // see gpt-6-astra
    supportsImageInput: true,
  },
  'chat-grok': {
    vendor: 'xai',
    providerModelId: 'x-ai/grok-4.7',
    directModelId: null,
    label: 'Grok 4.7',
    // Grok 4.7 keeps 4.6's list rate: $2/$6, cached input $0.50/M.
    // The >200K-token long-context tier ($4/$12) is not modeled — turns stay well under it.
    olivesPerMInputTokens: 20, // $2.00/M list
    olivesPerMCacheReadInputTokens: 5, // $0.50/M list
    olivesPerMCacheWriteInputTokens: 20, // automatic cache population has no separate write surcharge
    olivesPerMOutputTokens: 60, // $6.00/M list
    holdOlives: 5,
    supportsImageInput: true,
  },
  'chat-minimax': {
    vendor: 'minimax',
    providerModelId: 'minimax/minimax-m3',
    directModelId: null,
    label: 'MiniMax M3',
    // The budget rung: ~6× cheaper output than Grok, with a 1M context.
    // (Upstream it also accepts video, but chat attachments are image-only —
    // see CHAT_ATTACHMENT_POLICY — so that capability is unreachable from here.)
    olivesPerMInputTokens: 3, // $0.30/M list
    olivesPerMCacheReadInputTokens: 0.6, // $0.06/M list
    olivesPerMCacheWriteInputTokens: 3, // no separate write surcharge — falls back to the input rate
    olivesPerMOutputTokens: 12, // $1.20/M list
    holdOlives: 5,
    supportsImageInput: true,
  },
  'chat-mock': {
    vendor: 'mock',
    providerModelId: 'mock',
    directModelId: null,
    label: 'Mock (dev)',
    olivesPerMInputTokens: 0,
    olivesPerMCacheReadInputTokens: 0,
    olivesPerMCacheWriteInputTokens: 0,
    olivesPerMOutputTokens: 0,
    holdOlives: 0,
    supportsImageInput: true,
  },
} as const satisfies Record<string, ChatModelConfig>

export type ChatModelId = keyof typeof CHAT_MODELS

/** Preselected in the chat pane, on new Action nodes, and by the MCP action tools. */
/**
 * Spending for non-generation work; generation budgets remain separate. This is what a
 * customer sees: one olive figure per resource kind. What the provider billed us stays
 * on the server (docs/agent-work-billing.md), in the engine's report.
 */
export interface AgentBillingSummary {
  sessionId: string
  policyVersion: string
  rateVersion: string
  budgetPolicy: string
  status: 'active' | 'funding_required' | 'reconciliation_required' | 'completed' | 'failed' | 'cancelled'
  estimatedOlives: number
  accruedOlives: number
  reservedOlives: number
  chargedOlives: number
  resources: Array<{
    kind: 'model' | 'compute' | 'tool'
    accruedOlives: number
    unmetered: boolean
  }>
}

/** The policy a session pinned before unified agent billing applied to it; its work was measured by the run itself. */
export const LEGACY_AGENT_BILLING_POLICY = 'legacy'

/** A session that bills the customer under the unified policy, as opposed to a legacy contract's placeholder. */
export function isUnifiedAgentBilling(
  billing: Pick<AgentBillingSummary, 'policyVersion'> | null | undefined,
): billing is AgentBillingSummary {
  return !!billing && billing.policyVersion !== LEGACY_AGENT_BILLING_POLICY
}

/**
 * What a customer has spent on agent work under a unified session: what has accrued
 * while the session is open (active, paused for funding, or flagged for
 * reconciliation) and the settled charge once it is terminal. Reserved olives are
 * holds, never spending. Every surface that shows a session's agent olives reads
 * this one figure, so run history, receipts, the public API and MCP agree.
 */
export function agentSpendOlives(
  billing: Pick<AgentBillingSummary, 'status' | 'accruedOlives' | 'chargedOlives'>,
): number {
  switch (billing.status) {
    case 'active':
    case 'funding_required':
    case 'reconciliation_required':
      return billing.accruedOlives
    case 'completed':
    case 'failed':
    case 'cancelled':
      return billing.chargedOlives
  }
}

/**
 * The agent olives a run shows: the session's spending under unified billing; the run's
 * own measurement (`agentCostOlives`) for a legacy contract or a run with no session.
 */
export function customerAgentOlives(
  billing: Pick<AgentBillingSummary, 'policyVersion' | 'status' | 'accruedOlives' | 'chargedOlives'> | null | undefined,
  measuredOlives: number,
): number {
  return isUnifiedAgentBilling(billing) ? agentSpendOlives(billing) : measuredOlives
}

export const DEFAULT_CHAT_MODEL_ID: ChatModelId = 'opus-5.5'

export function isChatModelId(value: unknown): value is ChatModelId {
  return typeof value === 'string' && value in CHAT_MODELS
}

/**
 * Checkpoint-1 Action agent ids, kept as silent aliases so stored Actions
 * keep running. New Actions use ChatModelId directly as agentId — the
 * Execution dropdown and the chat pane's model picker share one catalog.
 */
export const LEGACY_ACTION_AGENT_ALIASES: Record<string, ChatModelId> = {
  martini: 'chat-sonnet',
  'martini-fast': 'chat-grok',
  claude: 'chat-sonnet',
  grok: 'chat-grok',
  mock: 'chat-mock',
}

/** Resolve an Action agentId (chat model id or legacy alias) to a chat model. */
export function resolveActionAgentChatModel(agentId: string): ChatModelId | null {
  if (isChatModelId(agentId)) return agentId
  return LEGACY_ACTION_AGENT_ALIASES[agentId] ?? null
}

/** Minimum charge per chat message (platform 0.1-olive billing floor). */
export const CHAT_MIN_CHARGE_OLIVES = 0.1

export interface ChatTokenUsage {
  inputTokens: number
  inputNoCacheTokens?: number
  inputCacheReadTokens?: number
  inputCacheWriteTokens?: number
  outputTokens: number
}

export interface ChatCostBreakdown {
  uncachedInputOlives: number
  cacheReadInputOlives: number
  cacheWriteInputOlives: number
  outputOlives: number
  rawOlives: number
  chargedOlives: number
}

function normalizeChatInputBuckets(usage: ChatTokenUsage): {
  uncached: number
  cacheRead: number
  cacheWrite: number
} {
  const inputTokens = Math.max(0, usage.inputTokens)
  const hasDetails =
    usage.inputNoCacheTokens !== undefined ||
    usage.inputCacheReadTokens !== undefined ||
    usage.inputCacheWriteTokens !== undefined
  if (!hasDetails) return { uncached: inputTokens, cacheRead: 0, cacheWrite: 0 }

  const cacheRead = Math.min(inputTokens, Math.max(0, usage.inputCacheReadTokens ?? 0))
  const cacheWrite = Math.min(inputTokens - cacheRead, Math.max(0, usage.inputCacheWriteTokens ?? 0))
  // Any missing or inconsistent remainder is conservatively priced as normal
  // input so incomplete gateway details can never silently undercharge.
  return { uncached: inputTokens - cacheRead - cacheWrite, cacheRead, cacheWrite }
}

/**
 * Token counts are integers and rates have at most two decimals, so a bucket's
 * exact value has finitely many decimals; a real fraction of a tenth is never
 * smaller than this. Guards the ceil against float noise such as
 * 0.1 × 30 = 3.0000000000000004, which would otherwise bill 3.1 for 3.0.
 */
const CHAT_ROUNDING_EPSILON = 1e-9

/** tokens × rate first, then scale — keeps whole-number products exact. */
export function olivesForTokens(tokens: number, olivesPerMillion: number): number {
  return (tokens * olivesPerMillion) / 1_000_000
}

export function calculateChatCostBreakdown(modelId: ChatModelId, usage: ChatTokenUsage): ChatCostBreakdown {
  return calculateChatCostBreakdownAtRates(CHAT_MODELS[modelId], usage)
}

/** The four token rates a chat price is computed from. */
export type ChatTokenRates = Pick<
  ChatModelConfig,
  | 'olivesPerMInputTokens'
  | 'olivesPerMCacheReadInputTokens'
  | 'olivesPerMCacheWriteInputTokens'
  | 'olivesPerMOutputTokens'
>

const SONNET_5_PICKER_RATES: ChatTokenRates = {
  olivesPerMInputTokens: 30,
  olivesPerMCacheReadInputTokens: 3,
  olivesPerMCacheWriteInputTokens: 37.5,
  olivesPerMOutputTokens: 150,
}
const GPT_6_SOL_PICKER_RATES: ChatTokenRates = {
  olivesPerMInputTokens: 20,
  olivesPerMCacheReadInputTokens: 2,
  olivesPerMCacheWriteInputTokens: 25,
  olivesPerMOutputTokens: 100,
}

/**
 * Models that left the picker, at the rates the picker billed them, by direct and gateway id. Workflow runs,
 * recovered runs and the judge can still name them (WORKFLOW_CLAUDE_CODE_MODEL, a pinned run, the judge's
 * calibrated tier), so they keep the price they had instead of falling back to another model's.
 */
export const RETIRED_CHAT_MODEL_RATES: Readonly<Record<string, ChatTokenRates>> = {
  'claude-sonnet-5': SONNET_5_PICKER_RATES,
  'anthropic/claude-sonnet-5': SONNET_5_PICKER_RATES,
  'gpt-6-sol': GPT_6_SOL_PICKER_RATES,
  'openai/gpt-6-sol': GPT_6_SOL_PICKER_RATES,
}

/** The rates for a picker key, a current direct or gateway id, or a retired model; null when unknown. */
export function chatTokenRatesFor(model: string): ChatTokenRates | null {
  const id = (Object.keys(CHAT_MODELS) as ChatModelId[]).find(
    key => key === model || CHAT_MODELS[key].directModelId === model || CHAT_MODELS[key].providerModelId === model,
  )
  return id ? CHAT_MODELS[id] : (RETIRED_CHAT_MODEL_RATES[model] ?? null)
}

/** The chat turn price at explicit rates, for callers billing a model that is not (or no longer) in the picker. */
export function calculateChatCostBreakdownAtRates(cfg: ChatTokenRates, usage: ChatTokenUsage): ChatCostBreakdown {
  const input = normalizeChatInputBuckets(usage)
  const uncachedInputOlives = olivesForTokens(input.uncached, cfg.olivesPerMInputTokens)
  const cacheReadInputOlives = olivesForTokens(input.cacheRead, cfg.olivesPerMCacheReadInputTokens)
  const cacheWriteInputOlives = olivesForTokens(input.cacheWrite, cfg.olivesPerMCacheWriteInputTokens)
  const outputOlives = olivesForTokens(Math.max(0, usage.outputTokens), cfg.olivesPerMOutputTokens)
  const rawOlives = uncachedInputOlives + cacheReadInputOlives + cacheWriteInputOlives + outputOlives
  const chargedOlives =
    rawOlives <= 0 ? 0 : Math.max(CHAT_MIN_CHARGE_OLIVES, Math.ceil(rawOlives * 10 - CHAT_ROUNDING_EPSILON) / 10)
  return {
    uncachedInputOlives,
    cacheReadInputOlives,
    cacheWriteInputOlives,
    outputOlives,
    rawOlives,
    chargedOlives,
  }
}

/**
 * Metered olive cost for one chat turn, rounded UP to 0.1 olive with the
 * platform floor applied. Free models (all-zero rates) cost 0.
 */
export function calculateChatCostOlives(modelId: ChatModelId, usage: ChatTokenUsage): number {
  return calculateChatCostBreakdown(modelId, usage).chargedOlives
}

/**
 * A canvas node referenced in a chat message (dragged or @-mentioned into the
 * composer). Travels as a `data-node-ref` UIMessage part; the backend renders
 * it into a bracketed context block for the model, which resolves content via
 * MCP tools (get_asset, video inspection). Display fields (name, thumbnailUri)
 * are client-supplied UI hints — the backend treats them as untrusted and
 * truncates them.
 */
/**
 * Where an inline chat reference sits in the message's text part. UTF-16
 * offsets (`String.prototype.slice` semantics) into the final outgoing text,
 * covering the `@Name` mention the composer wrote there. Absent on messages
 * sent before inline ranges existed; those render their chips after the text.
 */
export interface ChatNodeRefTextRange {
  start: number
  end: number
}

/** Display metadata every chat reference carries, frozen at send time. */
interface ChatNodeRefBase {
  /** Display name at send time; sanitized and length-capped server-side. */
  name: string
  thumbnailUri?: string
  textRange?: ChatNodeRefTextRange
}

export type ChatNodeRef =
  | (ChatNodeRefBase & {
      kind: 'asset'
      assetId: string
      assetType: 'image' | 'video' | 'audio'
      /** Scrub position (seconds) captured at drag time — video assets only. */
      timestamp?: number
    })
  | (ChatNodeRefBase & { kind: 'element'; elementId: string })
  | (ChatNodeRefBase & { kind: 'variable'; variableId: string })
  | (ChatNodeRefBase & { kind: 'bin'; binId: string })
  | (ChatNodeRefBase & { kind: 'note'; noteId: string })
  /** A canvas Action card — its id is what update_actions / run_workflow take. */
  | (ChatNodeRefBase & { kind: 'action'; actionId: string })
  /** A generation model — referenced for its capabilities; never changes the chat model. */
  | (ChatNodeRefBase & { kind: 'model'; modelId: string })
  | (ChatNodeRefBase & { kind: 'library-collection'; collectionId: string })
  | (ChatNodeRefBase & {
      kind: 'library-asset'
      collectionId: string
      libraryAssetId: string
      assetKind: 'image' | 'video' | 'text'
    })

export type ChatNodeRefKind = ChatNodeRef['kind']

/** The stable id a chat reference points at, whatever its kind. */
export function chatNodeRefId(ref: ChatNodeRef): string {
  switch (ref.kind) {
    case 'asset':
      return ref.assetId
    case 'element':
      return ref.elementId
    case 'variable':
      return ref.variableId
    case 'bin':
      return ref.binId
    case 'note':
      return ref.noteId
    case 'action':
      return ref.actionId
    case 'model':
      return ref.modelId
    case 'library-collection':
      return ref.collectionId
    case 'library-asset':
      return ref.libraryAssetId
  }
}

/** Max length the backend keeps of client-supplied node-ref display strings. */
export const CHAT_NODE_REF_NAME_MAX_LENGTH = 120

// ============================================
// Chat request context bounds
// ============================================

/**
 * Hard cap on one POST /chat body. The backend refuses anything larger before
 * parsing it (see chat/requestLimits.ts), then selects the recent context
 * itself. It sits well above MAX_CHAT_CONTEXT_BYTES because shipped iOS builds
 * (martini-mobile before its client-side selection) send the whole raw thread:
 * at 512 KB, one ~800 KB board-overview turn made every later send in that
 * thread fail with 413.
 */
export const MAX_CHAT_REQUEST_BYTES = 8 * 1024 * 1024
export const MAX_CHAT_CONTEXT_MESSAGES = 64
export const MAX_CHAT_CONTEXT_PARTS = 256
export const MAX_CHAT_CONTEXT_TEXT_CHARS = 64_000
/**
 * Serialized-message budget for one provider request. Clients select to it
 * before sending and the backend re-selects whatever arrives.
 *
 * This is the bound the text-character cap cannot express: tool outputs
 * live in tool parts, not text parts, so a thread of tool-heavy turns passes
 * every other bound and still overflows the body cap. Observed live as a
 * thread whose every send failed with 413 once its history reached ~530 KB.
 */
export const MAX_CHAT_CONTEXT_BYTES = 480 * 1024

interface ChatMessageMeasure {
  parts: number
  textChars: number
  bytes: number
}

const chatMessageEncoder = new TextEncoder()

/** Cheap structural measure of an unvalidated message-shaped value. */
export function measureChatMessage(message: unknown): ChatMessageMeasure {
  const bytes = chatMessageEncoder.encode(JSON.stringify(message) ?? '').length
  const parts = typeof message === 'object' && message !== null ? (message as { parts?: unknown }).parts : undefined
  if (!Array.isArray(parts)) return { parts: 0, textChars: 0, bytes }

  let textChars = 0
  for (const part of parts) {
    const text = typeof part === 'object' && part !== null ? (part as { text?: unknown }).text : undefined
    if (typeof text === 'string') textChars += text.length
  }
  return { parts: parts.length, textChars, bytes }
}

/**
 * Select the newest bounded context for one provider request.
 *
 * The full UI thread stays loaded and durably persisted; only the model input
 * rolls over. The client applies this before sending and the backend applies
 * it again to whatever arrives, so both sides must share this one
 * implementation and its constants: a bound the client does not mirror is a
 * thread that dies on every send (retry re-sends the same history).
 *
 * The newest message is always retained, even when it alone exceeds a bound,
 * so validation can reject that single turn with actionable copy instead of
 * silently sending an empty prompt.
 */
export function selectRecentChatMessages<T>(messages: ReadonlyArray<T>): T[] {
  if (messages.length === 0) return []

  let start = messages.length
  let totalParts = 0
  let totalText = 0
  let totalBytes = 0
  let selected = 0

  for (let index = messages.length - 1; index >= 0 && selected < MAX_CHAT_CONTEXT_MESSAGES; index -= 1) {
    const measure = measureChatMessage(messages[index])
    const wouldOverflow =
      totalParts + measure.parts > MAX_CHAT_CONTEXT_PARTS ||
      totalText + measure.textChars > MAX_CHAT_CONTEXT_TEXT_CHARS ||
      totalBytes + measure.bytes > MAX_CHAT_CONTEXT_BYTES
    if (selected > 0 && wouldOverflow) break
    start = index
    selected += 1
    totalParts += measure.parts
    totalText += measure.textChars
    totalBytes += measure.bytes
  }

  const bounded = messages.slice(start)
  const firstUserIndex = bounded.findIndex(
    message => typeof message === 'object' && message !== null && (message as { role?: unknown }).role === 'user',
  )
  return firstUserIndex > 0 ? bounded.slice(firstUserIndex) : bounded
}

// ============================================
// V7 Set Pipeline Types
// ============================================

/** Character type — determines which FAL endpoint to use for 3D mesh reconstruction. */
export type CharacterType = 'person' | 'creature' | 'object'

/** Visual style detected from a set source image or selected for V10 render output. */
export type VisualStyle = 'photorealistic' | 'anime' | '3d_animation' | 'illustration' | 'painting' | 'sketch'

/** Ordered visual style values for validation and UI controls. */
export const VISUAL_STYLE_VALUES = [
  'photorealistic',
  'anime',
  '3d_animation',
  'illustration',
  'painting',
  'sketch',
] as const satisfies readonly VisualStyle[]

/** Labels for render style selectors. */
export const VISUAL_STYLE_OPTIONS: Array<{ value: VisualStyle; label: string }> = [
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'anime', label: 'Anime' },
  { value: '3d_animation', label: '3D Animation' },
  { value: 'illustration', label: 'Illustration' },
  { value: 'painting', label: 'Painting' },
  { value: 'sketch', label: 'Sketch' },
]

/** How the set's 3D environment is sourced: single image, multiple images, or video. */
export type SetSourceType = 'image' | 'multi-image' | 'video'

/** Character color slot — maps to maximally distinct colors for SAM segmentation. */
export type CharacterSlotColor = 'red' | 'green' | 'blue' | 'yellow' | 'magenta'

/** Hex colors for rendering character meshes in the 3D viewer. */
export const CHARACTER_SLOT_COLORS: Record<CharacterSlotColor, string> = {
  red: '#FF0000',
  green: '#00FF00',
  blue: '#0000FF',
  yellow: '#FFFF00',
  magenta: '#FF00FF',
}

/** 3D vector for position, rotation, scale. */
export interface Vec3 {
  x: number
  y: number
  z: number
}

/** Normalized bounding box (0-1 range relative to image dimensions). */
export interface NormalizedBbox {
  x: number
  y: number
  w: number
  h: number
}

/** Character entity — extracted person from source image. */
export interface Character {
  id: string
  setId: string
  projectId: string
  slotIndex: number
  slotColor: CharacterSlotColor
  label: string
  sourceBbox: NormalizedBbox | null
  cutoutUrl: string | null
  characterSheetUrl: string | null
  headshotUrl: string | null
  meshUrl: string | null
  meshHeight: number | null
  characterType: CharacterType
  initialPosition: Vec3 | null
  initialRotation: Vec3 | null
  initialScale: Vec3 | null
  createdAt: string
  updatedAt: string
}

/** V7 set creation mode. */
export type SetCreationMode = 'fast' | 'quality'

/** V7 set creation pipeline phases (state machine). */
export type SetCreationPhase =
  'uploading' | 'segmenting' | 'processing_characters' | 'generating_environment' | 'complete' | 'failed'

/** V7 enhancement pipeline phases. */
export type EnhancementPhase =
  'pending' | 'estimating_divergence' | 'cleaning_world' | 'replacing_characters' | 'completed' | 'failed'

/** Character replacement progress within enhancement. */
export interface CharacterReplacementProgress {
  current: number
  total: number
  currentLabel: string
}

/** Character placement data sent with screenshot capture. */
export interface CharacterPlacement {
  characterId: string
  position: Vec3
  rotation: Vec3
  scale: Vec3
  screenBbox: NormalizedBbox
}

// =============================================================================
// Billing analytics
// =============================================================================

/** Pivot dimension for the team billing dashboard. */
export type SpendGroupBy =
  'project' | 'actor' | 'model' | 'actor_project' | 'actor_model' | 'user' | 'user_project' | 'user_model'

export type SpendActorType = 'user' | 'api_key' | 'unknown'

/** Actor option returned independently of the active dashboard pivot. */
export interface SpendActorFacetEntry {
  key: string
  label: string
  actorType: SpendActorType
  color?: string
}

/** Measure selected for chart/table emphasis. */
export type SpendMeasure = 'martini_olives' | 'byok_fal_usd' | 'generations'

/** Coarseness of the time axis in the analytics chart. */
export type SpendBucket = 'day' | 'week'

/** Source quality for BYOK FAL USD row costs. */
export interface ByokFalUsdSourceBreakdown {
  estimated: number
  reconciled: number
  missing: number
}

/** Single time bucket: total olives charged per pivot key in that bucket. */
export interface SpendTimeBucket {
  ts: string
  byKey: Record<string, number>
  byokFalUsdByKey: Record<string, number>
  countByKey: Record<string, number>
}

/** A single row in the analytics breakdown (one per pivot key). */
export interface SpendBreakdownEntry {
  key: string
  label: string
  totalOlives: number
  totalByokFalUsd: number
  txCount: number
  prevTotalOlives: number
  prevTotalByokFalUsd: number
  prevTxCount: number
  byokFalUsdSourceBreakdown: ByokFalUsdSourceBreakdown
  meta: {
    color?: string
    avatar?: string
    actorType?: SpendActorType
    actorId?: string
    actorLabel?: string
    apiKeyId?: string | null
    apiKeyLabel?: string | null
    auditUserId?: string | null
    auditUserAlias?: string | null
    projectId?: string
    projectName?: string
    userId?: string
    userAlias?: string
    model?: string
    modelLabel?: string
  }
  sparkline: number[]
  byokFalUsdSparkline: number[]
  countSparkline: number[]
}

/** Quick-glance KPI summary alongside the analytics payload. */
export interface SpendKpiHighlight {
  actorType?: SpendActorType
  actorId?: string
  actorLabel?: string
  apiKeyId?: string | null
  apiKeyLabel?: string | null
  auditUserId?: string | null
  auditUserAlias?: string | null
  userId?: string
  alias?: string
  color?: string
  model?: string
  modelLabel?: string
  totalOlives: number
  totalByokFalUsd: number
  txCount: number
}

export interface SpendAnalyticsResponse {
  /** @deprecated Use totalOlives. */
  total: number
  /** @deprecated Use prevTotalOlives. */
  prevTotal: number
  totalOlives: number
  prevTotalOlives: number
  totalByokFalUsd: number
  prevTotalByokFalUsd: number
  byokFalUsdSourceBreakdown: ByokFalUsdSourceBreakdown
  generationCount: number
  prevGenerationCount: number
  buckets: SpendTimeBucket[]
  bucket: SpendBucket
  actorFacet: SpendActorFacetEntry[]
  breakdown: SpendBreakdownEntry[]
  topUser: SpendKpiHighlight | null
  topModel: SpendKpiHighlight | null
  topUserByokFalUsd: SpendKpiHighlight | null
  topModelByokFalUsd: SpendKpiHighlight | null
  topUserByGenerationCount: SpendKpiHighlight | null
  topModelByGenerationCount: SpendKpiHighlight | null
  truncated: boolean
}

/**
 * Per-member spend cap row used by the team billing controls.
 * `monthSpendOlives` is live spend in the current calendar month (UTC).
 */
export interface MemberSpendCap {
  userId: string
  alias: string
  color: string
  email: string | null
  role: 'admin' | 'member'
  spendCapOlives: number | null
  spendAlertThresholdOlives: number | null
  monthSpendOlives: number
}

export interface MemberSpendCapsResponse {
  members: MemberSpendCap[]
  monthLabel: string
}

/**
 * Classified kind of AI agent talking to our MCP server.
 *
 * Detected from the HTTP user-agent (and, during MCP initialize, the
 * clientInfo.name field). Only kinds we have a brand asset for are rendered
 * in the collaborator avatar strip — others fall through to 'unknown' and
 * are ignored by the UI but still logged for analytics.
 */
export type McpAgentKind =
  | 'claude-code'
  | 'claude-desktop'
  | 'cursor'
  | 'windsurf'
  | 'cline'
  | 'continue'
  | 'zed'
  | 'gemini-cli'
  | 'codex'
  | 'chatgpt'
  | 'martini-chat'
  /** The first-party `martini` CLI (`X-Martini-Client: martini-cli/<version>`), on any host agent. */
  | 'martini-cli'
  /** Martini's own workflow agent, calling the registry in-process or from its sandbox. */
  | 'martini-workflow'
  /** Next Securities' orchestrator (`ncp-agent-worker/<version> (+nx-securities)`), an enterprise API/MCP client. */
  | 'ncp-agent-worker'
  | 'unknown'

/**
 * Ephemeral presence entry for an AI agent actively using the MCP on a
 * project. Lives in a backend in-memory registry with a sliding TTL and is
 * broadcast to the project's WebSocket room so the collaborator strip can
 * render a logo avatar alongside human peers.
 */
export interface McpAgentPresence {
  agentKind: McpAgentKind
  userId: string
  lastActiveAt: number
  expiresAt: number
}

/** WebSocket broadcast: current set of active MCP agents on a project. */
export interface McpAgentPresenceEvent {
  type: 'mcp_agent_presence'
  projectId: string
  agents: McpAgentPresence[]
}

// =============================================================================
// Subject Collections
// =============================================================================

export interface SubjectCollectionMember {
  subjectId: string
}

/**
 * First-class canvas object that groups existing SubjectCanvasNode records as
 * variants. Placement is stored separately in canvasPositions[`collection-${id}`].
 */
export interface SubjectCollection {
  id: string
  name: string
  /**
   * Optional MVP home scene used by the single-placement canvas UI.
   * Collection identity itself is project-scoped; placement lives in
   * canvasPositions[`collection-${id}`].
   */
  homeSceneId?: string
  createdAt: string
  createdBy: string
  /** Ordered subject members. Each referenced subject stores collectionId === id. */
  memberOrder: SubjectCollectionMember[]
}

// =============================================================================
// V10: Subject Poses
// =============================================================================

/**
 * A single 3D pose owned by a SubjectCanvasNode.
 *
 * Each pose bundles a mesh, character sheet, extracted panels, and cutout —
 * everything needed to place the subject in a V10 set and render it through
 * the V10 shot pipeline.  A subject can have multiple poses (e.g. from
 * different set pipelines or future manual creation).
 *
 * Stored inline on `SubjectCanvasNode.poses[]` in Yjs.  Only URL strings are
 * stored — binary assets live in R2.
 */
export interface SubjectPose {
  id: string
  name: string
  meshUrl: string
  meshHeight?: number
  characterType?: CharacterType
  cutoutUrl?: string
  cutoutThumbnailUrl?: string
  headshotUrl?: string
  headshotThumbnailUrl?: string
  /**
   * Set-level display image/thumbnail (a generated default used as the main when no
   * headshot exists). Persisted so the render/panel "is this a user swap?" detector
   * (`pickCustomMainImage`) recognises it as a generated default — matching the capture-time
   * `isGeneratedCharacterImageUrl` check, which already treats these as generated.
   */
  displayImageUrl?: string
  displayThumbnailUrl?: string
  characterSheetUrl?: string
  frontalPanelUrl?: string
  referencePanelUrls?: string[]
  portraitPanelUrl?: string
  /** Pre-rendered thumbnail of the 3D mesh (stored in R2). */
  meshVisualizationUrl?: string
  /** Set ID whose pipeline created this pose (for dedup on re-creation). */
  sourceSetId?: string
  createdAt: string
}

// =============================================================================
// SubjectCanvasNode (canonical — synced into frontend/src/types/shared.ts)
// =============================================================================

export type SubjectCategory = 'character' | 'animal' | 'prop' | 'costume' | 'scene' | 'effect' | 'other'

/** Structural mirror of the image-pipeline schema; kept dependency-free for shared-type sync. */
export interface ImageAdjustments {
  exposure: number
  temp: number
  tint: number
  highlights: number
  shadows: number
  whites: number
  blacks: number
  contrast: number
  saturation: number
  vibrance: number
  vignette: number
  /** Vignette shape (Lightroom post-crop vignette): midpoint and feather 0..100, roundness and offsets −100..100. */
  vignetteMidpoint: number
  vignetteFeather: number
  vignetteRoundness: number
  vignetteOffsetX: number
  vignetteOffsetY: number
  fade: number
  grain: number
  grainSeed: number
}

export interface ImageEdit {
  angle?: number
  crop?: SubjectCropRect
  adjustments?: ImageAdjustments
}

export interface EditRevision {
  revisionId: string
  edit: ImageEdit
  pipelineVersion: number
  uri: string
  createdAt: string
  /** Migrated crop revisions record the parent's source at promotion, not its historical file. */
  source: { uri: string; width: number; height: number }
}

/** Crop rectangle in natural (full-resolution) image pixels, stable across display scaling. */
export interface SubjectCropRect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Canonical SubjectCanvasNode shape — the Yjs record for a reusable visual
 * subject (character, animal, prop, costume, scene, effect, other).
 *
 * Both backend (board mutations, MCP, generation) and frontend (canvas,
 * inspector, store) consume this type. Keep field additions here; do not
 * re-declare in `backend/src/board/index.ts` or `frontend/src/types/index.ts`.
 *
 * When `category === 'character'`, the character-specific fields are
 * populated by the set pipeline (V10+) or by auto-generation. They are
 * internal — used under the hood for generation quality.
 */
export interface SubjectCanvasNode {
  id: string
  sceneId: string
  binId?: string
  /**
   * When set, the subject is a member of the named variation stack and is
   * hidden when the stack is collapsed. Mirrors `Asset.stackId` so stacks
   * can hold both assets and subjects with a uniform back-reference shape.
   * Mutually exclusive with `collectionId`.
   */
  stackId?: string
  /**
   * When set, this subject is a variant inside a SubjectCollection.
   * Mutually exclusive with `stackId`; collection membership is mirrored
   * by `subjectCollections[collectionId].memberOrder`.
   */
  collectionId?: string
  name: string
  shotNumber?: number
  description?: string
  category: SubjectCategory

  mainImageUrl?: string
  /** Canvas asset ID of the source image (for click-to-navigate). Null when uploaded directly. */
  mainImageAssetId?: string | null
  /** Pre-crop original — set when a new image is uploaded/dropped. Crop edits only update mainImageUrl. */
  originalMainImageUrl?: string
  /** Crop rect applied to originalMainImageUrl, in natural image pixels. Set when user crops; cleared on new upload. */
  mainImageCropRect?: SubjectCropRect
  referenceImageUrls?: string[]
  /** Canvas asset IDs parallel to referenceImageUrls (for click-to-navigate). Null entries = uploaded directly. */
  referenceImageAssetIds?: (string | null)[]
  /** Pre-crop originals — parallel to referenceImageUrls. Crop edits only update referenceImageUrls. */
  originalReferenceImageUrls?: string[]

  voiceId?: string
  voiceProvider?: string
  voiceSourceUrl?: string
  voiceLabel?: string

  thumbnailUrl?: string
  color?: string
  rating?: ShotRating
  starRating?: StarRating
  colorLabel?: ShotColorLabel

  createdAt: string
  createdBy: string
  /**
   * Variant property labels for collection variants (e.g. `{ pose: 'angry' }`).
   * Free-form for MVP; later phases may add property definitions.
   */
  variantProperties?: Record<string, string>

  // ---------------------------------------------------------------------------
  // Character-specific fields (populated when category === 'character')
  // ---------------------------------------------------------------------------

  /** NanoBanana 14-panel character sheet (hidden — used for panel extraction). */
  characterSheetUrl?: string
  /** Extracted frontal full-body panel from character sheet (hidden — best image for generation). */
  frontalPanelUrl?: string
  /** Extracted side/back panels from character sheet (hidden — generation angle refs). */
  referencePanelUrls?: string[]
  /** Extracted frontal portrait panel from character sheet (hidden). */
  portraitPanelUrl?: string

  /** SAM3D .glb mesh URL (hidden — used in 3D set viewer). */
  meshUrl?: string
  /** Mesh bounding-box height in meters (hidden — scale computation). */
  meshHeight?: number
  /** Character type — determines mesh reconstruction endpoint (hidden). */
  characterType?: 'person' | 'creature' | 'object'

  /** V10+ poses — each bundles a mesh, sheet, panels, cutout for set placement. */
  poses?: SubjectPose[]
}

/**
 * Lightweight binding between a subject and a V10 set.
 *
 * Stored in the Yjs `setSubjectPlacements` map keyed by `{setId}:{subjectId}`.
 * 3D transforms live separately in the `characterTransforms` map (composite
 * key `{setId}:{subjectId}`).  Mesh/panel URLs are resolved at read time by
 * looking up the subject's `poses` array via `poseId`.
 */
export interface SetSubjectPlacement {
  subjectId: string
  poseId: string
  name: string
  color: string
  slotIndex: number
  /** Initial 3D transform captured at set creation; characterTransforms overrides it after user edits. */
  initialPosition?: Vec3 | null
  initialRotation?: Vec3 | null
  initialScale?: Vec3 | null
  /** Tombstone used to remember that this subject was removed from the set. */
  removedAt?: string
}

/**
 * One mutation event from an MCP tool call. Broadcast over WebSocket to
 * the project room so the frontend activity log can render a receipt.
 *
 * Ephemeral by design — server holds no history; the log is whatever the
 * client buffered during this session. Read-only tools (`get_*`,
 * `list_*`) do not emit activity.
 */
export interface McpActivityEvent {
  type: 'mcp_activity'
  projectId: string
  /** Unique ID per tool invocation, lets the frontend update an entry in place. */
  toolCallId: string
  toolName: string
  agentKind: McpAgentKind
  userId: string
  status: 'completed' | 'failed'
  startedAt: number
  durationMs: number
  /** Scene the action targeted, when known. */
  sceneId?: string
  /** Primary entity acted on (assetId / nodeId / sceneId / subjectId). */
  targetId?: string
  /** All entities acted on by the tool call, when known. */
  targetIds?: string[]
  /**
   * Media kind affected by the call, when known. Lets the activity log
   * say "image" / "video" instead of generic "node". Pulled from the
   * tool result's asset record for create-style tools.
   */
  nodeKind?: 'image' | 'video' | 'set' | 'audio'
  /** Short human-readable description, e.g. "created node in Scene 2". */
  summary?: string
  errorMessage?: string
}

/** GET /organizations/:orgId/mcp-usage */
export interface McpUsageResponse {
  used: number
  limit: number
  /** Start of the current calendar month in UTC. */
  periodStart: string
}

// ============================================
// Actions (agent-run canvas cards; "workflow" names the chain/run)
// ============================================

export type ActionInputRef =
  | { type: 'bin'; id: string; label?: string }
  | { type: 'asset'; id: string; label?: string }
  | { type: 'note'; id: string; label?: string }
  | { type: 'variable'; id: string; label?: string }
  | { type: 'model'; id: string; label?: string }
  /**
   * A shared Instructions file (`project_files` path as the id): the method
   * several Actions follow, injected whole into the executor's prompt after
   * the brief. Not an input — it never appears in the resolved input context,
   * draws no edge, and the fingerprint hashes its path, not its content.
   */
  | { type: 'instructions'; id: string; label?: string }

export interface ActionPinnedSettings {
  modelId?: string
  duration?: number
  aspectRatio?: string
}

/**
 * A collaborative Action stored in the project's `actions` Yjs map.
 *
 * The brief (`instruction`) is the wiring: input and output bins are derived
 * from its @-refs, never configured separately. Cardinality (how many times an
 * Action runs) is decided by the plan pass reading the brief against current
 * bin contents — there is no per-input iterate/context configuration.
 */
export interface Action {
  id: string
  sceneId: string
  name: string
  instruction: string
  agentId: string
  /** Explicit execution order. Canvas x-position is presentation, not control flow. */
  order: number
  pinned?: ActionPinnedSettings
  inputRefs: ActionInputRef[]
  /**
   * Bins this Action may write into, derived from the brief's @-refs. An asset
   * belongs to exactly one bin, so several destinations mean *routing* — the
   * brief decides which takes land where ("keepers to Selects, the rest to
   * Retakes") — never copying one take into all of them.
   */
  outputBinIds?: string[]
  gate: 'auto' | 'approval'
  /**
   * The workflow this Action belongs to (a `workflows` map record). Actions
   * without one are grouped on the canvas by their wiring instead, and get a
   * record the first time someone names the group.
   */
  workflowId?: string
  /**
   * The project-file path of this Action's own Instructions (the how: steps,
   * commands, settings, workarounds with their reasons), injected whole after
   * the brief on every unit and rewritten by the harness after runs that
   * learned something. Set when the Action is authored (default from
   * `ownInstructionsPathFor`, made unique on the canvas) or when a person first
   * writes the file; never derived from names afterwards, so a rename moves
   * nothing, and carried verbatim through placement so every copy of a saved
   * workflow shares one file. Absent on Actions authored before this existed:
   * they have no own Instructions until someone writes the file (ADR-0008).
   */
  instructionsPath?: string
  /**
   * Wiring this Action lost because what it pointed at was deleted. Absent
   * when nothing is lost. See `ActionLostWiring`.
   */
  lostWiring?: ActionLostWiring[]
  createdAt: string
  updatedAt: string
}

/**
 * One piece of wiring an Action lost to a delete. The board still drops the
 * dead ref (an Action never points at a ghost) but records the loss here, so
 * it stays visible: the card says what went, get_canvas_children and
 * update_actions return it, and a run that finds its bin gone can say why.
 * Undoing the delete removes the record with the rest of the transaction. An
 * explicit rewire settles it (`settleActionLostWiring`). Never saved into a
 * workflow or carried to a placed copy.
 */
export interface ActionLostWiring {
  type: 'bin' | 'variable' | 'note'
  id: string
  /** The name when it was deleted: the record is gone, so this is the only label left. */
  name: string
  role: 'input' | 'output'
  /** When the delete happened (ISO). */
  at: string
}

/**
 * A named workflow on a canvas — the container the frame is drawn around.
 * Stored in the project's `workflows` Yjs map. Membership lives on the
 * Actions (`Action.workflowId`); the frame's bounds are derived from the
 * member cards and the bins they are wired to, never stored.
 */
export interface WorkflowKitCandidateIdentity {
  runId?: string
  candidateId?: string
  snapshotId: string
}

export interface ExternalKitCandidateProvenance {
  source: 'external'
  authorUserId: string
  idempotencyKey: string
  requestHash: string
  submissionId: string
  baseline: Pick<WorkflowToolBundle, 'id' | 'sha256' | 'snapshot'> | null
  reviewedWorkflowFingerprint: string
  reviewedFingerprints: Record<string, ActionFingerprint>
}

export interface WorkflowKitCandidate extends WorkflowKitCandidateIdentity {
  /** Explicit verification of this proposed fix; absent means not verified. */
  verification?: { passed: boolean }
  external?: ExternalKitCandidateProvenance
  at: string
  files: Array<{
    path: string
    change: 'added' | 'changed' | 'deleted'
    bytes: number
    beforeExecutable?: boolean
    afterExecutable?: boolean
  }>
  summary: string
}

export interface Workflow {
  /** Serializes saves of this placement until release or expiry. */
  saveLease?: { token: string; expiresAt: number }
  /** Publication authority, checked atomically with candidate mutations by the board. */
  candidateLeases?: Record<string, { token: string; expiresAt: number }>
  candidates?: WorkflowKitCandidate[]
  /** Opaque candidate-collection revision prevents compare-and-set ABA races. */
  candidateRevision?: string
  id: string
  sceneId: string
  name: string
  /**
   * The saved workflow this placed workflow was placed from or last saved as.
   * The storage key predates the workflows/runs vocabulary (renaming a live
   * Yjs key needs a read-both shim); the `workflows` row mirrors it as
   * `sourceWorkflowId`.
   */
  definitionId?: string
  /** Immutable tools inherited from a saved/copy/exported workflow. Runs keep their own pinned reference. */
  toolBundle?: WorkflowToolBundle
  /**
   * What a run does when the compiled kit is stale or missing: recompile
   * first (`auto`), or refuse and let the person decide (`manual`). Absent on
   * records written before the field — read as `auto`.
   */
  kitPolicy?: WorkflowKitPolicy
  /** Saved/imported copies may prepare a missing kit once; any existing kit still follows kitPolicy. */
  allowInitialKitSetup?: boolean
  createdAt: string
  updatedAt: string
}

/** Run-start policy for a stale or missing kit, stored on the placed workflow. */
export type WorkflowKitPolicy = 'auto' | 'manual'

/**
 * Optional UI barriers checked before the run door writes anything.
 *
 * The canvas UI sends the Actions it holds (`expectedActions`) and the backend
 * hashes both sides with one build of `workflowFingerprintSource` — a browser
 * never computes the fingerprint itself. A tab left open across a deploy runs
 * an older bundle, and an older hashing scheme against the current backend
 * refused every run as "still syncing" (2026-09-11). `expectedActionsFingerprint`
 * is the pre-2026-09-11 door: still accepted so tabs built before the switch
 * are no worse off; remove once the stale-build prompt has been live a while.
 */
export interface WorkflowRunEditBarriers {
  expectedActions?: Action[]
  expectedActionsFingerprint?: string
  expectedKitPolicy?: WorkflowKitPolicy
}

/**
 * Why a barrier refused: which Actions differ between what the client sent and
 * what the canvas read holds, by field name only — never content. Field names
 * are the keys of `actionMachinery`. Rides on the 409 so the client can attach
 * it to its report when the retries give up; the backend itself stays quiet,
 * because every genuinely syncing click produces a few of these on the way in.
 */
export interface WorkflowActionsMismatch {
  changed: Array<{ id: string; fields: string[] }>
  missingOnCanvas: string[]
  extraOnCanvas: string[]
}

/** Bins an Action reads, in tag order — the normalized wiring (brief tags merged with stored refs), like the run resolves it. */
export function actionInputBinIds(action: Pick<Action, 'instruction' | 'inputRefs'>): string[] {
  return normalizeActionInputRefs(action)
    .filter(ref => ref.type === 'bin')
    .map(ref => ref.id)
}

/** Where an Action's takes may land, deduped. Empty means "leave them on the canvas". */
export function resolveActionOutputBinIds(action: Pick<Action, 'outputBinIds'>): string[] {
  return [...new Set(action.outputBinIds?.filter(id => id.length > 0) ?? [])]
}

/**
 * Bins that are both read and written by the same Action.
 *
 * MCP uses these overlaps for warnings and the UI for hints; reading and
 * writing the same bin is allowed for generate-then-review Actions.
 */
export function actionBinConflicts(action: Pick<Action, 'instruction' | 'inputRefs' | 'outputBinIds'>): string[] {
  const inputs = new Set(actionInputBinIds(action))
  return resolveActionOutputBinIds(action).filter(id => inputs.has(id))
}

/** A board that keeps churning must not grow an Action record without bound. */
const ACTION_LOST_WIRING_LIMIT = 20

const lostWiringKey = (entry: Pick<ActionLostWiring, 'type' | 'id' | 'role'>) =>
  `${entry.type}:${entry.id}:${entry.role}`

/** Add newly lost wiring to an Action's record; an entry already recorded keeps its first time. */
export function recordActionLostWiring(
  existing: readonly ActionLostWiring[] | undefined,
  lost: readonly ActionLostWiring[],
): ActionLostWiring[] {
  const merged = [...(existing ?? [])]
  const seen = new Set(merged.map(lostWiringKey))
  for (const entry of lost) {
    if (seen.has(lostWiringKey(entry))) continue
    seen.add(lostWiringKey(entry))
    merged.push(entry)
  }
  return merged.slice(-ACTION_LOST_WIRING_LIMIT)
}

/** The bins, variables and notes an Action reads, as `type:id` keys. */
function actionWiredInputKeys(action: Pick<Action, 'instruction' | 'inputRefs'>): Set<string> {
  return new Set(
    normalizeActionInputRefs(action)
      .filter(ref => ref.type === 'bin' || ref.type === 'variable' || ref.type === 'note')
      .map(ref => `${ref.type}:${ref.id}`),
  )
}

const sameKeys = (a: ReadonlySet<string>, b: ReadonlySet<string>) =>
  a.size === b.size && [...a].every(key => b.has(key))

/**
 * The lost-wiring record after an edit: changing the output bins settles the
 * lost outputs, changing what the Action reads settles the lost inputs — the
 * person or agent rewired it. Compares the wiring itself, not which fields a
 * write carried, so a rename or an autosave of an unchanged brief settles
 * nothing. Undefined when nothing is left.
 */
export function settleActionLostWiring(
  before: Pick<Action, 'instruction' | 'inputRefs' | 'outputBinIds'>,
  after: Pick<Action, 'instruction' | 'inputRefs' | 'outputBinIds' | 'lostWiring'>,
): ActionLostWiring[] | undefined {
  if (!after.lostWiring?.length) return undefined
  const outputsChanged = !sameKeys(
    new Set(resolveActionOutputBinIds(before)),
    new Set(resolveActionOutputBinIds(after)),
  )
  const inputsChanged = !sameKeys(actionWiredInputKeys(before), actionWiredInputKeys(after))
  const rest = after.lostWiring.filter(entry => (entry.role === 'output' ? !outputsChanged : !inputsChanged))
  return rest.length > 0 ? rest : undefined
}

/** The Action with its lost-wiring record replaced; an empty record removes the key. */
export function withActionLostWiring<T extends Pick<Action, 'lostWiring'>>(
  action: T,
  lostWiring: readonly ActionLostWiring[] | undefined,
): T {
  const { lostWiring: _previous, ...rest } = action
  return (lostWiring?.length ? { ...rest, lostWiring: [...lostWiring] } : rest) as T
}

/**
 * `@[Kind:Label](id)` — the tag grammar Actions use inside briefs. Kept
 * byte-identical with backend/src/workflows/tags.ts and
 * frontend/src/lib/workflowTags.ts: the brief is the wiring, so every side
 * must parse the same tokens.
 */
export const ACTION_TAG_PATTERN_SOURCE = String.raw`@\[(Asset|Bin|Model|Variable|Note|Instructions):([^\]]+)\]\(([^)]+)\)`

/** The refs a brief tags, in brief order, one per (type, id), with their labels. */
export function actionBriefTagRefs(instruction: string): ActionInputRef[] {
  const refs: ActionInputRef[] = []
  const seen = new Set<string>()
  for (const match of instruction.matchAll(new RegExp(ACTION_TAG_PATTERN_SOURCE, 'g'))) {
    const type = match[1].toLowerCase() as ActionInputRef['type']
    const id = match[3]
    const key = `${type}:${id}`
    if (seen.has(key)) continue
    seen.add(key)
    refs.push({ type, id, label: match[2] })
  }
  return refs
}

/**
 * The refs an Action actually reads: the brief's tags merged with the stored
 * refs, one per (type, id), the tag's label winning over a stored one. The
 * brief is the wiring, so a tag the stored refs never carried (observed live
 * 2026-09-08: briefs tagging a script variable and source assets over
 * `inputRefs: []`) is still an input; a stored ref the brief does not tag (an
 * MCP caller passing refs without tags) stays one. Fingerprints, run
 * snapshots, pricing and input resolution all read this one definition, so
 * they can never disagree about what an Action is wired to.
 */
export function normalizeActionInputRefs(action: Pick<Action, 'instruction' | 'inputRefs'>): ActionInputRef[] {
  // A record authored before the brief was required, or a partial object, has no brief to parse.
  return dedupeActionInputRefs([...actionBriefTagRefs(action.instruction ?? ''), ...(action.inputRefs ?? [])])
}

/** One ref per (type, id) — the same tag twice in a brief is still one connection. */
export function dedupeActionInputRefs(refs: readonly ActionInputRef[]): ActionInputRef[] {
  const seen = new Set<string>()
  const result: ActionInputRef[] = []
  for (const ref of refs) {
    const key = `${ref.type}:${ref.id}`
    if (seen.has(key)) continue
    seen.add(key)
    result.push(ref)
  }
  return result
}

/**
 * The refs that draw as edges and ports: every input except models and
 * Instructions (which connect nothing), deduped. Bins the Action also writes
 * are excluded because a self-loop is not a dependency edge; their contents
 * are still read as inputs.
 */
export function actionEdgeRefs(action: Pick<Action, 'instruction' | 'inputRefs' | 'outputBinIds'>): ActionInputRef[] {
  const outputs = new Set(resolveActionOutputBinIds(action))
  return normalizeActionInputRefs(action).filter(
    ref => ref.type !== 'model' && ref.type !== 'instructions' && !(ref.type === 'bin' && outputs.has(ref.id)),
  )
}

/** Shared Instructions files an Action's brief tags, in tag order (the paths, deduped). */
export function actionInstructionsRefs(action: Pick<Action, 'inputRefs'>): ActionInputRef[] {
  return dedupeActionInputRefs(action.inputRefs).filter(ref => ref.type === 'instructions')
}

/** Immutable JSON bytes. Only this descriptor travels through collaboration and run records. */
export interface JsonFileSnapshot {
  id: string
  storageAssetId: string
  filename: string
  uri: string
  checksum: string
  sizeBytes: number
}

/** A constraint on an existing brief-tagged input bin. Defaults are explicit immutable revisions. */
export interface FileInputContract {
  key: string
  format: 'json'
  cardinality: 'one' | 'many'
  defaultFiles?: JsonFileSnapshot[]
}

export type WorkflowFileSource = { json: unknown; filename?: string } | { fileId: string }
export type WorkflowFileInputs = Record<string, WorkflowFileSource | WorkflowFileSource[]>

/** A wired bin or a descendant of an explicitly included preset input. */
export interface WorkflowDefinitionBin {
  collapsed?: boolean
  includeContents?: boolean
  parentSourceId?: string
  childOrder?: string[]
  fileInput?: FileInputContract
  sourceId: string
  name: string
  layoutMode?: 'freeform' | 'grid' | 'row' | 'columns'
  layoutWidth?: number
  gridColumns?: number
}

/** Common wired-bin snapshot for the run door and the live kit status key. */
export function snapshotKitBin(bin: Omit<WorkflowDefinitionBin, 'sourceId'> & { id: string }): WorkflowDefinitionBin {
  return {
    sourceId: bin.id,
    name: bin.name,
    ...(bin.collapsed !== undefined ? { collapsed: bin.collapsed } : {}),
    ...(bin.fileInput ? { fileInput: bin.fileInput } : {}),
    ...(bin.layoutMode ? { layoutMode: bin.layoutMode } : {}),
    ...(bin.gridColumns ? { gridColumns: bin.gridColumns } : {}),
    ...(bin.layoutWidth ? { layoutWidth: bin.layoutWidth } : {}),
  }
}

/** A note a brief references directly; instantiating writes a placeholder with this text. */
export interface WorkflowDefinitionNote {
  /** Source id of a bin carried by this definition. */
  binId?: string
  sourceId: string
  text: string
}

/** A variable a brief references directly; instantiating writes one with this name and value. */
export interface WorkflowDefinitionVariable {
  /** Source id of a bin carried by this definition. */
  binId?: string
  sourceId: string
  name: string
  value: string
  color?: string
}

/**
 * An Instructions file the workflow's Actions follow: the Action's own file
 * (found by convention at `ownInstructionsPathFor`, machine-maintained) or a
 * shared file the brief tags `@[Instructions:Label](path)`. Paths are stable
 * across instantiation (they derive from names, never from ids), so the same
 * file is found in every copy. Instantiation writes the file only where none
 * exists; an existing file always wins. On a run, `definition_snapshot.files`
 * holds the content the units ran with — read from the project at run start,
 * never copied from a saved definition.
 */
export interface WorkflowDefinitionFile {
  path: string
  content: string
  /** No writer at all until a human unlocks it in the inspector. */
  locked?: boolean
}

/**
 * The carried asset record: the source `BoardAssetRecord` minus its id, lineage
 * (parent/stack/version group), placement (scene/bin/shot number), and
 * source-project pointers. Media URLs point at the same immutable R2 objects.
 */
export interface WorkflowDefinitionAssetRecord {
  uri: string
  prompt: string
  duration: number
  mimeType: string
  checksum: string
  status: 'completed'
  proxyUri?: string
  thumbnailUri?: string
  assetType?: string
  aspectRatio?: string
  width?: number
  height?: number
  modelId?: string
  [key: string]: unknown
}

/**
 * An asset a brief references directly (a pinned character sheet, a style
 * reference); instantiating writes a new asset record on the same R2 objects.
 */
export interface WorkflowDefinitionAsset {
  sourceId: string
  label: string
  record: WorkflowDefinitionAssetRecord
  displayName?: string
}

/** Component hashes of an Action's compiled machinery. */
export interface ActionFingerprint {
  brief: string
  instructions: string
  wiring: string
  pinned: string
  action: string
}

/** Parsed tools/kit.json, with schema defaults applied. */
export interface Kit {
  version: 1
  /** Runtime semantics, distinct from the file format. Missing means legacy-unverified. */
  runtimeContract?: 'martini-kit-v1'
  driver: 'agent' | 'engine'
  /**
   * Omitted means serial; only agent-driven execution can opt in. `streaming-inputs` is env-gated
   * (`WORKFLOW_STREAMING_INPUTS`, docs/workflows/operational-toggles.md): off, it runs as settled-inputs.
   */
  concurrency?: 'serial' | 'settled-inputs' | 'streaming-inputs'
  /**
   * Opts the kit in to named generate families (`generate.<name>.json`) and submit-only applies
   * (`wait: false`, `--no-wait`); both also need `WORKFLOW_GENERATE_FAMILIES=1`. Omitted, a kit that
   * declares `concurrency: 'streaming-inputs'` is opted in and any other is not; `false` opts out.
   */
  generateFamilies?: boolean
  /** The step agent's model; omitted means the engine default (Fable 5.1, standard speed). Fast requires claude-opus-5-5. */
  agent?: { model: 'claude-fable-5-1' | 'claude-opus-5-5'; speed: 'standard' | 'fast' }
  workflowFingerprint?: string
  compiledAt?: string
  compiledByRunId?: string
  entrypoint?: string
  actions: Record<
    string,
    {
      step: string
      fingerprint: ActionFingerprint
      files: string[]
      decisionPoints: Array<{ id: string; when: string; whatTheModelJudges: string }>
      mode: 'script' | 'script+model' | 'judgement'
      /** Kit-owned judge configuration, preserved during placement and publication. */
      judges?: Array<Record<string, unknown>>
      /** Per-step round limits; omitted kinds use the operator default. */
      retakeRounds?: { generate?: number; judge?: number }
    }
  >
  tests?: {
    command: string
    lastRun?: { at: string; passed: number; failed: number }
  }
  status: 'valid' | 'invalid'
  invalidReason?: string
}

export type Freshness = 'fresh' | 'stale' | 'missing'

export interface KitFreshness {
  overall: Freshness
  removed: string[]
  actions: Array<{
    actionId: string
    name: string
    step: string
    freshness: Freshness
    changed: Array<'brief' | 'instructions' | 'wiring' | 'pinned'>
  }>
}

/** The placed workflow's Implementation inspection response. */
export interface KitStatusResponse {
  candidates?: WorkflowKitCandidate[]
  kit: Kit | null
  freshness: KitFreshness
  lastCompileRun: {
    id: string
    status: WorkflowRunStatusValue
    createdAt: string
    completedAt: string | null
    errorMessage: string | null
    runLog?: string | null
    /** Historical canvas log, retained across independent frontend/backend deployments. */
    runLogNoteId?: string | null
  } | null
  kitPolicy: WorkflowKitPolicy
  /** Agent skills the published kit ships (see WorkflowToolBundle.kit.skills). */
  skills?: WorkflowKitSkill[]
}

/** One skill folder inside a kit: its SKILL.md path and the frontmatter that names it. */
export interface WorkflowKitSkill {
  name: string
  description: string
  /** SKILL.md path relative to tools/; the folder around it is the whole skill. */
  path: string
}

/** A portable capability for immutable tool files; bytes and manifest live in R2, never in Yjs. */
export interface WorkflowToolBundle {
  /** Compile metadata carried with the immutable library; absent on older bundles. */
  kit?: {
    sha256: string
    workflowFingerprint?: string
    compiledAt?: string
    compiledByRunId?: string
    status: 'valid' | 'invalid'
    tests?: Kit['tests']
    /** Agent skills the kit ships (every SKILL.md with a valid frontmatter), discovered at publish time. */
    skills?: WorkflowKitSkill[]
  }
  id: string
  sha256: string
  /** Trusted storage URL used when a workflow file crosses Martini environments. */
  manifestUrl?: string
  /** Published Daytona snapshot required by the tools; null for a host-only library. */
  snapshot: string | null
  fileCount: number
  bytes: number
}

/**
 * A workflow as machinery: the Actions with their briefs, the bins they are
 * wired to as empty containers, and the notes, variables, and assets the
 * briefs reference directly (a directly tagged asset is part of the program;
 * it travels as a new record on the same R2 objects). Bin CONTENTS are
 * material and never travel.
 *
 * Every id is in the SOURCE canvas's id space (action ids, `sceneId`,
 * `workflowId`, bin/note/variable/asset ids inside briefs and refs).
 * Instantiating onto a canvas mints fresh ids for all of it and rewrites the
 * briefs. `definition_snapshot` on a run stores the same shape.
 */
export interface WorkflowDefinition {
  name: string
  actions: Action[]
  bins?: WorkflowDefinitionBin[]
  presetBinContents?: WorkflowPackageBinContents[]
  notes?: WorkflowDefinitionNote[]
  variables?: WorkflowDefinitionVariable[]
  assets?: WorkflowDefinitionAsset[]
  toolBundle?: WorkflowToolBundle
  files?: WorkflowDefinitionFile[]
  /** Saved canvas arrangement in source-id space, relative to the workflow’s top-left corner. */
  layout?: WorkflowPackageLayout
  schemaVersion?: 1
}

/**
 * Instructions files are injected whole into every unit's prompt, so they stay
 * a bounded slice of it: per file, and per Action (own plus tagged files).
 * Measured in UTF-8 bytes (Korean is three bytes a character, so fewer
 * characters fit; the token count comes out similar). 48 KiB is roughly 12k
 * tokens of English, a few percent of the smallest action-agent context
 * window, and it rides the prompt cache after the first unit. Enforced with
 * one rule set (`instructionsCapViolation`) wherever a definition is built:
 * run start and resume, saving a workflow, exporting one; the inspector shows
 * the same numbers live. Over the cap refuses with the path and size named,
 * never truncates. Independent of the saved-definition size cap.
 */
export const INSTRUCTIONS_FILE_MAX_BYTES = 16_384
export const INSTRUCTIONS_PER_ACTION_MAX_BYTES = 49_152

/** UTF-8 byte length: the unit every Instructions cap is measured in, browser and Node alike. */
export function utf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).length
}

/**
 * The first Instructions cap one Action breaks, as the sentence shown to the
 * person, or null when its files fit. `files` are the ones the Action reads:
 * its own file plus each tagged shared file, missing ones left out.
 */
/**
 * A short, stable key for an Instructions file's text: what a frozen run's
 * baseline records per path (`WorkflowRunOptions.instructionsBaseline`) so a
 * restart can tell an edit from a lock toggle or a save that changed nothing
 * (both bump `updatedAt`). Synchronous and identical in the browser and the
 * backend (53-bit cyrb53 plus the length); not a security hash.
 */
export function instructionsContentKey(content: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < content.length; i++) {
    const code = content.charCodeAt(i)
    h1 = Math.imul(h1 ^ code, 2654435761)
    h2 = Math.imul(h2 ^ code, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return `${content.length.toString(16)}-${(4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16)}`
}

export function instructionsCapViolation(
  actionName: string,
  files: ReadonlyArray<{ path: string; bytes: number }>,
): string | null {
  for (const file of files) {
    if (file.bytes > INSTRUCTIONS_FILE_MAX_BYTES) {
      return `The Instructions file "${file.path}" is ${file.bytes} bytes; the limit is ${INSTRUCTIONS_FILE_MAX_BYTES} bytes per file. Shorten it or split it.`
    }
  }
  const total = files.reduce((sum, file) => sum + file.bytes, 0)
  if (total > INSTRUCTIONS_PER_ACTION_MAX_BYTES) {
    return `The Instructions of "${actionName}" add up to ${total} bytes; the limit is ${INSTRUCTIONS_PER_ACTION_MAX_BYTES} bytes per Action (own file plus tagged files). Shorten or untag some.`
  }
  return null
}

/** Portable content, independent of its source account, project, or transport. */
export interface ContentPackage<T> {
  format: 'martini-content'
  version: 1
  name: string
  content: T
}

/**
 * Source geometry a workflow file carries so an import lands looking like the
 * sender's canvas. Keyed like `canvasPositions` (`action-<id>`, `bin-<id>`,
 * `annotation-<id>`, `variable-<id>`, `asset-<id>`) in the SOURCE id space.
 * Root entries are relative to the cluster's top-left corner; entries for bin
 * contents are bin-relative, as stored. Sizes ride along where the source
 * stored one (bins, notes).
 */
export interface WorkflowPackageLayout {
  positions: Record<string, { x: number; y: number; width?: number; height?: number }>
}

/**
 * Material the sender chose to send along inside one of the workflow's bins.
 * Used by explicitly included saved presets and by selected material in a workflow file.
 */
export interface WorkflowPackageBinContents {
  sourceBinId: string
  assets: Array<{ sourceId: string; record: WorkflowDefinitionAssetRecord; displayName?: string }>
}

export type WorkflowContentPackage = ContentPackage<{
  type: 'workflow'
  definition: WorkflowDefinition
  layout?: WorkflowPackageLayout
  binContents?: WorkflowPackageBinContents[]
}>

// ---------------------------------------------------------------------------
// Project files — the project's memory filesystem (`project_files`)
// ---------------------------------------------------------------------------

/** Hard cap per file (the table's check constraint). */
export const PROJECT_FILE_MAX_BYTES = 1_048_576
export const PROJECT_FILE_PATH_MAX_LENGTH = 200
/** Files per project; memory is a notebook, not a bucket. */
export const PROJECT_FILES_PER_PROJECT_CAP = 2_000

export interface ProjectFileRecord {
  id: string
  projectId: string
  /** Relative slash-separated path, unique per project (e.g. `project.md`, `workflows/dailies/experience.md`). */
  path: string
  content: string
  /** Locked means no writer at all (people, agents, the harness) until a human unlocks it in the inspector. */
  locked: boolean
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
}

/** One row of a project's file listing — no content, so a listing is cheap to put in agent context. */
export interface ProjectFileListing {
  path: string
  bytes: number
  updatedAt: string
  locked: boolean
}

/** A path-safe slug for a name ("EP01 Dailies — Cut!" → "ep01-dailies-cut"). */
export function projectFileSlug(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
    .replace(/-+$/g, '')
  return slug || 'untitled'
}

/** Where a workflow keeps its files: `workflows/<slug>/`. */
export function workflowFilesDirFor(workflowName: string): string {
  return `workflows/${projectFileSlug(workflowName)}`
}

/** What earlier runs of a workflow learned, appended by units and distilled after each Action. */
export function experiencePathFor(workflowName: string): string {
  return `${workflowFilesDirFor(workflowName)}/experience.md`
}

/**
 * The default path for an Action's own Instructions when it is authored:
 * `workflows/<workflow-slug>/instructions/<action-slug>.md`. A default only —
 * the path that counts is `Action.instructionsPath`, set once and never
 * recomputed from names.
 */
export function ownInstructionsPathFor(workflowName: string, actionName: string): string {
  return `${workflowFilesDirFor(workflowName)}/instructions/${projectFileSlug(actionName)}.md`
}

/**
 * A path no other Action uses: the candidate itself, or `<stem>-2.md`,
 * `<stem>-3.md`, … (two Actions named "Color Grade" and "Color-Grade" slug
 * alike; the second gets its own file).
 */
export function uniqueInstructionsPath(candidate: string, taken: ReadonlySet<string>): string {
  if (!taken.has(candidate)) return candidate
  const match = /^(.*?)(\.[A-Za-z0-9]+)?$/.exec(candidate)
  const stem = match?.[1] ?? candidate
  const extension = match?.[2] ?? ''
  for (let n = 2; ; n++) {
    const next = `${stem}-${n}${extension}`
    if (!taken.has(next)) return next
  }
}

/** The two kinds of workflow, one `workflows` table (ADR-0007). */
export type WorkflowKind = 'saved' | 'placed'

/**
 * An organization's library entry: reusable, versioned machinery. Runnable
 * directly (each run stamps a fresh placed copy) and placeable onto a canvas.
 */
/**
 * An operator pin on a saved workflow: every run of it — any door, any placed
 * copy linked to it — is driven by this engine, whatever the request asked
 * for. `oliveBudget` is what a pinned run gets when the request carries none
 * (Workflow Beta refuses a run without one); a budget in the request wins.
 * Set by hand on `workflows.run_engine` / `run_olive_budget`, like `feature_allowlist`.
 */
export interface WorkflowRunEnginePin {
  engine: 'fable'
  oliveBudget: number
}

export interface SavedWorkflowRecord {
  id: string
  kind: 'saved'
  organizationId: string
  name: string
  description: string
  /** Workspace folder name; null when unfiled. */
  folder: string | null
  version: number
  definition: WorkflowDefinition
  /** The placed workflow this was last saved from (provenance only). */
  sourceWorkflowId: string | null
  /** Operator pin on the engine of every run of this workflow; null when runs follow the request. */
  enginePin: WorkflowRunEnginePin | null
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

/**
 * One copy of a workflow on a canvas. The Yjs `workflows` record is the
 * truth (id == record id); the row mirrors it for the Workflows page and
 * links it to the saved workflow it was placed from or last saved as.
 */
export interface PlacedWorkflowRecord {
  id: string
  kind: 'placed'
  organizationId: string
  name: string
  /** The linked saved workflow, or null for a workflow authored on a canvas and never saved. */
  sourceWorkflowId: string | null
  /** The saved version it was placed from or last saved as; null when never linked. */
  version: number | null
  projectId: string
  sceneId: string
  canvasName: string
  /** A copy stamped for one API/MCP run of a saved workflow: hidden from listings, its runs roll up under the saved workflow. */
  hidden: boolean
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export type WorkflowRecord = SavedWorkflowRecord | PlacedWorkflowRecord

/** How the plan pass read the brief: fan out once per item of one input, or run once. */
export type ActionPlanMode = 'per_item' | 'single'

/** One approved execution unit, expanded mechanically by the harness from bin membership. */
export interface ActionPlanUnit {
  index: number
  /** Yjs asset UUIDs this unit consumes (the iterated item; context refs resolve at run time). */
  inputAssetIds: string[]
}

/**
 * The frozen contract between the plan pass and execution, stored on
 * `workflow_run_actions.plan`. The LLM authors only the *reading* of the brief
 * (`mode`, `iteratedRefId`, `outputsPerUnit`, `modelId`, `summary`,
 * `ambiguity`); the harness expands units, computes estimates, and hashes the
 * result — the model never authors numbers or UUIDs the gate relies on.
 */
/**
 * The plan pass's reading of the brief's judge clause. Guidance is the brief's
 * own acceptance language verbatim — strictness is never a code knob: the
 * judge model weighs it holistically and the harness branches only on `pass`.
 */
export interface ActionPlanJudge {
  guidance: string
  /** Total takes per unit, including the first (default 3). */
  maxAttempts: number
  /** Where gave-up takes route, ONLY when the brief names a destination; null = leave in place. */
  rejectBinId: string | null
}

/**
 * Why a plan re-parked after its units ran: the batch the agent built priced
 * (server-side, exact) above what was left on the run's rail, so nothing was
 * generated. `estOlives` is raised to the cost, so the next Go arms the rail
 * over it; the numbers let the gate say so instead of "needs input".
 */
export interface ActionPlanOverBudget {
  costOlives: number
  remainingOlives: number
}

export interface ActionPlan {
  mode: ActionPlanMode
  /** The input ref the Action fans out over; null when mode is 'single'. */
  iteratedRefId: string | null
  units: ActionPlanUnit[]
  outputsPerUnit: number
  totalPlannedOutputs: number
  modelId: string | null
  estOlives: number
  /** Set by the harness when the built batch was refused for exceeding the remaining budget. */
  overBudget?: ActionPlanOverBudget | null
  /** One human sentence of the plan's reading, rendered above the Go button. */
  summary: string
  /** The agent's question when the brief has more than one plausible reading. Parks, never fails. */
  ambiguity: string | null
  /** Present when the brief asks outputs to be judged; the harness owns the verdict/retake loop. */
  judge: ActionPlanJudge | null
  /** Server-computed. Approval stamps it into `review` so consent never carries to a re-derived plan. */
  planHash: string
}

/**
 * `needs_revision`: the run stopped because every unit of an Action asked for
 * its inputs to change (flag_for_revision, or a `DECLINED:` reply). Terminal
 * and resumable like `failed`, but the agent's decision, not a failure.
 */
export type WorkflowRunStatusValue =
  'pending' | 'awaiting_approval' | 'running' | 'paused' | 'completed' | 'failed' | 'needs_revision' | 'cancelled'

export type WorkflowRunActionStatusValue =
  | 'pending'
  | 'awaiting_approval'
  | 'running'
  | 'completed'
  | 'failed'
  /** Every unit asked for the inputs to change and none broke; see WorkflowRunStatusValue. */
  | 'needs_revision'
  | 'cancelled'
  | 'skipped'

export type WorkflowRunItemStatusValue =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'rejected'
  | 'superseded'
  /** A retake holds this take: it is the source of a rerun in flight and reverts or supersedes when that settles. */
  | 'rerunning'
  /**
   * The agent chose not to generate for this unit because the brief's inputs
   * fail a precondition it sets (flag_for_revision, or a `DECLINED:` reply):
   * `inputPayload.needsRevision` carries the request, or errorMessage the
   * reason when only the marker was used. Not a crash: the inputs need to
   * change, then the run is resumed or run again.
   */
  | 'needs_revision'

/** Compact plan block on the mirror so the canvas can render the gate without HTTP. */
export interface WorkflowRunStatusPlan {
  unitCount: number
  totalPlannedOutputs: number
  estOlives: number
  summary: string
  ambiguity: string | null
  overBudget?: ActionPlanOverBudget | null
}

/**
 * One action's slot on the mirror: enough for the canvas to label queued and
 * finished actions ("waiting" / "done · 2m 15s") and for the inspector to find
 * every run an action took part in — no HTTP round trip.
 */
export interface WorkflowRunStatusAction {
  actionId: string
  status: WorkflowRunActionStatusValue
  startedAt: string | null
  completedAt: string | null
}

export type WorkflowProgressStage =
  'starting' | 'planning' | 'executing' | 'generating' | 'reviewing' | 'recovering' | 'finalizing'

/** Bounded execution activity, independent of lease renewals and the diagnostic trace. */
export interface WorkflowRunProgress {
  stage: WorkflowProgressStage
  operation: string
  lastActivityAt: string
  actionId: string | null
  executorModel: string | null
  activeUnitCount: number
  queuedUnitCount: number
  /** Known outputs still executing, settling or awaiting review. */
  pendingOutputCount: number
  /** Concurrent sessions; step counters belong to one unit/take, never the whole run. */
  units: Array<{
    unitIndex: number
    take: number
    stage: WorkflowProgressStage
    operation: string
    stepsCompleted: number
    stepLimit: number | null
  }>
  recentActivity: Array<{ at: string; operation: string }>
}

/** A non-blocking caveat. Warnings never change lifecycle status or clear errors. */
export interface WorkflowWarning {
  code: string
  message: string
}

export interface WorkflowRunWarning extends WorkflowWarning {
  /** Null for a warning about the whole run, such as an applied budget default. */
  actionId: string | null
}

/** Includes warnings from earlier attempts, in Action order. Legacy records have none. */
export function workflowRunWarnings(
  actions: Pick<WorkflowRunActionRecord, 'actionId' | 'warnings'>[],
  runWarnings: WorkflowWarning[] = [],
): WorkflowRunWarning[] {
  return [
    ...runWarnings.map(warning => ({ ...warning, actionId: null })),
    ...actions.flatMap(action => (action.warnings ?? []).map(warning => ({ ...warning, actionId: action.actionId }))),
  ]
}

/** Small read model shared by the run inspector and compact API/MCP polls. */
export interface WorkflowRunStatusSummary {
  /** Optional for older servers and persisted records. Empty means no reported caveats. */
  warnings?: WorkflowRunWarning[]
  runId: string
  status: WorkflowRunStatusValue
  phase: string | null
  progress: WorkflowRunProgress | null
  currentAction: { id: string; name: string; index: number } | null
  actions: { completed: number; pending: number; total: number }
  outputs: {
    delivered: number
    accepted: number
    rejected: number
    failed: number
    /** Units the agent ended on purpose because the inputs need to change (see WorkflowRunStatusValue). */
    needsRevision: number
    pending: number
    /** Sum of plans derived so far, not a required-shot completeness guarantee. */
    planned: number
    plannedActions: number
  }
  olives: { budget: number | null; generation: number; agent: number }
  errorMessage: string | null
  /** A failed run's named cause, for code; null otherwise. Optional for older servers. */
  errorCode?: WorkflowRunErrorCode | null
  /** Which engine drives the run. Optional for older servers. */
  engine?: 'fable' | 'harness'
  /** True for a script build (Workflow Beta), which authors and tests the kit and generates no media. Absent on other runs. */
  compile?: boolean
  /**
   * Present only on a build that chains into an execution run: null until the handoff, then the
   * execution run's id — read that run for media results. Absent on build-only requests and on runs.
   */
  thenRunId?: string | null
  /** Compiles and promotions: how the kit tests went, with the failed output tail; absent on other runs and before verification. */
  kitVerification?: WorkflowRunKitVerification
}

/** Lightweight backend-owned mirror for live canvas progress. Keyed by run ID. */
export interface WorkflowRunStatus {
  /** Optional for older servers and persisted records. Empty means no reported caveats. */
  warnings?: WorkflowRunWarning[]
  runId: string
  projectId: string
  sceneId: string
  status: WorkflowRunStatusValue
  currentActionId: string | null
  currentActionIndex: number
  actionCount: number
  /** Run start, ISO; null until the harness claims the run. Optional: statuses mirrored before this field was added lack it. */
  startedAt?: string | null
  /** Every action in the run, in run order. Optional for the same reason as `startedAt`. */
  actions?: WorkflowRunStatusAction[]
  /**
   * The bins this run's Actions read or write, from its definition snapshot
   * (what the run executes, not the live canvas). The canvas keeps them from
   * being deleted while the run is live, even if an Action is rewired
   * mid-run. Absent on build runs, which touch no bins.
   */
  binIds?: string[]
  completedItemCount: number
  totalItemCount: number
  failedItemCount: number
  generationCostOlives: number
  agentCostOlives: number
  oliveBudget: number | null
  /** Present while a plan awaits approval (and kept for the run's duration once approved). */
  plan: WorkflowRunStatusPlan | null
  /** What the harness is doing right now ("unit 3/7 · waiting on generations"); null when idle/terminal. */
  phase: string | null
  errorMessage: string | null
  updatedAt: string
  /** Which door started the run. Optional: statuses mirrored before this field was added lack it. */
  origin?: WorkflowRunOrigin
  /** Set when the run is driven by Workflow Beta (one autonomous session); absent for the per-Action harness and on older mirrors. */
  engine?: WorkflowRunEngineValue
  /**
   * Set on a Workflow Beta compile run: it builds the workflow's scripts and executes no Action, so its
   * per-action slots track the build, never an Action's outcome. The run it chains into carries the outcomes.
   */
  build?: true
  /** Run creation, ISO — the stable order between two live runs on one canvas (`updatedAt` flips every sync). */
  createdAt?: string
  /** Who started the run. On the mirror so every collaborator's canvas can say whose run it is without HTTP. */
  startedBy?: WorkflowRunPerson | null
  /** The workspace whose olives this run spends: the starter's billing organization, whoever presses Go. */
  payer?: WorkflowRunPayer | null
  /** Who pressed Go on this plan, or authorized the run's budget when the plan auto-approved. */
  approvedBy?: WorkflowRunPerson | null
  cancelledBy?: WorkflowRunPerson | null
}

/** A person named on a run's mirror: the starter, the approver, the canceller. */
export interface WorkflowRunPerson {
  id: string
  /** The profile alias at mirror time. */
  name: string
}

export interface WorkflowRunPayer {
  organizationId: string
  name: string
}

/**
 * `workflow_run_actions.review`: the gate decision for one action's plan.
 * Approval binds to `planHash`; `approvedBy` is the person who pressed Go
 * (absent on auto-approved plans and on reviews written before it existed).
 */
export interface WorkflowRunActionReview {
  approved: boolean
  autoApproved?: boolean
  approvedAt: string
  planHash: string
  approvedBy?: string
}

export type WorkflowRunOrigin = 'ui' | 'chat' | 'mcp' | 'api'

/**
 * Who chose the run's olive rail. `explicit`: the caller passed a budget (or,
 * for canvas runs, a Go on the approval card arms it). `auto`: the run was
 * created without one and the harness arms the rail at each action's gate
 * with leeway over that action's estimate.
 */
export type WorkflowRunBudgetSource = 'explicit' | 'auto'

/**
 * What happens when spend crosses the budget. `strict`: canvas runs park for
 * a Go, API runs fail at the gate with the estimate. `soft`: runs driven over
 * MCP or chat, and canvas runs with run-level budget consent, keep going. The
 * budget is a marker `get_run` reports spend against. A trace notes crossings;
 * the step cap and repeated-error guard bound a runaway unit instead. Derived from the run's
 * origin and durable consent (workflows/runBudget.ts), never stored.
 */
export type WorkflowRunBudgetPolicy = 'strict' | 'soft'

/**
 * One variable value pinned for a single run. The value replaces the canvas
 * variable's value in every Action that reads it, for this run only — the
 * canvas node is never written, so concurrent runs of the same instance can
 * carry different values.
 */
export interface WorkflowRunVariable {
  /** The canvas variable node id. */
  id: string
  /** The variable's name at the time the run started. */
  name: string
  value: string
}

/**
 * One input bin's contents pinned for a single run: these project assets
 * stand in for the bin's canvas members in every Action that reads it. The
 * bin itself is never written.
 */
export interface WorkflowRunBinInput {
  /** Internal snapshots of Files in ordinary bins retain their existing live-note behavior. */
  includeCanvasNotes?: boolean
  /** The canvas bin id. */
  id: string
  /** The bin's name at the time the run started. */
  name: string
  /** Project asset ids, in the order the Action should read them. */
  assetIds: string[]
}

/**
 * How the caller asked the run to be driven; rides beside the pinned inputs
 * in `workflow_runs.input` so a run option needs no column of its own.
 */
export interface WorkflowRunOptions {
  /** Host workspace affinity; absent on legacy and portable Daytona runs. */
  workspaceRoot?: string
  candidate?: WorkflowKitCandidateIdentity
  consumedCandidate?: WorkflowKitCandidateIdentity & { applied: string[] }
  candidateBaseline?: Record<string, string>
  /** Workflow Beta only: author and dry-test kit scripts without generating media. */
  compile?: boolean
  /**
   * The run's Instructions are frozen from the saved workflow's carried files
   * (a saved-workflow run copy; see `CreateCanvasWorkflowRunInput.instructionsFiles`):
   * the agent reads the snapshot's text, never the project's file at the same
   * fixed kit path, which an older version or a sibling run may hold. Absence is
   * frozen too: a path the saved version carries no file for reads as missing.
   * A restart keeps the snapshot except where `instructionsBaseline` says a file
   * changed.
   */
  frozenInstructions?: boolean
  /**
   * A visible run copy's project files as the run last read them: the content
   * key (`instructionsContentKey`) per Instructions path, null where the project
   * had none; recorded at run start. A restart (a classic resume or rehearsal
   * Go; Workflow Beta runs do not restart) takes the project's file for a path
   * whose content no longer matches — someone edited the copy since — keeps the
   * frozen snapshot everywhere else, and writes the keys it read back as the
   * next baseline in the same compare-and-set as the snapshot, so reverting an
   * adopted edit counts as an edit too. A lock toggle or an unchanged save never
   * brings the older bytes back. Absent on hidden copies: nobody edits them.
   */
  instructionsBaseline?: Record<string, string | null>
  /** Original JSON run request, started only after the compiled kit has been saved and this run completes. */
  thenRun?: Record<string, unknown>
  thenRunId?: string
  /**
   * Engine-owned, stamped on the run an automatic compile chained into: the compile that rebuilt the
   * scripts first and its clock, so the run can show the build as its first step and time it.
   */
  compiledBy?: WorkflowRunCompileStep
  /** Engine-owned durable compile checkpoints, never caller-authored. */
  chain?: { status: 'pending' | 'handoff' | 'done'; key: string }
  compileBaseline?: {
    ownership?: Record<string, string[]>
    freshness?: KitFreshness
    /** Only files owned by originally fresh Actions; missing files have an empty hash. */
    hashes: Record<string, string>
    sha256?: string
  }
  compilePhase?: 'verified' | 'published'
  /**
   * Written with `compilePhase: 'published'` when the kit's head swap landed but its canvas pin did not
   * follow: the durable record of a mirror failure (the run warning beside it is best effort). Operator copy.
   */
  compileMirror?: { error: string; at: string }
  compileVerdict?: { failure?: string }
  /** Engine-stamped clock of the compile's selftest, so the run can show the tests as a step of their own. */
  compileTests?: { startedAt: string; completedAt?: string; failed?: boolean }
  verifiedKit?: WorkflowToolBundle
  compilePublication?: { predecessorVersion: string | null; epoch: string }
  testsPassedByRunId?: string
  /** Engine-recorded verification environment, never trusted from uploaded kit metadata. */
  kitVerification?: WorkflowRunKitVerification
  /** Engine-measured wall clock of a promotion's stages; written at publication or failure, absent on other runs. */
  promotionTimings?: WorkflowPromotionTimings
  /** Where and when the Workflow Beta runner first admitted this run (queue wait behind the per-machine cap). */
  admission?: WorkflowRunAdmission
  /** Engine-authored totals and findings; the full evidence lives in the project post-mortem file. */
  postmortem?: WorkflowRunPostmortem
  /**
   * Why the engine failed the run, for operators: written by the Workflow Beta runner beside the user-facing
   * `errorMessage` when an internal error ends the run, so a generic "unexpected error" is diagnosable from the
   * row (`get_run`, Backstage) without the machine's log buffer. Operator copy, never rendered to users.
   */
  failure?: WorkflowRunFailure
  /**
   * Rehearsal (ADR-0008 amendment): every Action parks for a Go before its
   * plan executes, never auto-approved, and its Instructions are re-read from
   * the project when it proceeds, so the operator can fix the method between
   * Actions. An authoring mode, not the default.
   */
  rehearsal?: boolean
  /**
   * Ignored since 2026-09-11: how a sandbox session reaches Martini is the deployment's `WORKFLOW_CLI_MODE`
   * (`cli-first`, the default, or the `mcp` rollback), resolved at session start; a stored per-run preference
   * never overrides it. Kept only so rows written by the earlier per-run override still parse.
   */
  cliMode?: 'complement' | 'cli-first'
  /**
   * Which engine drives the run: the per-Action harness (default) or the Workflow Beta engine — one autonomous
   * session for the whole workflow. `'claude-code'` is the pre-rename spelling of the same engine: read for rows
   * written before 2026-09-09, never written.
   */
  engine?: WorkflowRunEngineValue
  /** Engine session id, persisted by the Workflow Beta runner so a recovered run can resume its transcript. */
  claudeCodeSessionId?: string
  /** The step agent's model and speed, fixed at the first launch; a recovered run reuses it rather than re-reading kit.json. */
  claudeCodeAgent?: { model: string; speed: 'standard' | 'fast' }
  /**
   * The kit's named-generate-family opt-in, fixed at the first launch while WORKFLOW_GENERATE_FAMILIES is on; a
   * recovered run reuses it rather than re-reading kit.json.
   */
  generateFamiliesKitOptIn?: boolean
  /**
   * Written by the Workflow Beta runner when a backend shutdown hands the run back mid-session: when, which tool
   * was executing, and how far into its turn the agent was. The owner that relaunches the agent reads it for the
   * `workflow_agent_relaunched` event and clears it; a recovered run without one lost its owner without a handback.
   */
  agentInterrupted?: WorkflowAgentInterruption
  /**
   * The container agent this run launched and may still be running: what a later owner needs to attach to it
   * after a deploy handed the run over (WORKFLOW_CONTAINER_AGENT_ADOPT). Written at launch, cursor advanced with
   * the usage flush, removed when the agent exits.
   */
  containerAgent?: WorkflowContainerAgentLaunch
  /** Engine-owned Actions, Notes and Run report regions, shown in the workflow panel. */
  runLog?: string
  /** Canvas log written by historical runs. */
  runLogNoteId?: string
}

/**
 * How a compile or promotion's kit tests went, as the engine ran them in the protected verification
 * view. Counts are the enforcement evidence; `blamed` and `outputTail` are for the author reading a
 * failure without the sandbox, which is torn down with the run.
 */
export interface WorkflowRunKitVerification {
  at: string
  runtimeContract: string | null
  formatVersion: 1
  snapshot: string | null
  host: 'local' | 'daytona'
  backendRevision: string | null
  cliVersion: string | null
  passed: number
  failed: number
  /** The test command's exit status; null when it was killed at its timeout. Absent on rows written before it was recorded. */
  exitCode?: number | null
  /** The test command ran past its timeout and was killed. */
  timedOut?: boolean
  /** Wall clock of the test command, ms. */
  durationMs?: number
  /** Tools-relative files the test command named on `test failed: <path>` lines; absent on rows written before it was recorded. */
  blamed?: string[]
  /**
   * The last part of the failed test command's stdout+stderr, bounded (see `KIT_TEST_OUTPUT_TAIL_CHARS`),
   * with a leading `…[N chars omitted]` marker when cut. Only on a failure; authoring-agent copy — file
   * paths and stack traces are expected — never rendered as end-user UI.
   */
  outputTail?: string
}

/**
 * Where a promotion's wall clock went, in ms. Each stage is set when it ends, so a failed run
 * carries the stages it reached; `totalMs` runs from the claim to publication or failure.
 */
export interface WorkflowPromotionTimings {
  /**
   * How the sandbox came up; `host` for the host-mode environment. `prewarmed` and `pooled` appear
   * only on promotions recorded before the run prewarm and the standing sandbox pool were removed.
   */
  boot?: 'prewarmed' | 'pooled' | 'cold' | 'resumed' | 'host'
  /** Opening the sandbox, including the tools library materialization. */
  sandboxOpenMs?: number
  /** Inside `sandboxOpenMs`: waiting for the environment lease. Daytona only. */
  leaseMs?: number
  /** Inside `sandboxOpenMs`: the sandbox create-or-start and workspace setup. Daytona only. */
  sandboxBootMs?: number
  /** Inside `sandboxOpenMs`: materializing tools/ from object storage. Daytona only. */
  toolsSyncMs?: number
  /** Reading the candidate, the baseline fence and the kit hashes (verifying the kit). */
  baselineMs?: number
  /** Writing the protected verification view and running the kit's tests.command. */
  testsMs?: number
  /** Freezing the verified kit into an immutable bundle. */
  freezeMs?: number
  /** Publishing the head and mirroring the canvas pin. */
  publishMs?: number
  totalMs?: number
}

/** The engine's own account of a failed Workflow Beta run: a stable code, the internal message, and when. */
export interface WorkflowRunFailure {
  /** A guard's stable identifier (`COMPILE_BASELINE_MISSING`, …) or the thrown error's class name. */
  code: string
  /** The internal error message as thrown — operator copy. */
  message: string
  /** ISO timestamp of the failure. */
  at: string
}

/**
 * The engine failure codes (`WorkflowRunFailure.code`) published to callers, by public name. Every
 * other failure stays unnamed: its code is a class name or a guard id, operator detail.
 */
export const PUBLIC_WORKFLOW_RUN_FAILURE_CODES = {
  /** The model provider kept failing or could not be reached: not the workflow; running again is the fix. */
  MODEL_PROVIDER_UNAVAILABLE: 'model_provider_unavailable',
} as const

/** Why a failed run failed, for code (the API's `errorCode`, the Workflows page's badge). */
export type WorkflowRunErrorCode =
  (typeof PUBLIC_WORKFLOW_RUN_FAILURE_CODES)[keyof typeof PUBLIC_WORKFLOW_RUN_FAILURE_CODES]

/**
 * The public code of a failed run's cause; null for any other status and for a cause without one.
 * Reads only the failure's code, so a run listing that narrows its input still answers.
 */
export function workflowRunErrorCode(run: {
  status: WorkflowRunStatusValue
  input: { options?: { failure?: Pick<WorkflowRunFailure, 'code'> } }
}): WorkflowRunErrorCode | null {
  const code = run.status === 'failed' ? run.input.options?.failure?.code : undefined
  return code && Object.hasOwn(PUBLIC_WORKFLOW_RUN_FAILURE_CODES, code)
    ? PUBLIC_WORKFLOW_RUN_FAILURE_CODES[code as keyof typeof PUBLIC_WORKFLOW_RUN_FAILURE_CODES]
    : null
}

/**
 * How a Workflow Beta run was admitted onto its first machine: written once, with the pending → running
 * transition, so the completion event and the time report can say where the run executed and how long it sat
 * behind the per-machine run cap. Re-claims after a handback keep the first admission.
 */
export interface WorkflowRunAdmission {
  /** The machine (Fly machine id, `SERVER_INSTANCE_ID`) whose runner admitted the run. */
  machine: string
  /** Milliseconds between the run's creation and its admission: the queue behind the run cap plus the claim. */
  queueWaitMs: number
  /** The admitting machine's lane load right after this run took its slot, and the lane's cap. */
  laneActive: number
  laneCap: number
  /** ISO timestamp of the admission. */
  at: string
}

/** The moment a shutdown handed a Workflow Beta run back: what the interrupted agent turn was doing. */
export interface WorkflowAgentInterruption {
  /** Epoch ms of the handback. */
  at: number
  /** The tool executing, or the last one invoked, when the owner stopped; absent before the first tool. */
  tool?: string
  /** How far into its turn the agent was, in ms — the work the relaunch redoes. */
  turnMs: number
  /**
   * Written while no session was live (the sandbox was opening, or the run was past its session in
   * settle or delivery): the run is a deploy handback, but no model turn was lost, and recovery must
   * not read it as evidence that a session ran.
   */
  outsideSession?: true
  /**
   * Epoch ms of the claim that relaunched this handback. The claim stamps it so no machine's 30 s
   * sweep lists the row again: one durable relaunch per deploy handback, across replicas. A later
   * shutdown writes a fresh marker without it.
   */
  sweptAt?: number
}

/** A running container agent's launch record: the environment session and command, and how far its output was read. */
export interface WorkflowContainerAgentLaunch {
  v: 1
  /** The run-token owner minted for this launch; a later owner accepts it beside its own. */
  launchOwner: string
  sessionId: string
  cmdId: string
  /** Epoch ms. */
  launchedAt: number
  /** The last stream record dispatched in order; an attach drops records at or below it. */
  seq: number
}

/** The automatic compile that ran before a Workflow Beta run: which run built the scripts, and when. */
export interface WorkflowRunCompileStep {
  runId: string
  /** The compile run's own clock, ISO; `startedAt` is null only for a compile that never claimed a worker. */
  startedAt: string | null
  completedAt: string | null
}

export interface WorkflowPostmortemFinding {
  scope: 'engine' | 'workflow' | 'provider'
  category: string
  summary: string
  evidence: {
    actionId?: string
    step?: string
    spans: string[]
    ms?: number
    log?: string
    assetIds: string[]
  }
  suggestedFix?: string
  confidence: 'low' | 'medium' | 'high'
  source: 'engine' | 'agent' | 'script'
}

export interface WorkflowRunPostmortem {
  /** Manifest judge calls are agent usage, never session or decision turns. */
  judgeCalls?: Array<{
    actionId: string
    model: string
    tier: 'fast' | 'careful'
    items: string[]
    ms: number
    inputTokens: number
    outputTokens: number
    inputCacheReadTokens?: number
    inputCacheWriteTokens?: number
    olives: number
    usd: number
    error?: string
  }>

  repairs?: Array<{
    kind: 'script' | 'manifest' | 'bible' | 'other'
    path: string
    bytes: number
    actionId: string
    action: string
    published?: boolean
    reason?: string
  }>
  totals: {
    wallMs: number
    modelMs: number
    toolMs: number
    stepMs: number
    providerWaitMs: number
    idleMs: number
    turns: number
    /** Absent on reports recorded before fast-path turn accounting. */
    decisionTurns?: number
    otherTurns?: number
    apiMs: number | null
    sdkRoundTrips: number | null
    generationOlives: number
    agentOlives: number
  }
  findings: WorkflowPostmortemFinding[]
}

/** The stored engine marker: the client-facing `'fable'`, or the pre-rename `'claude-code'` still on older rows. */
export type WorkflowRunEngineValue = 'fable' | 'claude-code'

/** One definition of "this run is on Workflow Beta", shared by the backend doors and the panel. */
export function isWorkflowBetaEngine(engine: string | null | undefined): engine is WorkflowRunEngineValue {
  return engine === 'fable' || engine === 'claude-code'
}

/** Pinned run inputs and server-owned authorization (`workflow_runs.input`). */
export interface WorkflowRunInput {
  /** Server-authored caveats recorded at creation, retained across builds and retries. */
  warnings?: WorkflowWarning[]
  /** Server-written by Go: budget consent for this entire run, retained across resumes and replans. */
  budgetApproval?: { approvedAt: string; approvedBy?: string }
  fileRequestIdentity?: string
  variables?: WorkflowRunVariable[]
  bins?: WorkflowRunBinInput[]
  files?: JsonFileSnapshot[]
  options?: WorkflowRunOptions
}

/** The inputs a workflow accepts, read from its Actions. */
export interface WorkflowInputs {
  /** Variables the Actions read, with their current canvas values. */
  variables: Array<{ id: string; name: string; value: string }>
  /**
   * Bins the Actions read but no Action writes. assetCount counts runnable assets, notes and variables
   * throughout the bin's subtree on this canvas. It describes contents, not whether a run is allowed.
   */
  bins: Array<{ id: string; name: string; assetCount: number; fileInput?: FileInputContract }>
}

export interface WorkflowRunRecord {
  id: string
  projectId: string
  sceneId: string
  createdBy: string | null
  /** Who cancelled the run, when a person did (anyone with project write access may). Absent on records built before the column. */
  cancelledBy?: string | null
  /** Which door started the run: the canvas (`ui`), the project assistant (`chat`), an external agent (`mcp`), or the public API (`api`). */
  origin: WorkflowRunOrigin
  budgetSource: WorkflowRunBudgetSource
  status: WorkflowRunStatusValue
  /** The placed workflow (Yjs `workflows` record / `workflows.id`) this run executed; null for runs older than placed rows. */
  workflowId: string | null
  /** The saved workflow the placed copy was linked to when the run started, and its version then. */
  savedWorkflowId: string | null
  savedWorkflowVersion: number | null
  definitionSnapshot: WorkflowDefinition
  input: WorkflowRunInput
  currentActionIndex: number
  oliveBudget: number | null
  generationCostOlives: number
  agentCostOlives: number
  errorMessage: string | null
  /**
   * The harness's live "what it is doing" line ("unit 3/7 · waiting on
   * generations"), written per phase change while the run executes. Display
   * copy, meaningful only while the run is `running`.
   */
  phase: string | null
  /** Absent on older records; lifecycle status remains authoritative when work stops. */
  progress?: WorkflowRunProgress | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
}

/**
 * One line of the per-action diagnostics trail on `workflow_run_actions.trace`:
 * the plan pass, every tool call (with wall time and serialized result size —
 * the context-cost driver), per-unit outcomes with token totals, and sweeps.
 * Written by the harness; read by the canvas inspector and the public API.
 */
export interface ActionTraceEvent {
  /** Wrapper owner; pre-action means its invocation started no Action. */
  actionId?: string
  integrity?: 'changed'
  admittedSha256?: string
  afterSha256?: string | null
  /** Milliseconds since this action's execution began (gate and pause waits excluded). */
  t: number
  /** Wall clock of the event (epoch ms), so a run timeline can place it; spans start at `at - ms`. */
  at?: number
  /** Stable span id <actionIndex>.<seq>; findings reference this rather than copying spans. */
  id?: string
  /** The unit (0-based) and take this event belongs to; absent for action-level events. */
  unit?: number
  take?: number
  /** Turn classification is measured once; CLI judgements may mark it later. */
  decisionPoint?: boolean
  firstTool?: string
  kind: 'plan' | 'tool' | 'unit' | 'revision' | 'settle' | 'judge' | 'sweep' | 'note' | 'turn'
  name?: string
  ms?: number
  chars?: number
  outputAssetIds?: string[]
  inputTokens?: number
  outputTokens?: number
  /** Model steps the session took (unit events from the real executor). */
  steps?: number
  /** Input tokens the provider served from its prompt cache, when it reported them. */
  cacheReadTokens?: number
  /**
   * tool: how much of `ms` is already counted by the spans nested inside this one
   * (generation waits, tool calls). Subtracted from the span's own total so a
   * compiled step phase — or a `martini` call from the sandbox — and the waits it
   * contains are never both charged.
   */
  nestedMs?: number
  /** settle: when the first output landed, ms after the wait began (the rest is the provider's batch spread). */
  firstMs?: number
  /** settle: board reads the wait took. */
  polls?: number
  note?: string
  /** note: the producer's own verdict, so a label's wording can never colour a span. */
  status?: 'error'
  /**
   * tool · a `martini` CLI call: how the engine answered when it was neither done nor pending.
   * `note: 'error'` still counts every one of these in the rollup; `outcome` is what lets a
   * reader tell a refusal (the engine will not do this) from an error or a cancelled call.
   */
  outcome?: 'refused' | 'error' | 'cancelled'
  /** Call summary for computer tools (the exec command / written path). */
  detail?: string
  /** note · trace truncated: spans the stored window dropped. Rollup totals still count them. */
  dropped?: number
}

export interface WorkflowRunActionRecord {
  /** Optional for older servers and persisted records. Empty means no reported caveats. */
  warnings?: WorkflowWarning[]
  id: string
  runId: string
  actionId: string
  actionIndex: number
  definition: Action
  agentId: string
  status: WorkflowRunActionStatusValue
  attempt: number
  plan: ActionPlan | null
  /** Diagnostics trail (plan pass, tool calls, unit outcomes); shape is backend-owned. */
  trace: unknown[] | null
  inputTokens: number
  outputTokens: number
  agentCostOlives: number
  review: Record<string, unknown> | null
  errorMessage: string | null
  startedAt: string | null
  completedAt: string | null
}

export interface WorkflowRunItemRecord {
  id: string
  runId: string
  runActionId: string
  actionId: string
  inputAssetIds: string[]
  inputPayload: Record<string, unknown> | null
  outputAssetId: string | null
  generationId: string | null
  replacesItemId: string | null
  status: WorkflowRunItemStatusValue
  attempt: number
  costOlives: number
  agentCostOlives: number
  feedback: string | null
  verdict: Record<string, unknown> | null
  errorMessage: string | null
  createdAt: string
  completedAt: string | null
}

/**
 * The settled result of an item or Action, beside its lifecycle `status` (the
 * check-run status/conclusion split). `generated` for completed work, `failed`
 * for a broken one, `needs_revision` when the agent ended the unit on purpose
 * with flag_for_revision because the brief's inputs failed a precondition.
 */
export type WorkflowRunItemOutcome = 'generated' | 'failed' | 'needs_revision'

/** Written to `workflow_run_items.input_payload.needsRevision` when the agent ended the unit with flag_for_revision. */
export interface WorkflowRunRevisionRequest {
  /** The headline: what fails and what must change, a few sentences at most. */
  reason: string
  /** The agent's longer working-out, when it gave one; rendered collapsed. */
  detail?: string
  /** Ids or names of the variables, assets, or bins the agent says need changing; empty when it named none. */
  inputRefs: string[]
  /** Root ids of comment threads the agent left during the unit (add_comment), for the canvas link. */
  commentThreadIds: string[]
}

function stringsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []
}

/**
 * Defensive read of a `needs_revision` item's request. Two records exist: the
 * jsonb payload carries the flag_for_revision request (full reason, named
 * inputs, comment threads), or — when the agent only answered with the
 * `DECLINED:` marker — the error message is the reason (no named inputs).
 * Non-string refs and thread ids are dropped. Null for every other item.
 */
export function workflowRunRevisionRequestOf(
  item: Pick<WorkflowRunItemRecord, 'status' | 'inputPayload' | 'errorMessage'>,
): WorkflowRunRevisionRequest | null {
  if (item.status !== 'needs_revision') return null
  const raw = item.inputPayload?.needsRevision
  if (typeof raw !== 'object' || raw === null) {
    const reason = item.errorMessage?.trim()
    return reason ? { reason, inputRefs: [], commentThreadIds: [] } : null
  }
  const { reason, detail, inputRefs, commentThreadIds } = raw as Record<string, unknown>
  if (typeof reason !== 'string' || reason.trim() === '') return null
  return {
    reason,
    ...(typeof detail === 'string' && detail.trim() !== '' ? { detail } : {}),
    inputRefs: stringsOf(inputRefs),
    commentThreadIds: stringsOf(commentThreadIds),
  }
}

/** Items: completed → generated; needs_revision → needs_revision; failed → failed; anything else (still open) → null. */
export function workflowRunItemOutcome(item: Pick<WorkflowRunItemRecord, 'status'>): WorkflowRunItemOutcome | null {
  if (item.status === 'completed') return 'generated'
  if (item.status === 'needs_revision') return 'needs_revision'
  return item.status === 'failed' ? 'failed' : null
}

/** Actions: the same reading as items; anything still open → null. */
export function workflowRunActionOutcome(status: WorkflowRunActionStatusValue): WorkflowRunItemOutcome | null {
  if (status === 'completed') return 'generated'
  if (status === 'needs_revision') return 'needs_revision'
  return status === 'failed' ? 'failed' : null
}

// ---------------------------------------------------------------------------
// Workflows payloads. Built by the backend from `workflows` + `workflow_runs`
// so no page touches Yjs docs. Four readers, each sized for what it shows:
// GET /workflows/overview (the Workflows page: statistics per workflow, no
// runs), GET /workflows/runs (one workflow's runs, paged), GET
// /workflows/index (the canvas picker: headers and derived summaries) and
// GET /workflows/saved (MCP's full listing with copies and runs). The numbers
// the detail view shows over runs (average cost per settled run, success
// rate, average duration) are computed client-side from run rows; the
// overview's statistics are the same definitions summed by the backend.
// ---------------------------------------------------------------------------

export type WorkflowRefKind = 'bin' | 'variable' | 'note' | 'asset'
/** Saved input-bin defaults. Omitted bins use these contents; explicit overrides replace them. */
export interface SavedWorkflowBinDefaults {
  required: boolean
  defaultSource: 'saved_preset' | 'saved_files' | null
}

export interface WorkflowRef extends Partial<SavedWorkflowBinDefaults> {
  kind: WorkflowRefKind
  name: string
}

/** Run states as the page sees them — the run's own status union. */
export type WorkflowRunState = WorkflowRunStatusValue

export interface WorkflowStepSummary {
  name: string
  agent: string
}

export interface WorkflowRunStep {
  name: string
  state: WorkflowRunActionStatusValue
  durationMs: number | null
  /** When the step began; the page clocks elapsed from this while it is running. */
  startedAt: string | null
}

export interface WorkflowRunSummary {
  id: string
  /** The saved workflow the run's placed copy was linked to, when it was. */
  savedWorkflowId: string | null
  /** The placed workflow that ran. */
  workflowId: string
  state: WorkflowRunState
  /** When the run got a slot and began; a run still queued carries its creation time here, as the list's sort key. */
  startedAt: string
  /** When the run was asked for: the start of its queue wait. */
  createdAt: string
  /**
   * How long the run waited for a slot before it started; null while it is still queued.
   * Never part of `durationMs`, which starts at `startedAt`.
   */
  queueWaitMs: number | null
  /** Start to end, queue wait excluded. */
  durationMs: number | null
  /** Generation + agent olives. */
  costOlives: number
  costSplit: { generationOlives: number; agentOlives: number }
  trigger: WorkflowRunOrigin
  /** Set when the run was driven by Workflow Beta; absent for the per-Action harness. */
  engine?: WorkflowRunEngineValue
  /**
   * Set when the run built the workflow's scripts instead of generating media.
   * `chained` when the engine built them on its own ahead of the run that was
   * asked for (the `auto` kit policy); false for an explicit Compile.
   */
  compile?: { chained: boolean }
  /** Profile alias of who started the run; null when the account is gone. */
  startedBy: string | null
  /** Takes the run landed in its output bins. */
  outputs: number
  steps: WorkflowRunStep[]
  /** Backend-authored copy for failed runs. */
  note?: string
  /** A failed run's named cause, when it has one (`workflowRunErrorCode`). */
  errorCode?: WorkflowRunErrorCode
}

/** One placed workflow (a copy on a canvas) with its runs. */
export interface PlacedWorkflowSummary {
  id: string
  kind: 'placed'
  /** The saved workflow it was placed from or last saved as, when linked. */
  sourceWorkflowId: string | null
  version: number | null
  name: string
  projectId: string
  projectName: string
  canvasId: string
  canvasName: string
  /** A run copy stamped for one API/MCP run; listed only under its saved workflow. */
  hidden: boolean
  placedAt: string
  runs: WorkflowRunSummary[]
}

export interface SavedWorkflowSummary {
  id: string
  kind: 'saved'
  name: string
  description: string
  /** Workspace folder name; null when unfiled. */
  folder: string | null
  version: number
  /** Content identity of the machinery (see backend/src/workflows/fingerprint.ts). */
  fingerprint: string
  steps: WorkflowStepSummary[]
  inputs: WorkflowRef[]
  outputs: WorkflowRef[]
  /** The doors it has been run through. */
  runsVia: WorkflowRunOrigin[]
  /** Agent skills the saved kit ships; absent when it ships none (or predates skill discovery). */
  skills?: WorkflowKitSkill[]
  /** Operator pin: every run of this workflow is driven by this engine (see SavedWorkflowRecord.enginePin). */
  enginePin?: WorkflowRunEnginePin | null
  /** Stable paging key for MCP listings: an edit or rename never moves it. */
  createdAt: string
  updatedAt: string
  /** Copies on canvases, hidden run copies included (their runs are the API runs). */
  placed: PlacedWorkflowSummary[]
}

export interface WorkflowsListResponse {
  saved: SavedWorkflowSummary[]
  /** Non-hidden placed workflows with no listed saved workflow (authored on a canvas, or whose saved workflow is gone). */
  placed: PlacedWorkflowSummary[]
}

/**
 * Run statistics for one placed copy, summed by the backend over every run
 * it has (`runStatsOf` in backend/src/workflows/savedWorkflows.ts, over the
 * same run summaries the detail view shows, receipts included). The
 * Workflows page's numbers are defined by these fields the way
 * `frontend/src/lib/workflowSummaries.ts` defines them over runs: settled =
 * completed, failed or needs revision, builds excluded; success rate =
 * completed / settled; average cost = settledCostOlives / settled; total
 * cost counts every run, builds included.
 */
export interface WorkflowRunStats {
  /** Every run, builds included. */
  runs: number
  /** Running, awaiting approval, or pending. */
  live: number
  /** The newest run still going, for the row's state word; null when none is. */
  liveRun: Pick<WorkflowRunSummary, 'state' | 'compile' | 'startedAt'> | null
  /** Ended with a verdict, builds excluded: the cohort of the success rate and the average cost. */
  settled: number
  /** Completed among the settled. */
  completed: number
  /** Olives over every run, builds included. */
  totalCostOlives: number
  /** Olives over the settled cohort. */
  settledCostOlives: number
  /** Newest start (creation, for a run not yet started); null with no runs. */
  lastRunAt: string | null
  /** The doors runs came through. */
  origins: WorkflowRunOrigin[]
}

/** A placed copy on the Workflows overview: its run statistics instead of its runs. */
export interface PlacedWorkflowOverview extends Omit<PlacedWorkflowSummary, 'runs'> {
  stats: WorkflowRunStats
}

/** A saved workflow on the Workflows overview: copies with statistics, and the statistics summed over them. */
export interface SavedWorkflowOverview extends Omit<SavedWorkflowSummary, 'placed'> {
  placed: PlacedWorkflowOverview[]
  stats: WorkflowRunStats
}

/**
 * GET /workflows/overview — the Workflows page in one request: what the
 * overview shows, sized by the number of workflows rather than the number of
 * runs. Runs themselves come from GET /workflows/runs when one workflow is
 * opened.
 */
export interface WorkflowsOverviewResponse {
  orgId: string
  /** Over every run under the listed workflows and copies — not a page of them. */
  totals: { runs: number; live: number; costOlives: number }
  saved: SavedWorkflowOverview[]
  /** Non-hidden placed workflows with no listed saved workflow. */
  placed: PlacedWorkflowOverview[]
}

/** GET /workflows/runs — one workflow's runs, newest first, keyset-paged. */
export interface WorkflowRunsPage {
  runs: WorkflowRunSummary[]
  /** Pass back as `cursor` for the next page; null on the last page. */
  nextCursor: string | null
}

/**
 * A saved workflow as a chooser lists it: the row and what its definition
 * derives (steps, inputs, outputs, fingerprint, skills), without copies or
 * run history. Enough to show a list and place one on a canvas.
 */
export type SavedWorkflowIndexEntry = Omit<SavedWorkflowSummary, 'placed' | 'runsVia'>

/**
 * GET /workflows/index — every saved workflow in a workspace as an index
 * entry, most recently updated first. The canvas picker's request: it reads
 * headers and the derived-summary cache, never projects, copies or runs.
 */
export interface WorkflowsIndexResponse {
  orgId: string
  saved: SavedWorkflowIndexEntry[]
}

// ============================================
// Lineage (provenance / copyright / compliance trail)
// ============================================

/**
 * Mirrors the Postgres `generation_capability` enum (see 04_generations.sql).
 */
export type GenerationCapability =
  | 'generate-video'
  | 'extend-video'
  | 'edit-video'
  | 'generate-still'
  | 'upscale-image'
  | 'upscale-video'
  | 'generate-set-3d'
  | 'generate-audio'
  | 'inpaint-audio'

/**
 * Mirrors the Postgres `generation_status` enum (see 04_generations.sql).
 */
export type GenerationStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

/** How a reference asset was fed into a generation. */
export type LineageReferenceKind = 'startFrame' | 'endFrame' | 'styleRef' | 'character' | 'video' | 'element' | 'audio'

/**
 * How the parent generation was resolved.
 * `yjs-parent` = walked `asset.parentId` in the Yjs doc.
 * `input-videoAssetId` / `input-sourceAssetId` = read `generation.input` JSONB.
 * `root` = no parent (upload, prompt-only t2v, etc.).
 */
export type LineageDerivationSource = 'yjs-parent' | 'input-videoAssetId' | 'input-sourceAssetId' | 'root'

/** One input asset consumed by a generation. */
export interface LineageTakeReference {
  kind: LineageReferenceKind
  assetId: string | null
  url: string
  originalFilename: string | null
}

/** One generation in the lineage, with everything a legal reviewer needs. */
export interface LineageTake {
  generationId: string
  assetId: string | null // null if the generation failed before producing an asset
  jobId: string | null
  operation: AssetOperation
  capability: GenerationCapability
  prompt: string // post-substitution, as actually sent to the model
  userPrompt: string | null // original, may contain @[uuid:Name] element tags
  variableSnapshot: Record<string, { name: string; value: string; color?: string }> | null
  model: string
  modelVersion: string | null
  status: GenerationStatus
  params: Record<string, unknown>
  references: LineageTakeReference[]
  parentAssetId: string | null
  inferredParentGenerationId: string | null
  derivationSource: LineageDerivationSource
  shotNumber: number | null // null for orphaned takes (never placed on a shot)
  siblingIndex: number | null
  variationNumber: number
  createdAt: string
  completedAt: string | null
  authorId: string | null
  authorName: string | null
  outputUrl: string | null
  thumbnailUrl: string | null
  errorCode: string | null
  errorMessage: string | null
}

/** All takes belonging to one shot (selected + variations + extensions + offshoots). */
export interface LineageShot {
  shotNumber: number
  selectedAssetId: string | null
  takes: LineageTake[]
}

/** A scene and the shots inside it. */
export interface LineageScene {
  id: string
  name: string
  description: string | null
  shots: LineageShot[]
}

/** An uploaded or generated reference asset used as input anywhere in the project. */
export interface LineageReferenceAsset {
  assetId: string
  url: string
  mimeType: string
  originalFilename: string | null
  source: 'uploaded' | 'generated'
  uploadedBy: string | null
  uploadedAt: string
  usedInShots: number[]
}

/** High-level counts shown in the summary card. */
export interface LineageSummary {
  totalScenes: number
  totalShots: number
  totalTakes: number
  orphanTakes: number
  modelCounts: Record<string, number>
  dateRange: { from: string; to: string } | null
  statusCounts: Partial<Record<GenerationStatus, number>>
}

/**
 * Canonical lineage graph for a project.
 * `contentHash` is SHA-256 over the canonical JSON form of this object,
 * with `contentHash` itself held at the empty string during hashing.
 */
export interface LineageTree {
  projectId: string
  projectName: string
  projectSlug: string
  organizationName: string | null
  generatedAt: string
  generatedByUserId: string
  generatedByUserName: string
  contentHash: string
  scenes: LineageScene[]
  orphanTakes: LineageTake[]
  references: LineageReferenceAsset[]
  summary: LineageSummary
}

/** What portion of the project a lineage export covers. */
export type LineageExportScope =
  { kind: 'full' } | { kind: 'scenes'; sceneIds: string[] } | { kind: 'dateRange'; from: string; to: string }

/** Export artifact format. */
export type LineageExportFormat = 'bundle' | 'html' | 'manifest'

/** Export job lifecycle status surfaced to the frontend. */
export type LineageExportStatus = 'pending' | 'assembling' | 'bundling' | 'uploading' | 'completed' | 'failed'

/** Row returned from the lineage export status endpoint. */
export interface LineageExportRecord {
  id: string
  projectId: string
  requestedByUserId: string | null
  scope: LineageExportScope
  format: LineageExportFormat
  includeTakeVideos: boolean
  status: LineageExportStatus
  generationCount: number | null
  contentHash: string | null
  bundleUrl: string | null
  bundleSizeBytes: number | null
  expiresAt: string | null
  createdAt: string
  errorMessage: string | null
}

// ============================================
// Billing Invoices
// ============================================

/**
 * Normalized invoice status — derived from Stripe's invoice.status.
 * Stored as a string union so the frontend can switch on it without importing Stripe types.
 */
export type InvoiceStatus = 'paid' | 'open' | 'void' | 'uncollectible' | 'draft'

/**
 * Invoice row returned to the frontend. Source data is Autumn's CustomerInvoice
 * (itself backed by Stripe), normalized for display.
 */
export interface NormalizedInvoice {
  /** Stripe invoice ID (used for download lookup). */
  stripeId: string
  /** Unix ms timestamp. */
  createdAt: number
  /** Human-readable description, e.g. "Pro plan — April 2026" or "Top-up". */
  description: string
  /** Amount in major currency units (dollars), matching Autumn's invoice shape. */
  amount: number
  /** ISO 4217 currency code, lowercase (matches Stripe). */
  currency: string
  /** Normalized status. */
  status: InvoiceStatus
  /** Stripe-hosted invoice page. Used as fallback link. */
  hostedUrl: string | null
}

// ============================================
// Organization Membership
// ============================================

/**
 * Organization member role. Mirrors the `organization_role` Postgres enum.
 * - `admin`: full management (members, settings, billing, credits)
 * - `member`: can use credits on team projects
 * - `billing`: billing-page access only — view invoices and top up credits.
 *   Enterprise-gated (finance / AP users).
 */
export type OrganizationRole = 'admin' | 'member' | 'billing'

export type OrganizationSecurityEventActorType = 'user' | 'backstage' | 'service' | 'anonymous'
/**
 * Facet used to filter the team security log. The table is append-only, so a
 * miscategorized event can never be corrected — see
 * `docs/security/security-event-taxonomy.md` for the decision ladder, the
 * boundaries that are easy to guess wrong (`permission` vs `data_modification`,
 * `security` vs `administration`), and what adding a new category requires.
 */
export type OrganizationSecurityEventCategory =
  | 'authentication'
  | 'administration'
  | 'account'
  | 'permission'
  | 'data_access'
  | 'data_modification'
  | 'data_deletion'
  | 'security'
export type OrganizationSecurityEventOutcome = 'success' | 'failure' | 'denied'
export type OrganizationSecurityEventSource = 'application' | 'supabase_auth' | 'partykit' | 'backstage'

/**
 * `source` is stored raw but never shown raw: `supabase_auth`, `partykit`, and `backstage` name
 * our vendors and internal tooling, which we don't disclose to team admins. Every read surface
 * (log table, filter, CSV export) renders through this map, so the customer only ever sees which
 * broad tier an event came from.
 */
export const SECURITY_EVENT_SOURCE_LABELS: Record<OrganizationSecurityEventSource, string> = {
  application: 'Martini app',
  supabase_auth: 'Backend database',
  partykit: 'Backend services',
  backstage: 'Backend services',
}

/**
 * The filter buckets an admin picks from. Several stored sources collapse into one bucket, so the
 * read API accepts a comma-separated `source` list rather than a single value.
 */
export interface OrganizationSecurityEventSourceGroup {
  value: string
  label: string
  sources: OrganizationSecurityEventSource[]
}

export const SECURITY_EVENT_SOURCE_GROUPS: OrganizationSecurityEventSourceGroup[] = [
  { value: 'martini_app', label: 'Martini app', sources: ['application'] },
  { value: 'backend_database', label: 'Backend database', sources: ['supabase_auth'] },
  { value: 'backend_services', label: 'Backend services', sources: ['partykit', 'backstage'] },
]

export interface OrganizationSecurityEvent {
  id: string
  occurredAt: string
  organizationId: string
  actorId: string | null
  actorType: OrganizationSecurityEventActorType
  actorLabel: string | null
  category: OrganizationSecurityEventCategory
  eventType: string
  outcome: OrganizationSecurityEventOutcome
  source: OrganizationSecurityEventSource
  resourceType: string | null
  resourceId: string | null
  projectId: string | null
  ip: string | null
  userAgent: string | null
  requestMethod: string | null
  requestPath: string | null
  statusCode: number | null
  metadata: Record<string, unknown>
}

export interface OrganizationSecurityEventPage {
  items: OrganizationSecurityEvent[]
  nextCursor: string | null
}

/**
 * Roles authorized for billing operations: viewing invoices and
 * purchasing credit top-ups. Plan lifecycle (cancel / resubscribe / upgrade)
 * remains admin-only.
 */
export const BILLING_ROLES: readonly OrganizationRole[] = ['admin', 'billing']

// ============================================
// Translator Route Coverage
// ============================================

/**
 * One route entry returned by `GET /i18n/route-keys`. Locale-agnostic
 * `hashes` come from `i18n_route_keys` (freshness-filtered); `completed`
 * fields come from `i18n_route_completion` for the requested locale.
 *
 * Frontend merges with bundle + active overrides to compute the
 * "translated/total" badge in the picker.
 */
export interface I18nRouteEntry {
  /** Normalized static route (locale prefix stripped, dynamic segments collapsed to `:id`). */
  route: string
  /** Message-key hashes seen on this route within the last 30 days. */
  hashes: string[]
  /** True iff `i18n_route_completion` has a row for `(locale, route)`. */
  completed: boolean
  /** Alias of the user who marked it complete, if available. */
  completedBy: string | null
  /** ISO timestamp of completion, if set. */
  completedAt: string | null
}

// ============================================
// Onboarding V2
// ============================================
// Single source of truth for the onboarding intent + one-tap survey answer
// values. The literal strings MUST stay identical to the SQL CHECK constraints
// in supabase/schemas/24_onboarding_state.sql and the PostHog/Loops property
// values (contracts C1/C3/C4/C9 in docs/plans/onboarding-v2-contracts.md).
// Intent set is founder-final (2026-06-10) — reshaped pre-remote-apply; see
// the contracts-doc amendment. 'other' added 2026-06-10 (founder dogfood):
// free-typing a brief without picking a chip auto-selects it, so Draft Shots
// can enable. Survey answers remain frozen at wave 0.

/** Intent chips offered on the brief step (S1), in display order. */
export const ONBOARDING_INTENTS = ['movie_trailer', 'short_drama', 'film', 'commercial', 'social', 'other'] as const
export type OnboardingIntent = (typeof ONBOARDING_INTENTS)[number]

/** Survey answer: where the user works. */
export const ONBOARDING_WORKPLACES = ['agency', 'studio', 'freelance', 'personal', 'school'] as const
export type OnboardingWorkplace = (typeof ONBOARDING_WORKPLACES)[number]

/** Survey answer: team size. */
export const ONBOARDING_TEAM_SIZES = ['solo', '2_10', '11_50', '50_plus'] as const
export type OnboardingTeamSize = (typeof ONBOARDING_TEAM_SIZES)[number]

/** Survey answer: prior AI video experience. */
export const ONBOARDING_AI_EXPERIENCES = ['first_time', 'tried_few', 'power_user'] as const
export type OnboardingAiExperience = (typeof ONBOARDING_AI_EXPERIENCES)[number]

/**
 * Survey answer: how the user heard about Martini. Multi-select (2026-06-10):
 * the column is text[] with a per-element CHECK against these literals; the
 * PostHog/Loops mirrors flatten the array to a comma-joined string.
 */
export const ONBOARDING_REFERRAL_CHANNELS = ['tiktok_ig', 'youtube', 'x', 'friend', 'search', 'other'] as const
export type OnboardingReferralChannel = (typeof ONBOARDING_REFERRAL_CHANNELS)[number]

export type OnboardingPresetMoveId = 'stopmotion-couple' | 'garden-friends' | 'portrait-closeup'

export interface CameraMoveFingerprintInput {
  keyframes: Array<{
    timeSeconds: number
    pose: {
      position: { x: number; y: number; z: number }
      quaternion: { x: number; y: number; z: number; w: number }
      fov: number
    }
    ease?: { preset?: string }
    tension?: number
  }>
  aspectRatio?: string
  speedId?: string
  mode?: string
  defaults?: { tension?: number }
}

/**
 * Stable identity for a curated camera move. Includes ordered keyframe timing,
 * pose, easing, and effective tension plus the path's framing and playback
 * mode. Keyframe ids and source labels are deliberately excluded because the
 * editor may regenerate them.
 */
export function fingerprintCameraMove(input: CameraMoveFingerprintInput): string {
  const rounded = (value: number): string => {
    const normalized = Math.round(value * 1000) / 1000
    return (Object.is(normalized, -0) ? 0 : normalized).toFixed(3)
  }
  const defaultTension = input.defaults?.tension ?? 0
  return [
    input.aspectRatio ?? '16:9',
    input.speedId ?? 'ease-in-out',
    input.mode === 'smooth' ? 'smooth' : 'exact',
    input.keyframes
      .map(keyframe => {
        const { position, quaternion, fov } = keyframe.pose
        return [
          rounded(keyframe.timeSeconds),
          [position.x, position.y, position.z].map(rounded).join(','),
          [quaternion.x, quaternion.y, quaternion.z, quaternion.w].map(rounded).join(','),
          rounded(fov),
          keyframe.ease?.preset ?? '',
          rounded(keyframe.tension ?? defaultTension),
        ].join('~')
      })
      .join(';'),
  ].join('|')
}

/**
 * KEEP IN SYNC with MOTION_PRESET_PATHS. The frontend node test fails when a
 * curated seed changes without updating its server-side fingerprint.
 */
export const ONBOARDING_PRESET_MOVE_FINGERPRINTS: Record<OnboardingPresetMoveId, string> = {
  'stopmotion-couple':
    '16:9|ease-in-out|exact|0.000~0.000,0.000,0.000~0.000,0.000,0.000,1.000~27.839~custom~0.000;0.685~0.000,0.000,0.000~0.000,0.000,0.000,1.000~27.839~ease-in-out~0.000;5.000~0.089,-0.044,-0.291~0.115,0.251,-0.030,0.961~27.839~~0.000',
  'garden-friends':
    '16:9|ease-in-out|exact|0.000~0.000,0.000,0.000~0.000,0.000,0.000,1.000~27.839~custom~0.000;0.685~0.000,0.000,0.000~0.000,0.000,0.000,1.000~27.839~ease-in-out~0.000;5.000~0.089,-0.044,-0.291~0.115,0.251,-0.030,0.961~27.839~~0.000',
  'portrait-closeup':
    '16:9|ease-in-out|exact|0.000~0.000,0.000,0.000~0.000,0.000,0.000,1.000~42.151~ease-in-out~0.000;5.000~0.081,-0.004,-0.294~0.025,0.066,-0.002,0.998~37.800~~0.000',
}

// ============================================
// Affiliate Program
// ============================================
// Status literals are the single source of truth and must stay identical to
// the column comments in supabase/schemas/29_affiliates.sql. Scheme (locked):
// 25% of net subscription revenue (30% at >=25 active referrals), 12-month
// reward window per referred customer. Cash uses Dub's 60-day holdback and
// monthly payouts; designated partners receive manual monthly Martini olive
// credits. Subscriptions only — rewards never attach to olive top-ups,
// signups, or clicks.

export const AFFILIATE_STATUSES = ['applied', 'approved', 'suspended', 'terminated'] as const
export type AffiliateStatus = (typeof AFFILIATE_STATUSES)[number]

export const AFFILIATE_REFERRAL_STATUSES = ['attributed', 'active', 'expired', 'voided'] as const
export type AffiliateReferralStatus = (typeof AFFILIATE_REFERRAL_STATUSES)[number]

export const AFFILIATE_VOID_REASONS = ['self_referral', 'fraud', 'refund_window'] as const
export type AffiliateVoidReason = (typeof AFFILIATE_VOID_REASONS)[number]

export const AFFILIATE_REWARD_TYPES = ['cash', 'olives'] as const
export type AffiliateRewardType = (typeof AFFILIATE_REWARD_TYPES)[number]

/** Default commission rate: 25% of net subscription revenue. */
export const AFFILIATE_DEFAULT_COMMISSION_BPS = 2500
/** Performance tier: 30% once an affiliate has >= AFFILIATE_TIER_THRESHOLD active referrals. */
export const AFFILIATE_TIER_COMMISSION_BPS = 3000
export const AFFILIATE_TIER_THRESHOLD = 25
/** Reward window per referred customer, in months (capped, not lifetime). */
export const AFFILIATE_REWARD_WINDOW_MONTHS = 12

/** Body for the public application form (POST /affiliates/apply). */
export interface AffiliateApplicationInput {
  displayName: string
  contactEmail: string
  channelUrls: string[]
  applicationNote?: string
  /** Must be true — the form requires accepting the program agreement. */
  agreementAccepted: boolean
}

/** What a logged-in user sees about their own partner standing (Settings → Partner program). */
/**
 * Net commission reported to Dub, from our own audit mirror (clawbacks are
 * negative rows, so these are already net of refunds). NOT a payout balance —
 * Dub owns the 60-day holdback, the balance, and the payout schedule.
 */
export interface AffiliateCashEarnings {
  type: 'cash'
  lifetimeCents: number
  last30dCents: number
}

export interface AffiliateOliveEarnings {
  type: 'olives'
  earnedOlives: number
  availableOlives: number
  creditedOlives: number
  /** Cash commissions earned before the one-way cutover remain in Dub. */
  hasHistoricalCashEarnings: boolean
}

export type AffiliateEarnings = AffiliateCashEarnings | AffiliateOliveEarnings

export interface AffiliateSelfView {
  status: AffiliateStatus
  rewardType: AffiliateRewardType
  slug: string | null
  appliedAt: string
  approvedAt: string | null
  commissionRateBps: number
  /** Filmmakers bound to this partner (active = currently subscribed referrals). */
  referralCounts: { total: number; active: number }
  /**
   * null = the ledger read failed, or the partner isn't approved yet. Render
   * "unavailable" — never a zero, which would read as "you've earned nothing".
   */
  earnings: AffiliateEarnings | null
}

/** Admin list/detail view of an affiliate (backstage queue). */
export interface AffiliateAdminView {
  id: string
  displayName: string
  contactEmail: string
  channelUrls: string[]
  status: AffiliateStatus
  applicationNote: string | null
  promoCampaignId: string | null
  promoCampaignSlug: string | null
  dubPartnerId: string | null
  commissionRateBps: number
  rewardType: AffiliateRewardType
  rewardOrganizationId: string | null
  rewardTypeEffectiveAt: string | null
  rewardTermsAcceptedAt: string | null
  agreementAcceptedAt: string | null
  approvedAt: string | null
  notes: string | null
  createdAt: string
  referralCounts: { total: number; active: number; voided: number }
}

export interface AffiliateRewardDestinationCandidate {
  organizationId: string
  organizationName: string
  userId: string
  email: string | null
  source: 'linked_user' | 'contact_email'
}

/** Admin view of one referred organization. */
export interface AffiliateReferralAdminView {
  id: string
  affiliateId: string
  organizationId: string
  attributedAt: string
  firstPaidAt: string | null
  rewardWindowEndsAt: string | null
  status: AffiliateReferralStatus
  voidReason: AffiliateVoidReason | null
}

/** Admin view of one commission-log entry (our audit mirror of Dub). */
export interface AffiliateCommissionLogEntry {
  id: string
  affiliateId: string
  referralId: string
  invoiceId: string
  kind: 'sale' | 'clawback'
  amountCents: number
  commissionCents: number
  commissionRateBps: number
  rewardType: AffiliateRewardType
  rewardOlives: number | null
  invoiceSettledAt: string | null
  olivePayoutKey: string | null
  olivePayoutClaimedAt: string | null
  oliveGrantedAt: string | null
  status: 'pending' | 'reported' | 'available' | 'payout_pending' | 'payout_review' | 'credited' | 'absorbed' | 'voided'
  voidReason: string | null
  reportedToDubAt: string | null
  createdAt: string
}

export interface AffiliateOlivePayoutBatch {
  payoutKey: string
  status: 'payout_pending' | 'payout_review' | 'credited'
  olives: number
  rowCount: number
  claimedAt: string | null
  grantedAt: string | null
  stale: boolean
  reviewReason:
    | 'definite_rejection_release_failed'
    | 'unknown_grant_outcome'
    | 'autumn_granted_audit_write_failed'
    | 'credited_in_autumn_ledger_finalize_failed'
    | null
  providerConfirmedGranted: boolean
}

export interface AffiliateAbsorbedRefundEvent {
  invoiceId: string
  type: 'refund' | 'chargeback'
  date: string
  olives: number
  amountCents: number
}

export interface AffiliateOliveSummary {
  cutoff: string
  earnedOlives: number
  availableOlives: number
  availableRowCount: number
  creditedOlives: number
  reviewRequiredOlives: number
  absorbedOlives: number
  absorbedAmountCents: number
  absorbedEventCount: number
  historicalCashCents: number
  payoutBatches: AffiliateOlivePayoutBatch[]
  absorbedEvents: AffiliateAbsorbedRefundEvent[]
}

export interface AffiliateOliveProgramSummary {
  absorbedOlives: number
  absorbedAmountCents: number
  absorbedEventCount: number
}

// ============================================================================
// Library (Collections) — wire contract
// ============================================================================
// Shared 1:1 with frontend/src/store/libraryStore.ts (which re-exports these).
// Ids are client-generated short text ids (plus deterministic 'smp-*' sample
// ids) — unique per workspace only. Timestamps are unix ms on the wire;
// Postgres stores timestamptz (converted in backend/src/library/serialize.ts).
// See spec/48-library-data-architecture.md and supabase/schemas/30_library.sql.

export type LibraryAssetKind = 'image' | 'video' | 'model3d' | 'audio' | 'text'
export type LibrarySourceKind = 'upload' | 'import' | 'generation'
export type LibraryVersionSource = 'initial' | 'replace' | 'restore' | 'generation' | 'upload' | 'import'

/** One immutable entry in an asset's append-only media history. */
export type LibAssetVersion = {
  id: string
  assetId: string
  versionNumber: number
  kind: LibraryAssetKind
  src: string
  srcLarge?: string
  posterSrc?: string
  r2Key?: string
  posterR2Key?: string
  originalFilename?: string
  width?: number
  height?: number
  sizeBytes?: number
  /** The prompt-fragment body for kind='text' versions (src is '' for those). */
  textValue?: string
  source: LibraryVersionSource
  generationId?: string
  createdBy?: string
  createdAt: number
}

/** What a generation referenced from the library — captured into library_links. */
export type LibraryRef = {
  collectionId: string
  assetId?: string
  /** The asset's currentVersionId at reference time — what makes staleness computable. */
  versionId?: string
}

export type LibAsset = {
  id: string
  collectionId: string
  kind: LibraryAssetKind
  /** https URL (persisted) or `idb:<assetId>` (upload in flight, browser-local). */
  src: string
  /** Uploader-private IndexedDB blob pointer; irrelevant once src is https. */
  blobKey?: string
  /** Native-aspect variant when it differs from src (sample imagery). */
  srcLarge?: string
  /** Display still for non-image media (video poster) — grids render <img>. */
  posterSrc?: string
  /** R2 object key for library uploads (org-prefixed; null for captured/sample). */
  r2Key?: string
  posterR2Key?: string
  name: string
  originalFilename?: string
  width?: number
  height?: number
  sizeBytes?: number
  sourceKind?: LibrarySourceKind
  /**
   * propertyKey → typed value, keyed by the property's stable `key`. Shape
   * depends on the property `type`:
   *   select    → optionId (string; label resolves via the vocabulary)
   *   text      → string (literal)
   *   number    → number
   *   checkbox  → boolean
   * Absent key = unset. Select still stores option IDS (never labels) so an
   * option rename is a one-row edit (spec 48 §4).
   */
  attrs: Record<string, AttrValue>
  /** Prompt-fragment body for kind='text' assets (src is '' for those). */
  textValue?: string
  /** Where the pixels were captured FROM (canvas capture only). */
  origin?: { projectId: string; sceneId?: string; assetId?: string }
  /** The version the flat media fields mirror. '<id>-v1' is the history root. */
  currentVersionId?: string
  rev: number
  createdBy?: string
  updatedBy?: string
  createdAt: number
  updatedAt: number
  deletedAt?: number
}

export type LibCollection = {
  id: string
  name: string
  /** Stable kebab slug — the future SQL/table name (spec §7 naming contract). */
  slug: string
  /** Folder path: 'Night Shift' or 'Night Shift/Cast'. null = unfiled. */
  folder: string | null
  tags: string[]
  heroAssetId: string | null
  /** Free-form notes about the collection, shown under its title. */
  description?: string
  /** Sample-catalog collections keep mock usage; user ones don't. */
  sample?: boolean
  usedIn: number
  film?: string
  rev: number
  createdBy?: string
  updatedBy?: string
  createdAt: number
  updatedAt: number
  deletedAt?: number
}

export type LibraryPropertyOption = {
  id: string
  /** Display label ('Happy'). Renaming touches only this row. */
  value: string
  color?: string
}

/** The user-visible field types (spec 49). Widening this is an additive
    migration (a Postgres check-constraint + this union) — multi_select, date,
    rating etc. are later phases. */
export type LibraryPropertyType = 'select' | 'text' | 'number' | 'checkbox'

/**
 * A single asset attribute value. The runtime shape is dictated by the owning
 * property's `type` (see LibAsset.attrs). `null`/absent means unset.
 *   string  → select optionId, text
 *   number  → number
 *   boolean → checkbox
 */
export type AttrValue = string | number | boolean

/** Type-specific property settings (spec 49). Only the keys relevant to the
    property's `type` are read; unknown keys are ignored. */
export type LibraryPropertyConfig = {
  /** text: render as a multi-line textarea. */
  multiline?: boolean
  /** number: fixed decimal places (0 = integer). */
  precision?: number
}

export type LibraryProperty = {
  id: string
  /** Stable slug key ('mood') — the attrs key; never renamed. */
  key: string
  /** The user's word ('Mood'); renamed freely. */
  label: string
  type: LibraryPropertyType
  /** Option vocabulary — only populated for select / multi_select. */
  options: LibraryPropertyOption[]
  /** Type-specific settings; absent = defaults. */
  config?: LibraryPropertyConfig
  sortOrder: number
  deletedAt?: number
}

export type LibraryViewLayout = 'gallery' | 'table' | 'matrix'

/** A saved way of looking: serializable, rename-proof (property keys + option ids). */
export type LibraryViewDefinition = {
  /** Collection scope (v1 views are per-collection). */
  collectionId: string
  layout: LibraryViewLayout
  /** propertyKey → allowed optionIds (empty/absent = no filter). */
  filters?: Record<string, string[]>
  groupBy?: string
  sort?: { by: 'name' | 'createdAt' | string; dir: 'asc' | 'desc' }
  /** Matrix axes: two property keys. */
  axes?: { x: string; y: string }
}

export type LibView = {
  id: string
  name: string
  definition: LibraryViewDefinition
  rev: number
  createdAt: number
  updatedAt: number
  deletedAt?: number
}

export type LibraryAssetUpdatePatch = Partial<{
  src: string
  blobKey: string | null
  srcLarge: string | null
  posterSrc: string | null
  r2Key: string | null
  posterR2Key: string | null
  kind: LibraryAssetKind
  originalFilename: string | null
  width: number | null
  height: number | null
  sizeBytes: number | null
  sourceKind: LibrarySourceKind | null
  textValue: string | null
}>

/** The command seam — mirrors the store executor 1:1. */
export type LibraryCommand =
  | { type: 'collection.create'; collection: LibCollection }
  | { type: 'collection.rename'; id: string; name: string }
  | { type: 'collection.delete'; id: string }
  | { type: 'collection.restore'; id: string }
  | { type: 'collection.move'; id: string; folder: string | null }
  | { type: 'collection.setTags'; id: string; tags: string[] }
  | { type: 'collection.setHero'; id: string; assetId: string | null }
  | { type: 'collection.setDescription'; id: string; description: string }
  | { type: 'folder.create'; path: string }
  | { type: 'folder.rename'; path: string; next: string }
  | { type: 'folder.delete'; path: string; restoreCollections?: Record<string, string | null> }
  | { type: 'asset.create'; asset: LibAsset }
  | { type: 'asset.rename'; id: string; name: string }
  | { type: 'asset.move'; ids: string[]; collectionId: string; perAssetTarget?: Record<string, string> }
  | { type: 'asset.delete'; ids: string[] }
  | { type: 'asset.restore'; ids: string[] }
  | { type: 'asset.setValue'; id: string; key: string; value: AttrValue | null }
  | {
      type: 'asset.update'
      id: string
      patch: LibraryAssetUpdatePatch
      prev?: Record<string, unknown>
      /** Present when the patch replaces media: the client-minted id of the
          version this write appends (idempotent retries; executor parity). */
      versionId?: string
    }
  /** Move the current pointer to an existing version (history restore / undo
      of replace-media). Copies that version's media onto the flat columns —
      never appends, never rewrites history. */
  | { type: 'asset.setCurrentVersion'; id: string; versionId: string }
  | { type: 'property.create'; property: LibraryProperty }
  | { type: 'property.rename'; key: string; label: string }
  /** Change a property's field type in place. Stored values are KEPT and
      re-interpreted (readers are shape-tolerant; mismatched leftovers display
      raw until edited); the option vocabulary stays dormant off-select. */
  | { type: 'property.setType'; key: string; propertyType: LibraryPropertyType; config?: LibraryPropertyConfig }
  | { type: 'property.delete'; key: string }
  | { type: 'property.restore'; key: string }
  | { type: 'property.addOption'; key: string; option: LibraryPropertyOption }
  | { type: 'property.removeOption'; key: string; optionId: string }
  | { type: 'property.renameOption'; key: string; optionId: string; value: string }
  | { type: 'tag.addToVocab'; tags: string[] }
  | { type: 'view.save'; view: LibView }
  | { type: 'view.update'; id: string; name?: string; definition?: LibraryViewDefinition }
  | { type: 'view.delete'; id: string }
  | { type: 'link.setProject'; projectId: string; collectionIds: string[] }
  | { type: 'batch'; label: string; commands: LibraryCommand[] }

/** GET /library/:orgId response. Includes soft-deleted rows (stable-id revive). */
export type LibrarySnapshot = {
  /** Echo of the workspace alias the client asked for ('personal' | orgId). */
  workspaceId: string
  /** The resolved org uuid. */
  orgId: string
  collections: LibCollection[]
  assets: LibAsset[]
  /** Append-only media history for every asset (mostly one row each). */
  versions: LibAssetVersion[]
  folders: string[]
  tagVocab: string[]
  properties: LibraryProperty[]
  views: LibView[]
  projectLinks: Record<string, string[]>
  /** created_by/updated_by uuid → profile alias, for attribution rows. */
  members?: Record<string, string>
}

/**
 * GET /library/community/snapshot — the published community library: a live,
 * scrubbed snapshot of the source workspace (COMMUNITY_LIBRARY_ORG_ID env).
 * Recipients copy it into their own workspace as sample rows; assets keep
 * their public src URLs but never the source org's R2 keys, canvas origins,
 * or author ids.
 */
export type CommunityLibrarySnapshot = {
  collections: LibCollection[]
  assets: LibAsset[]
  folders: string[]
  tagVocab: string[]
  properties: LibraryProperty[]
}

/** POST /library/:orgId/uploads — presigned PUT init, one entry per file. */
export type LibraryUploadFileRequest = {
  assetId: string
  filename: string
  contentType: string
  sizeBytes: number
  /** Client-captured poster still (video first frame), uploaded alongside. */
  poster?: { contentType: string; sizeBytes: number }
}
export type LibraryUploadFileInit = {
  assetId: string
  key: string
  /** null when the backend runs without R2 (local dev) — use /uploads/direct. */
  uploadUrl: string | null
  publicUrl: string
  directUploadToken?: string
  poster?: { key: string; uploadUrl: string | null; publicUrl: string; directUploadToken?: string }
  directUpload: boolean
}
export type LibraryUploadsResponse = { files: LibraryUploadFileInit[] }

// =============================================================================
// Comments (canvas + timeline review threads)
// =============================================================================

/**
 * What a comment is about — never a resolved position. Pins re-resolve from
 * the live doc every render (node moved → pin follows; clip trimmed → marker
 * rides). When the referent disappears the UI freezes the last projection and
 * badges the thread (node) or falls back to `fallbackTicks` (clip).
 */
export type CommentAnchor =
  /** Pinned to a spot on the board (world coordinates). */
  | { type: 'canvas'; sceneId: string; x: number; y: number }
  /**
   * Pinned to a node; u/v are normalized offsets within the node bounds so the
   * pin survives resizes. fx/fy capture the world position at creation — the
   * frozen-pin fallback when the node is later deleted.
   */
  | { type: 'node'; sceneId: string; nodeId: string; u: number; v: number; fx?: number; fy?: number }
  /**
   * Pinned to a moment inside a clip (offset from clip start, in ticks).
   * Rides trims/moves/reorders because clip ids are stable; `fallbackTicks`
   * (absolute sequence time at creation) + `assetId` let the UI degrade
   * gracefully when the clip is deleted or the timeline is rebuilt.
   */
  | {
      type: 'clip'
      sequenceId: string
      clipId: string
      offsetTicks: number
      fallbackTicks: number
      assetId: string
      /** Range comments: duration of the marked span, in ticks. */
      rangeTicks?: number
    }
  /** Pinned to absolute sequence time (about the cut/moment, not any clip). */
  | { type: 'time'; sequenceId: string; ticks: number; rangeTicks?: number }

/**
 * Mention target stored on a comment. Only people are mentionable — agents act
 * on behalf of the user they authed as, so you @ the person and their connected
 * agent picks the work up as them (get_comments mentions:'me' matches by userId).
 */
export type CommentMention = { kind: 'user'; userId: string }

/**
 * A single comment. Threads are flat: the root has threadId === id and owns
 * the anchor + resolved state; replies share the root's threadId and carry
 * neither. Canonical store is Supabase; the project Yjs doc holds a mirror
 * (written only by the backend) for real-time delivery.
 */
export interface CommentRecord {
  id: string
  projectId: string
  threadId: string
  authorUserId: string | null
  /** Engine-owned run identity; mutually exclusive with a human author. */
  authorRunId?: string | null
  /** MCP client name (e.g. 'claude-code') when authored via an agent; null from the app. */
  authorClient?: string | null
  body: string
  mentions: CommentMention[]
  /** Root comments only; replies inherit the thread root's anchor. */
  anchor?: CommentAnchor | null
  resolvedAt?: string | null
  resolvedBy?: string | null
  createdAt: string
  editedAt?: string | null
  /** Mirror tombstone: soft-deleted comments keep the record, lose the body. */
  deleted?: boolean
}

/** POST /projects/:projectId/comments */
export interface CreateCommentRequest {
  /** Client-generated UUID for optimistic render + idempotent retry; server mints one if absent. */
  id?: string
  /** Present = reply to that thread; absent = new thread (anchor required). */
  threadId?: string
  body: string
  /** @deprecated Put user references in body; accepted for older clients. */
  mentions?: CommentMention[]
  anchor?: CommentAnchor
}

export const COMMENT_REF_KINDS = ['user', 'asset', 'subject', 'bin', 'annotation', 'variable'] as const
export type CommentRefKind = (typeof COMMENT_REF_KINDS)[number]
/** Hard cap per comment — bounds notifier fan-out and render work. */
export const COMMENT_REF_MAX = 50

export interface CommentRef {
  kind: CommentRefKind
  id: string
  label: string
  /** The exact token text, e.g. '@[asset-…:Shot 004]'. */
  raw: string
  start: number
  end: number
}
export type CommentSegment = { type: 'text'; text: string } | { type: 'ref'; ref: CommentRef }

const COMMENT_REF_RE =
  /@\[(?:(user|asset|subject|bin|annotation|variable)-([A-Za-z0-9_-]{1,64})|([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})):([^\]\r\n]{1,120})\]/gi
const COMMENT_REF_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Tokenise a comment body. Unknown kinds and malformed tokens stay text. Parses at most COMMENT_REF_MAX refs. */
export function parseCommentBody(body: string): CommentSegment[] {
  const segments: CommentSegment[] = []
  let cursor = 0
  let count = 0
  for (const match of body.matchAll(COMMENT_REF_RE)) {
    if (count >= COMMENT_REF_MAX) break
    const start = match.index
    const end = start + match[0].length
    if (start > cursor) segments.push({ type: 'text', text: body.slice(cursor, start) })
    segments.push({
      type: 'ref',
      ref: {
        kind: match[1] ? (match[1].toLowerCase() as CommentRefKind) : 'asset',
        id: match[2] ?? match[3],
        label: match[4],
        raw: match[0],
        start,
        end,
      },
    })
    cursor = end
    count += 1
  }
  if (cursor < body.length) segments.push({ type: 'text', text: body.slice(cursor) })
  return segments
}

export function commentRefsOf(body: string): CommentRef[] {
  return parseCommentBody(body).flatMap(segment => (segment.type === 'ref' ? [segment.ref] : []))
}

/**
 * Timeline time base — the ONE definition. The frontend's `lib/constants.ts`
 * and the MCP timeline tools derive from these; nothing else may restate them.
 */
export const TIMELINE_TICKS_PER_SECOND = 48000
export const TIMELINE_FPS = 24
export const TIMELINE_TICKS_PER_FRAME = TIMELINE_TICKS_PER_SECOND / TIMELINE_FPS

/**
 * Read-only timeline projection for the phone (GET /mobile/v1/sequences/:id).
 * Nested sequences are flattened server-side; all times are timeline ticks.
 */
/** Wire shape of a clip volume point — mirrors VolumePoint in types/timeline-editing.ts. */
export interface MobileVolumePoint {
  time: number
  gainDb: number | null
  curve?: 'linear' | 'hold'
}
export type MobileSequenceClipKind = 'video' | 'image' | 'audio'
export interface MobileSequenceTrack {
  id: string
  index: number
  type: 'video' | 'audio'
  name: string
  muted: boolean
  solo: boolean
  /** False when the editor hid the track (video `isVisible: false`); the phone skips its clips. */
  visible: boolean
  /**
   * The phone must not edit this track's clips. True for either of two reasons: the editor locked the
   * track (an edit is refused with TRACK_LOCKED), or the track is a nested sequence's sound shown
   * through this sequence (`id` like `<clip>/track-<n>`): it is unlocked in its own sequence and edited
   * there, never through the parent.
   */
  locked: boolean
  gainDb?: number
}
export interface MobileSequenceClip {
  id: string
  trackId: string
  trackIndex: number
  kind: MobileSequenceClipKind
  assetId: string | null
  startTicks: number
  durationTicks: number
  trimInTicks: number
  trimOutTicks: number
  speed: number
  gainDb?: number
  fadeInTicks?: number
  fadeOutTicks?: number
  volumePoints?: MobileVolumePoint[]
  loop?: boolean
  preservePitch?: boolean
  /** Ids of the nested-sequence clips this clip was flattened through (outermost first); absent for root clips. */
  nestedPath?: string[]
  /** Root clips only: echo it in an edit's `base.clips` so the edit is refused if this clip changed meanwhile. */
  version?: string
  /** Root clips only: shared by a picture and its sound. Re-creating the clip should re-create the sound. */
  linkGroupId?: string
}
export interface MobileSequenceAsset {
  id: string
  uri: string
  proxyUri?: string
  thumbUri?: string
  mimeType: string
  assetType?: string
  durationSeconds?: number
  width?: number
  height?: number
  shotNumber?: number
  displayName?: string
}
export interface MobileSequenceComment {
  id: string
  threadId: string
  clipId: string | null
  ticks: number
  rangeTicks?: number
  resolved: boolean
  authorUserId: string | null
  createdAt: string
  preview: string
}
export interface MobileSequenceListItem {
  id: string
  name: string
  durationTicks: number
  clipCount: number
  revision: string
}

export interface MobileSequenceList {
  sequences: MobileSequenceListItem[]
}

export interface MobileSequencePayload {
  sequence: {
    id: string
    name: string
    durationTicks: number
    fps: number
    ticksPerSecond: number
    frame?: { width: number; height: number }
  }
  tracks: MobileSequenceTrack[]
  clips: MobileSequenceClip[]
  assets: Record<string, MobileSequenceAsset>
  comments: MobileSequenceComment[]
  revision: string
}

export interface CommentTimecode {
  raw: string
  start: number
  end: number
  ticks: number
}

/** Find timecodes in a comment body. Never inside @[…] ref tokens. */
export function parseCommentTimecodes(body: string): CommentTimecode[] {
  const excluded = [
    ...commentRefsOf(body),
    ...Array.from(body.matchAll(/\b(?:[a-z][a-z\d+.-]*:\/\/|www\.)[^\s<>]+/gi), match => ({
      start: match.index,
      end: match.index + match[0].length,
    })),
  ]
  const timecodes: CommentTimecode[] = []
  // Consume whole numeric candidates before validating so malformed fields
  // cannot turn into shorter, valid timecodes by regex backtracking.
  for (const match of body.matchAll(/(?<![\w:.])(?:\d+(?::\d+){1,3}(?:\.\d+)?|\d+\.\d+s)(?![\w:])/g)) {
    const raw = match[0]
    const start = match.index
    const end = start + raw.length
    if (/^\s*(?:am|pm)\b/i.test(body.slice(end))) continue
    if (excluded.some(span => start < span.end && end > span.start)) continue

    let seconds: number
    let frames = 0
    if (raw.endsWith('s')) {
      if (!/^\d+\.\d{1,3}s$/.test(raw)) continue
      seconds = Number(raw.slice(0, -1))
    } else {
      const fields = raw.split(':')
      const values = fields.map(Number)
      if (!/^\d{1,2}$/.test(fields[0])) continue
      if (fields.length === 2) {
        if (!/^\d{2}(?:\.\d{1,3})?$/.test(fields[1]) || values[0] > 59 || values[1] >= 60) continue
        seconds = values[0] * 60 + values[1]
      } else {
        if (fields.slice(1).some(field => !/^\d{2}$/.test(field))) continue
        if (fields.length === 3) {
          // Three fields are always MM:SS:FF (what the ruler prints). An out-of-range
          // frame count is not a timecode — never a silent H:MM:SS reinterpretation.
          if (values[0] > 59 || values[1] > 59 || values[2] >= TIMELINE_FPS) continue
          seconds = values[0] * 60 + values[1]
          frames = values[2]
        } else {
          if (values[1] > 59 || values[2] > 59 || values[3] >= TIMELINE_FPS) continue
          seconds = values[0] * 3600 + values[1] * 60 + values[2]
          frames = values[3]
        }
      }
    }
    const ticks = Math.round(seconds * TIMELINE_TICKS_PER_SECOND) + frames * TIMELINE_TICKS_PER_FRAME
    if (!Number.isSafeInteger(ticks)) continue
    timecodes.push({ raw, start, end, ticks })
    if (timecodes.length === 100) break
  }
  return timecodes
}

/** MM:SS:FF at the timeline frame rate — the one timecode formatter (the frontend's formatTimecodeFromTicks delegates here). */
export function formatCommentTimecode(ticks: number): string {
  const totalFrames = Math.round(ticks / TIMELINE_TICKS_PER_FRAME)
  const frames = totalFrames % TIMELINE_FPS
  const totalSeconds = Math.floor(totalFrames / TIMELINE_FPS)
  const seconds = totalSeconds % 60
  const minutes = Math.floor(totalSeconds / 60)
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}:${String(frames).padStart(2, '0')}`
}

/** Write validation must detect the first ref beyond the read-time parsing cap. */
export function hasTooManyCommentRefs(body: string): boolean {
  const matches = body.matchAll(COMMENT_REF_RE)
  for (let count = 0; count <= COMMENT_REF_MAX; count += 1) {
    if (matches.next().done) return false
  }
  return true
}

/** Build a token with a single-line label, capped at 120 chars. Empty labels fall back to the id. */
export function formatCommentRef(kind: CommentRefKind, id: string, label: string): string {
  const safeLabel = label.replace(/\s+/g, ' ').replace(/\]/g, ')').trim().slice(0, 120) || id
  return `@[${kind}-${id}:${safeLabel}]`
}

/** Tokens → '@Label' for email, notifications, previews. */
export function stripCommentRefs(body: string): string {
  return parseCommentBody(body)
    .map(segment => (segment.type === 'ref' ? `@${segment.ref.label}` : segment.text))
    .join('')
}

/** Kinds that look like tokens but aren't allowed, deduped and lowercase. */
export function unknownCommentRefKinds(body: string): string[] {
  const unknown = new Set<string>()
  for (const match of body.matchAll(/@\[([a-z]+)-([A-Za-z0-9_-]{1,64}):[^\]\r\n]{1,120}\]/gi)) {
    const kind = match[1].toLowerCase()
    // A legacy bare UUID can start with only letters (e.g. deadbeef-…).
    if (COMMENT_REF_UUID_RE.test(`${match[1]}-${match[2]}`)) continue
    if (!(COMMENT_REF_KINDS as readonly string[]).includes(kind)) unknown.add(kind)
  }
  return [...unknown]
}

// =============================================================================
// Topaz model limits
// =============================================================================

/**
 * Constraints Topaz enforces server-side and rejects the whole request for.
 *
 * Every entry here is backed by an observed production rejection — we do not
 * guess limits, because an invented ceiling blocks work that would have
 * succeeded. The comment on each records the error Topaz returned.
 *
 * Checked in the upscale modal (so the user sees it before spending time and
 * olives) and re-checked in the handler (so presets, reused settings and MCP
 * callers can't bypass it).
 */

/** Starlight video models. Shared so the limit table and the UI agree on the family. */
export const STARLIGHT_VIDEO_MODEL_CODES = [
  'sl-1',
  'slp-2',
  'slp-2.5',
  'slhq-1',
  'slm-1',
  'wonder-1',
  'slf-1',
  'slf-2',
] as const

export function isStarlightVideoModel(model: string): boolean {
  return (STARLIGHT_VIDEO_MODEL_CODES as readonly string[]).includes(model)
}

/**
 * Hyperion (SDR→HDR) video models. Shared so the 4K output cap, "HDR by model"
 * labeling, and pricing all agree on the family.
 */
export const HYPERION_VIDEO_MODEL_CODES = ['hyp-1', 'hyp-2', 'hyp-2.5'] as const

export function isHyperionVideoModel(model: string): boolean {
  return (HYPERION_VIDEO_MODEL_CODES as readonly string[]).includes(model)
}

/**
 * Hyperion models Topaz bills per frame (~13 frames/credit at 1080p, ~6 at 4K) —
 * an order of magnitude above Hyperion 1's duration rate — priced on the
 * `topaz-video-enhance-hyperion` tier.
 */
export const HYPERION_PER_FRAME_MODEL_CODES = ['hyp-2', 'hyp-2.5'] as const

export function isHyperionPerFrameModel(model: string): boolean {
  return (HYPERION_PER_FRAME_MODEL_CODES as readonly string[]).includes(model)
}

/**
 * `grain` range. The enhancement models reject above 0.1 with
 * "grain must be a float between 0 and 0.1"; Starlight accepts the full 0-1
 * range (16 completed jobs at 0.5 on slp-2.5 / sl-1, no grain 400 ever).
 */
export const TOPAZ_GRAIN_MAX_ENHANCE = 0.1
export const TOPAZ_GRAIN_MAX_STARLIGHT = 1

export function getTopazGrainMax(model: string): number {
  return isStarlightVideoModel(model) ? TOPAZ_GRAIN_MAX_STARLIGHT : TOPAZ_GRAIN_MAX_ENHANCE
}

/**
 * Max input frames for Starlight.
 *
 * Observed: "input_frame_count (1328) needs to be <= 450 for slp-2 model".
 * Topaz named slp-2, but no Starlight job above 450 frames has ever completed
 * (best observed: slf-2 and slp-2 at ~363), so the cap is applied family-wide.
 * If a specific model turns out to allow more, give it an entry here.
 */
export const TOPAZ_STARLIGHT_MAX_INPUT_FRAMES = 450

/**
 * Image upscale factor range.
 * Observed: "Filter 1 (Wonder 3) invalid upscale factor: 0.658…, should be in
 * range: [1, 6]". Topaz image models never downscale.
 */
export const TOPAZ_IMAGE_SCALE_MIN = 1
export const TOPAZ_IMAGE_SCALE_MAX = 6

/**
 * Max input megapixels, per image model.
 * Observed: "input image dimensions (43.53 MP) exceed maximum allowed of
 * 24.00 MP for \"Recovery V2\"". The message is explicitly per-model, so only
 * the model we have evidence for is listed.
 */
export const TOPAZ_IMAGE_MAX_INPUT_MEGAPIXELS: Record<string, number> = {
  'Recovery V2': 24,
}

/** A limit the request would be rejected for, with copy safe to show a user. */
export interface TopazLimitViolation {
  field: 'frameCount' | 'scaleFactor' | 'inputMegapixels'
  message: string
}

/**
 * Hard caps published on the FAL endpoint for `blackforestlabs/flux-video-upscale`.
 * These are provider-enforced: exceeding them fails the submit outright, so both the
 * modal and the handler gate on them before a job is created.
 */
/**
 * The provider cap is 20s, but it validates the MP4 container duration, and a re-encoded
 * 20.000s trim lands at ~20.02–20.05s once AAC priming/padding is muxed in — FAL then
 * rejects the clip Martini just cut for it ("Clip too long"). Holding the window at 19.5s
 * keeps every trim under the cap with headroom for frame rounding too.
 */
export const FLUX_VIDEO_UPSCALE_MAX_SOURCE_SECONDS = 19.5
export const FLUX_VIDEO_UPSCALE_MAX_SOURCE_BYTES = 50 * 1024 * 1024
export const FLUX_VIDEO_UPSCALE_SCALE_MIN = 1.5
export const FLUX_VIDEO_UPSCALE_SCALE_MAX = 3

/** Creativity switch exposed to users; maps to the provider's 0/1 `creativity` field. */
export type FluxUpscaleCreativity = 'precise' | 'creative'

export const FLUX_UPSCALE_CREATIVITY_VALUES: Record<FluxUpscaleCreativity, number> = {
  precise: 0,
  creative: 1,
}

/** A Flux Video Upscale limit the request would be rejected for, with user-safe copy. */
export interface FluxVideoUpscaleLimitViolation {
  field: 'sourceDuration' | 'sourceBytes' | 'scaleFactor'
  message: string
  /** True when trimming the clip would bring the job back inside the limit. */
  trimmable: boolean
}

/**
 * Returns the limit this Flux upscale would be rejected for, or null when it is
 * within every published constraint. Unknown duration/size are treated as fine:
 * asset metadata is not always populated, and the provider is the backstop.
 */
export function checkFluxVideoUpscaleLimits(params: {
  sourceDurationSec?: number | null
  sourceBytes?: number | null
  scaleFactor?: number | null
}): FluxVideoUpscaleLimitViolation | null {
  const { sourceDurationSec, sourceBytes, scaleFactor } = params

  if (
    typeof sourceDurationSec === 'number' &&
    Number.isFinite(sourceDurationSec) &&
    sourceDurationSec > FLUX_VIDEO_UPSCALE_MAX_SOURCE_SECONDS + 0.05
  ) {
    return {
      field: 'sourceDuration',
      message: `Flux Video Upscale takes clips up to ${FLUX_VIDEO_UPSCALE_MAX_SOURCE_SECONDS}s — this one is ${sourceDurationSec.toFixed(1)}s. Trim it to ${FLUX_VIDEO_UPSCALE_MAX_SOURCE_SECONDS}s or shorter, or pick a Topaz model.`,
      trimmable: true,
    }
  }

  if (
    typeof sourceBytes === 'number' &&
    Number.isFinite(sourceBytes) &&
    sourceBytes > FLUX_VIDEO_UPSCALE_MAX_SOURCE_BYTES
  ) {
    return {
      field: 'sourceBytes',
      message: `Flux Video Upscale takes files up to 50 MB — this one is ${(sourceBytes / (1024 * 1024)).toFixed(0)} MB. Trimming the clip shorter usually gets under the cap, or pick a Topaz model.`,
      // A shorter window means fewer bytes, so trimming is a real fix here too —
      // just not a guaranteed one for a high-bitrate source.
      trimmable: true,
    }
  }

  if (typeof scaleFactor === 'number' && Number.isFinite(scaleFactor)) {
    if (scaleFactor < FLUX_VIDEO_UPSCALE_SCALE_MIN || scaleFactor > FLUX_VIDEO_UPSCALE_SCALE_MAX) {
      return {
        field: 'scaleFactor',
        message: `Flux Video Upscale supports ${FLUX_VIDEO_UPSCALE_SCALE_MIN}x-${FLUX_VIDEO_UPSCALE_SCALE_MAX}x — this one is ${scaleFactor.toFixed(2)}x.`,
        trimmable: false,
      }
    }
  }

  return null
}

/**
 * Returns the limit this video upscale would be rejected for, or null when it
 * is within every constraint we know about.
 */
export function checkTopazVideoLimits(params: {
  model: string
  /** Source frames the job will feed Topaz. Skipped when unknown. */
  frameCount?: number | null
}): TopazLimitViolation | null {
  const { model, frameCount } = params
  if (
    isStarlightVideoModel(model) &&
    typeof frameCount === 'number' &&
    Number.isFinite(frameCount) &&
    frameCount > TOPAZ_STARLIGHT_MAX_INPUT_FRAMES
  ) {
    return {
      field: 'frameCount',
      message: `Starlight models handle up to ${TOPAZ_STARLIGHT_MAX_INPUT_FRAMES} frames per job — this clip is ${Math.round(frameCount)}. Trim it shorter, or pick a non-Starlight model.`,
    }
  }
  return null
}

/**
 * Returns the limit this image upscale would be rejected for, or null when it
 * is within every constraint we know about.
 */
export function checkTopazImageLimits(params: {
  model: string
  scaleFactor?: number | null
  sourceWidth?: number | null
  sourceHeight?: number | null
}): TopazLimitViolation | null {
  const { model, scaleFactor, sourceWidth, sourceHeight } = params

  if (typeof scaleFactor === 'number' && Number.isFinite(scaleFactor)) {
    if (scaleFactor < TOPAZ_IMAGE_SCALE_MIN || scaleFactor > TOPAZ_IMAGE_SCALE_MAX) {
      return {
        field: 'scaleFactor',
        message: `Upscale factor must be between ${TOPAZ_IMAGE_SCALE_MIN}x and ${TOPAZ_IMAGE_SCALE_MAX}x — this one is ${scaleFactor.toFixed(2)}x. Topaz image models don't downscale.`,
      }
    }
  }

  const maxMegapixels = TOPAZ_IMAGE_MAX_INPUT_MEGAPIXELS[model]
  if (
    maxMegapixels != null &&
    typeof sourceWidth === 'number' &&
    typeof sourceHeight === 'number' &&
    sourceWidth > 0 &&
    sourceHeight > 0
  ) {
    const megapixels = (sourceWidth * sourceHeight) / 1_000_000
    if (megapixels > maxMegapixels) {
      return {
        field: 'inputMegapixels',
        message: `${model} accepts images up to ${maxMegapixels} MP — this one is ${megapixels.toFixed(1)} MP (${sourceWidth}x${sourceHeight}). Pick a different model, or upscale a smaller version.`,
      }
    }
  }

  return null
}

/** Collaboration deployment selected by projects.collab_backend. */
export type CollabBackend = 'legacy' | 'cf'

/**
 * Detector version stamped on ShotDetectionMetadata. Bump when the algorithm
 * or tuning changes so cached results are recomputed instead of trusted.
 * v2: TransNetV2 became the primary detector (ENG-777 benchmark: F1 94.2 vs
 * 66.7 for the v1 ffmpeg config), with ffmpeg as the no-dependency fallback.
 */
export const SHOT_DETECTION_VERSION = 2

/** Which engine produced a ShotDetectionMetadata result. */
export type ShotDetector = 'transnetv2-v1' | 'ffmpeg-scene-v1'

/** Automatic shot-cut detection result stored on a video asset. */
export interface ShotDetectionMetadata {
  /** Must equal SHOT_DETECTION_VERSION to be trusted; older results are recomputed. */
  version: number
  /** Cut positions in source ticks (48kHz), ascending, exclusive of 0 and the asset duration. */
  cutsTicks: number[]
  detectedAt: string
  /** Engine that produced this result (fallbacks mean it can vary per asset). */
  detector: ShotDetector
}

/** Sequential execution order, shared by the runner and canvas. Null means a dependency cycle. */
export function resolveActionExecutionOrder<
  T extends Pick<Action, 'id' | 'order' | 'instruction' | 'inputRefs' | 'outputBinIds'>,
>(actions: readonly T[]): T[] | null {
  const byOrder = [...actions].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
  const writerByBin = new Map<string, string>()
  for (const action of byOrder) {
    for (const binId of resolveActionOutputBinIds(action)) writerByBin.set(binId, action.id)
  }
  const upstream = new Map<string, string[]>()
  for (const action of byOrder) {
    const writers = new Set<string>()
    for (const ref of normalizeActionInputRefs(action)) {
      if (ref.type !== 'bin') continue
      const writer = writerByBin.get(ref.id)
      if (writer && writer !== action.id) writers.add(writer)
    }
    upstream.set(action.id, [...writers])
  }

  const result: T[] = []
  const placed = new Set<string>()
  let remaining = byOrder
  while (remaining.length > 0) {
    const next = remaining.find(action =>
      upstream.get(action.id)!.every(id => placed.has(id) || !remaining.some(r => r.id === id)),
    )
    if (!next) {
      return null
    }
    result.push(next)
    placed.add(next.id)
    remaining = remaining.filter(action => action.id !== next.id)
  }
  return result
}

/** JSON with object keys sorted at every level, so key order never changes the hash. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(',')}}`
  }
  return JSON.stringify(value)
}

/** Presentation only: scripts do not consume canvas geometry, colour or layout.
 * Keep this deny-list explicit: every new snapshot field is machinery by default.
 */
const KIT_BIN_PRESENTATION_FIELDS = new Set([
  'position',
  'size',
  'color',
  'colour',
  'collapsed',
  'layoutMode',
  'layoutWidth',
  'gridColumns',
])

/** Shared raw inputs for backend component hashes and live client invalidation. */
export function actionFingerprintInputs(
  action: Action,
  files: readonly WorkflowDefinitionFile[],
  bins: readonly WorkflowDefinitionBin[],
) {
  const refs = normalizeActionInputRefs(action)
  const paths = new Set(refs.filter(ref => ref.type === 'instructions').map(ref => ref.id))
  if (action.instructionsPath) paths.add(action.instructionsPath)
  const byPath = new Map(files.map(file => [file.path, file.content]))
  const inputs = refs
    .filter(ref => ref.type !== 'instructions')
    .map(({ type, id, label }) => ({ type, id, label: label ?? null }))
  const outputs = resolveActionOutputBinIds(action)
  const binIds = new Set([...inputs.filter(ref => ref.type === 'bin').map(ref => ref.id), ...outputs])
  return {
    brief: action.instruction,
    instructions: [...paths].sort().map(path => ({ path, content: byPath.get(path) ?? null })),
    wiring: {
      inputs,
      outputs,
      bins: bins
        .filter(bin => binIds.has(bin.sourceId))
        .sort((a, b) => (a.sourceId < b.sourceId ? -1 : a.sourceId > b.sourceId ? 1 : 0))
        .map(bin => Object.fromEntries(Object.entries(bin).filter(([key]) => !KIT_BIN_PRESENTATION_FIELDS.has(key)))),
    },
    pinned: { pinned: action.pinned ?? null, agentId: action.agentId, gate: action.gate },
  }
}

/**
 * One Action's machinery as the workflow fingerprint hashes it. The single
 * projection behind `workflowFingerprintSource` and `describeActionsMismatch`,
 * so "what the hash covers" and "which field differs" can never disagree.
 * Changing it changes every fingerprint: update the golden hashes in
 * fingerprint.test.ts deliberately, and remember open tabs hash nothing —
 * they send their Actions (WorkflowRunEditBarriers).
 */
export function actionMachinery(action: Action) {
  return {
    id: action.id,
    instruction: action.instruction,
    agentId: action.agentId,
    order: action.order,
    gate: action.gate,
    pinned: action.pinned ?? null,
    // The brief's tags merged with the stored refs (normalizeActionInputRefs):
    // a placed workflow, its run snapshot and its saved definition all hash
    // the same wiring, whatever the stored refs happened to carry.
    inputRefs: normalizeActionInputRefs(action).map(ref => ({
      type: ref.type,
      id: ref.id,
      label: ref.label ?? null,
    })),
    outputBinIds: resolveActionOutputBinIds(action),
    // The own Instructions PATH is machinery like a tag's path; its content
    // is not (ADR-0008). Only present when set, so Actions without one keep
    // the fingerprint they had.
    ...(action.instructionsPath ? { instructionsPath: action.instructionsPath } : {}),
  }
}

/**
 * Field-level account of why two Action sets hash differently: the Actions on
 * both sides whose machinery differs (with the differing field names), the ids
 * the client sent that the canvas read lacks, and the ids it has that the
 * client did not send. Names only — the content stays where it is.
 */
export function describeActionsMismatch(
  expected: readonly Action[],
  canvas: readonly Action[],
): WorkflowActionsMismatch {
  const project = (actions: readonly Action[]) =>
    new Map(actions.map(action => [action.id, actionMachinery(action) as Record<string, unknown>]))
  const sent = project(expected)
  const held = project(canvas)
  const changed: WorkflowActionsMismatch['changed'] = []
  for (const [id, mine] of sent) {
    const theirs = held.get(id)
    if (!theirs) continue
    const fields = [...new Set([...Object.keys(mine), ...Object.keys(theirs)])]
      .filter(field => canonicalJson(mine[field]) !== canonicalJson(theirs[field]))
      .sort()
    if (fields.length) changed.push({ id, fields })
  }
  return {
    changed,
    missingOnCanvas: [...sent.keys()].filter(id => !held.has(id)),
    extraOnCanvas: [...held.keys()].filter(id => !sent.has(id)),
  }
}

export function workflowFingerprintSource(
  definition: Pick<WorkflowDefinition, 'actions' | 'bins' | 'toolBundle' | 'presetBinContents'>,
): string {
  const actions = [...definition.actions]
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
    .map(actionMachinery)
  const bins = [...(definition.bins ?? [])]
    .sort((a, b) => a.sourceId.localeCompare(b.sourceId))
    .map(bin => ({
      sourceId: bin.sourceId,
      name: bin.name,
      layoutMode: bin.layoutMode ?? null,
      gridColumns: bin.gridColumns ?? null,
      ...(bin.fileInput ? { fileInput: bin.fileInput } : {}),
      ...(bin.includeContents ? { includeContents: true } : {}),
      ...(bin.parentSourceId ? { parentSourceId: bin.parentSourceId } : {}),
      ...(bin.childOrder ? { childOrder: bin.childOrder } : {}),
    }))
  return canonicalJson({
    actions,
    bins,
    ...(definition.toolBundle
      ? { toolBundle: { sha256: definition.toolBundle.sha256, snapshot: definition.toolBundle.snapshot } }
      : {}),
    // Saved defaults are execution inputs reviewed with this version. Keep asset
    // order, but canonicalize bin order; absent presets retain their legacy hash.
    ...(definition.presetBinContents?.length
      ? {
          presetBinContents: [...definition.presetBinContents].sort((a, b) =>
            a.sourceBinId.localeCompare(b.sourceBinId),
          ),
        }
      : {}),
  })
}

/** A new team's monthly checkout price, in major currency units, before location-dependent taxes. */
export interface TeamCheckoutQuote {
  planId: string
  promotionCode?: string
  amount: number
  regularAmount: number
  currency: string
  /** 0 = no discount, null = forever; otherwise the number of discounted months. */
  discountMonths: number | null
}

export type ChatRunCodeOutput =
  | { ok: true; exitCode: number; stdout: string; stderr: string; durationMs: number; truncated: boolean }
  | { ok: false; error: 'busy' | 'timeout' | 'failed'; message: string }
